/**
 * Taste Profile Quiz
 * Helps users discover wines based on their taste preferences
 */

// Version des regles de selection du kit, ecrite dans chaque commande (`_Kit algo`).
// A changer a chaque modification de la selection, avec une entree dans CHANGELOG.md.
// Kit choisi dans la bande 32,00-32,50 EUR, bouteilles <= 14 EUR, prix dans le score. Facture 29,90 EUR.
const KIT_ALGO_VERSION = '2026-07-30 bande-prix';

class TasteProfileQuiz {
  constructor() {
    console.log('🍷 TasteProfileQuiz: Initializing...');
    
    this.currentQuestion = 1;
    this.totalQuestions = 6;
    this.targetX = 0;
    this.targetY = 0;
    this.targetZ = 0;
    this.colorPreference = 'both'; // Q1: 'red', 'white', or 'both'
    this.colorMixPreference = null;
    this.answers = [];
    this.quizSavePromise = null;
    this.lastQuizSaveSignature = '';
    this.isProcessing = false;
    this.isPaidAdTraffic = this._detectPaidAdTraffic();
    this.axisTweakActive = false;
    this.axisUpdateRequestId = 0;
    // Extra bottles upsell on the result page. The percentage here is display only: the
    // real reduction comes from the "Buy 1 Test Kit, get N at 20% off" automatic discount.
    // `upsellMaxBottles` MUST equal that discount's "Customer gets" quantity, otherwise the
    // page shows -20% on a bottle Shopify will charge in full.
    // Master on/off for the whole upsell.
    // NOTE: this value differs per theme by deliberate manual push, not by runtime detection
    // (Shopify.theme.role reports 'main' even under ?preview_theme_id, so it cannot tell dev
    // from live).
    // 2026-07-28 : coupé partout (dev + live + repo). Six jours de campagne ads sur la page
    // test-kit sans une seule vente, on change d'approche ; la remise -20% sur bouteilles
    // supplémentaires sort du parcours en attendant. L'état complet de la campagne est figé
    // sur la branche `archive/ads-hero-testkit-20260728`.
    // Pour réactiver : repasser à `true` ici, et confirmer d'abord que la remise automatique
    // Shopify « Buy 1 Test Kit, get N at 20% off » s'applique bien au checkout.
    this.upsellEnabled = false;
    this.upsellDiscountRate = 0.2;
    this.upsellMaxBottles = 9;
    this.upsellSwapOptions = 3;
    // Keep every eligible candidate. Only the wines actually displayed are exclusive, so
    // the pool just has to hold at least `upsellMaxBottles` wines for a full box. It is
    // ranked by taste distance, so the deep end is only reached by a customer who already
    // added many bottles.
    this.upsellPoolSize = 40;
    this._upsell = null;
    this.distanceLookupCache = new Map();
    this.calibrationConfig = {
      tasteZoneRatio: 0.20,
      weights: { x: 0.4, y: 0.4, intensity: 0.2 },
      targetPerceptiveDistance: 10,
      similarityWindows: [
        { min: 8, max: 12 },
        { min: 6, max: 14 },
        { min: 5, max: 15 }
      ],
      axisDistanceFallbackWeight: 1,
      // Lab similarity between the 3 bottles, in perceptive-distance units
      // (similarity % = 100 - distance). Target 80-90%; 93% is the hard ceiling
      // and 80% the hard floor, so the pack is never off-band in either direction.
      labSimilarityBands: [
        { min: 10, max: 20 },
        { min: 7, max: 20 }
      ],
      labAnchorAttempts: 10,
      labPoolLimit: 20
    };
    this.forceRetakeStorageKey = 'vinpop_force_retake';
    
    this.popup = document.getElementById('taste-profile-popup');
    this.triggerBtn = document.getElementById('taste-profile-trigger-btn');
    this.closeBtn = this.popup ? this.popup.querySelector('.taste-profile-close') : null;
    this.overlay = this.popup ? this.popup.querySelector('.taste-profile-popup-overlay') : null;
    this.progressFill = this.popup ? this.popup.querySelector('.taste-profile-progress-fill') : null;
    this.progressText = this.popup ? this.popup.querySelector('.current-question') : null;
    this.loadingEl = this.popup ? this.popup.querySelector('.taste-profile-loading') : null;
    this.backBtn = this.popup ? this.popup.querySelector('.taste-profile-back-btn') : null;
    // Question-set chooser (test-kit only). 'expert' shows the .tpq-expert wording of Q2-Q6;
    // both sets carry identical x/y/z per answer, so scoring and the API never see the path.
    this.pathChooser = this.popup ? this.popup.querySelector('.tpq-path') : null;
    // Survives the results-page reload, so the add-to-cart can still tag the kit.
    // null = unknown (e.g. a profile restored from the API), and then no tag is written.
    this.quizPath = null;
    try {
      const storedPath = sessionStorage.getItem('vinpop_quiz_path');
      if (storedPath === 'expert' || storedPath === 'guided') this.quizPath = storedPath;
    } catch (e) {}
    this.recommendedWines = null;
    
    console.log('🍷 Elements found:', {
      popup: !!this.popup,
      triggerBtn: !!this.triggerBtn,
      closeBtn: !!this.closeBtn,
      backBtn: !!this.backBtn
    });
    
    this.init();
  }

  // All paid ad traffic gets the free bottle, not just Meta: Google Ads sends the same
  // cold visitors and used to be silently excluded from the offer.
  _detectPaidAdTraffic() {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.has('fbclid') || params.has('gclid') || params.has('gbraid')
        || params.has('wbraid') || params.has('gad_source')) {
        sessionStorage.setItem('wr_meta_traffic', '1');
        return true;
      }
      const src = (params.get('utm_source') || '').toLowerCase();
      if (['facebook', 'instagram', 'meta', 'fb', 'google', 'googleads', 'adwords'].includes(src)) {
        sessionStorage.setItem('wr_meta_traffic', '1');
        return true;
      }
      const medium = (params.get('utm_medium') || '').toLowerCase();
      if (['cpc', 'ppc', 'paid', 'paidsocial', 'paid_social'].includes(medium)) {
        sessionStorage.setItem('wr_meta_traffic', '1');
        return true;
      }
      return sessionStorage.getItem('wr_meta_traffic') === '1';
    } catch (e) {
      return false;
    }
  }

  init() {
    if (!this.popup || !this.triggerBtn) {
      console.error('❌ TasteProfileQuiz: Required elements not found!');
      return;
    }
    
    console.log('✅ TasteProfileQuiz: All elements found, setting up events...');
    // Inline mode: quiz is always visible on page
    const isInline = this.popup.classList.contains('taste-profile-popup--inline');

    if (this.canUseVinpopAuth()) {
      const savedState = sessionStorage.getItem('vinpop_quiz_state');
      if (savedState && !sessionStorage.getItem('vinpop_last_results')) {
        console.log('TasteProfileQuiz: restoring in-progress quiz state');
        this.restoreQuizState(JSON.parse(savedState));
      } else if (this.isForceRetakeEnabled()) {
        console.log('TasteProfileQuiz: force retake enabled, skipping API auto-restore');
        this.openPopup();
      } else {
        this.restoreQuizFromApiOrStart(isInline);
      }
    } else {
    // Check if there's an in-progress quiz to restore (e.g. language switch)
    const savedState = sessionStorage.getItem('vinpop_quiz_state');
    if (savedState && !sessionStorage.getItem('vinpop_last_results')) {
      console.log('🔄 Restoring quiz state (language switch)');
      this.restoreQuizState(JSON.parse(savedState));
    } else if (!sessionStorage.getItem('vinpop_last_results')) {
      console.log('🚀 Auto-starting quiz on page load');
      this.openPopup();
    } else {
      console.log('📋 Quiz results already exist, skipping auto-open');
      const isCalibrationKit = window.VinPopRoute && typeof window.VinPopRoute.isCalibrationKitPath === 'function'
        ? window.VinPopRoute.isCalibrationKitPath(window.location.pathname)
        : /\/collections\/(?:test-kit|tasting-kit)(?:\/|$)/i.test(window.location.pathname);

      if (isCalibrationKit) {
        const savedResults = sessionStorage.getItem('vinpop_last_results');
        if (savedResults) {
          try {
            const wines = JSON.parse(savedResults);
            this.recommendedWines = wines;
            this.reset();
            // On page-load restore, keep the visitor at the top (the hero). Do not
            // scroll down to the results or replay the reveal animation.
            this.startWineRevealSequence(wines, { preserveScroll: true });
          } catch (e) {
            console.warn('⚠️ Could not restore calibration results:', e);
          }
        }
        return;
      }

      // In inline mode, show a retake banner instead of hiding everything
      if (isInline) {
        // Hide quiz questions but show a retake prompt
        const questionsEl = this.popup.querySelector('.taste-profile-questions');
        const progressEl = this.popup.querySelector('.taste-profile-progress');
        if (questionsEl) questionsEl.style.display = 'none';
        if (progressEl) progressEl.style.display = 'none';

        const isEN = (document.documentElement.lang || '').toLowerCase().slice(0, 2) !== 'nl';
        const retakeBanner = document.createElement('div');
        retakeBanner.className = 'quiz-retake-banner';
        retakeBanner.innerHTML = `
          <p>${isEN ? 'You already completed the Test Kit quiz!' : 'Je hebt de Test Kit-quiz al ingevuld!'}</p>
          <p class="quiz-retake-sub">${isEN ? 'Your personalised results are shown below.' : 'Je persoonlijke resultaten staan hieronder voor je klaar.'}</p>
          <div class="relative z-10 pb-button-shadow" style="display:inline-block;margin:0 auto;">
            <button class="quiz-retake-btn push-btn" type="button">
              <span class="push-btn__surface">
                ${isEN ? '🔄 Retake the quiz' : '🔄 Quiz opnieuw doen'}
              </span>
            </button>
          </div>
        `;
        const contentEl = this.popup.querySelector('.taste-profile-popup-content');
        if (contentEl) contentEl.appendChild(retakeBanner);

        retakeBanner.querySelector('.quiz-retake-btn').addEventListener('click', () => {
          this.setForceRetake(true);
          localStorage.removeItem('vinpop-quiz-completed');
          sessionStorage.removeItem('vinpop-quiz-completed');
          sessionStorage.removeItem('vinpop_last_results');
          sessionStorage.removeItem('vinpop_taste_profile');
          sessionStorage.removeItem('tasteProfileResults');
          sessionStorage.removeItem('vinpop_quiz_state');
          this.quizSavePromise = null;
          this.lastQuizSaveSignature = '';
          this._reloadToQuizTop();
        });
      }
    }
    }
    
    // Event listeners
    this.triggerBtn.addEventListener('click', () => this.openPopup());
    if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.closePopup());
    if (this.overlay) this.overlay.addEventListener('click', () => this.closePopup());
    if (this.backBtn) {
      this.backBtn.addEventListener('click', () => this.goBack());
    }
    // Intro start button
    const introStartBtn = this.popup.querySelector('.quiz-intro-start-btn');
    if (introStartBtn) {
      introStartBtn.addEventListener('click', () => {
        this.setForceRetake(false);
        this.isProcessing = false;
        if (this.loadingEl) this.loadingEl.style.display = 'none';
        if (this.pathChooser) {
          this.showPathChooser();
          return;
        }
        this.startQuestions();
      });

      // Elements that look clickable and must behave like the intro CTA (test-kit step 1).
      this.popup.querySelectorAll('[data-quiz-start]').forEach(el => {
        el.addEventListener('click', () => introStartBtn.click());
      });
    }

    if (this.pathChooser) {
      this.pathChooser.querySelectorAll('[data-quiz-path]').forEach(btn => {
        btn.addEventListener('click', () => {
          this.setQuizPath(btn.dataset.quizPath);
          this.startQuestions();
        });
      });
    }

    // Answer buttons
    const answerButtons = this.popup.querySelectorAll('.taste-profile-answer');
    answerButtons.forEach(btn => {
      if (btn.classList.contains('taste-profile-answer--mix')) return;
      btn.addEventListener('click', (e) => this.handleAnswer(e));
      if (btn.getAttribute('role') === 'button') {
        btn.addEventListener('keydown', (e) => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          if (e.target && e.target.closest('select')) return;
          e.preventDefault();
          this.handleAnswer(e);
        });
      }
    });
    this.initColorMixControls();
    
    // Keyboard navigation (only for popup mode)
    if (!isInline) {
      document.addEventListener('keydown', (e) => {
        if (this.popup.style.display === 'block') {
          if (e.key === 'Escape') this.closePopup();
        }
      });
    }
  }

  canUseVinpopAuth() {
    return !!(
      this.getMetaContent('customer-id') &&
      this.getMetaContent('friends-auth-timestamp') &&
      this.getMetaContent('friends-auth-signature')
    );
  }

  getPaidDisplayPrice(product) {
    if (!product) return 0;
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const availablePaid = variants.find(v => v && v.available && Number(v.price) > 0);
    if (availablePaid) return Number(availablePaid.price) || 0;
    const anyPaid = variants.find(v => v && Number(v.price) > 0);
    if (anyPaid) return Number(anyPaid.price) || 0;
    return Number(product.price) || 0;
  }

  getDesiredColorMix() {
    if (this.colorPreference === 'red') return { red: 3, white: 0 };
    if (this.colorPreference === 'white') return { red: 0, white: 3 };
    if ((this.colorPreference === 'mix' || this.colorPreference === 'both') && this.colorMixPreference) {
      const rawRed = parseInt(this.colorMixPreference.red, 10);
      const red = rawRed >= 2 ? 2 : 1;
      const white = 3 - red;
      if (red + white === 3) return { red, white };
    }
    // 'both' with no explicit mix yet: honour the 2 red + 1 white the panel shows.
    if (this.colorPreference === 'both') return { red: 2, white: 1 };
    return null;
  }

  normalizeColorMixPreference(redCount) {
    const parsedRed = parseInt(redCount, 10);
    const red = Math.max(0, Math.min(3, Number.isFinite(parsedRed) ? parsedRed : 1));
    return {
      red,
      white: 3 - red
    };
  }

  initColorMixControls() {
    const mixButtons = this.popup.querySelectorAll('.taste-profile-answer--mix');
    mixButtons.forEach(button => {
      const dropdown = button.querySelector('[data-mix-dropdown]');
      const trigger = button.querySelector('[data-mix-trigger]');
      const options = Array.from(button.querySelectorAll('[data-mix-red-count]'));
      const totalEl = button.querySelector('[data-mix-total]');
      if (!dropdown || !trigger || !options.length) return;
      let selectedRed = 0;

      const syncMix = () => {
        const red = Math.max(0, Math.min(3, parseInt(selectedRed, 10) || 0));
        const white = 3 - red;
        button.dataset.redCount = String(red);
        button.dataset.whiteCount = String(white);
        const total = red + white;
        if (totalEl) totalEl.textContent = `${total} / 3`;
        return total;
      };

      trigger.addEventListener('click', event => {
        event.preventDefault();
        event.stopPropagation();
        dropdown.classList.toggle('is-open');
      });

      options.forEach(option => {
        option.addEventListener('click', event => {
          event.preventDefault();
          event.stopPropagation();
          selectedRed = parseInt(option.dataset.mixRedCount, 10) || 0;
          trigger.textContent = option.textContent.trim();
          dropdown.classList.remove('is-open');
          if (syncMix() === 3) this.handleAnswer({ currentTarget: button });
        });
      });

      button.addEventListener('click', event => {
        event.stopPropagation();
        if (!event.target.closest('[data-mix-dropdown]')) {
          dropdown.classList.remove('is-open');
        }
      });
      syncMix();
    });
  }

  getWineColorCode(wine, catalog = window.shopCatalog || {}) {
    const wineColor = String(wine?.color || '').toUpperCase();
    if (wineColor === 'R' || wineColor === 'W') return wineColor;
    const wineId = String(wine?.wine_id || wine?.id || '');
    if (wineId === '15513213370755') return 'W';
    if (wineId === '15510612148611') return 'R';
    const product = catalog[wineId];
    const tags = Array.isArray(product?.tags) ? product.tags.join(' ') : '';
    const text = [
      product?.type,
      product?.wineColor,
      product?.productType,
      product?.title,
      product?.handle,
      tags
    ].filter(Boolean).join(' ').toLowerCase();
    if (/\b(w|white|wit|blanc|bianco|weiß|weiss)\b/.test(text) || text.includes('white wine') || text.includes('witte wijn')) return 'W';
    if (/\b(r|red|rood|rouge|rosso|rot)\b/.test(text) || text.includes('red wine') || text.includes('rode wijn')) return 'R';
    return '';
  }

  isWineAllowedForColorPreference(wine, catalog = window.shopCatalog || {}) {
    if (this.colorPreference !== 'red' && this.colorPreference !== 'white') return true;
    const code = this.getWineColorCode(wine, catalog);
    if (this.colorPreference === 'red') return code === 'R';
    if (this.colorPreference === 'white') return code === 'W';
    return true;
  }

  getWineProductKey(wine) {
    return String(wine?.wine_id || wine?.product_id || wine?.id || '');
  }

  uniqueWinesByProduct(wines) {
    const seen = new Set();
    return (Array.isArray(wines) ? wines : []).filter(wine => {
      const key = this.getWineProductKey(wine);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  // Used by the calibration bonus bottle and by the paid-ad free bottle: both ship as a
  // dedicated 0€ variant, so the discount can never leak onto a normally sold bottle.
  getFreeBonusVariantIdByProductId(productId) {
    const pid = String(productId || '');
    if (pid === '15513213370755') return 58080491012483; // white bonus product -> 0€ variant
    if (pid === '15510612148611') return 58080438649219; // red bonus product -> 0€ variant
    return null;
  }

  // A visitor who left their email to save the offer comes back tagged, so the same colour
  // of bottle is honoured on the next visit even outside a paid-ad click.
  getGiftProductIdFromCustomerTags(catalog = window.shopCatalog || {}) {
    const tags = (window.VinpopCustomerData && Array.isArray(window.VinpopCustomerData.tags))
      ? window.VinpopCustomerData.tags.map(tag => String(tag || '').trim().toLowerCase())
      : [];
    try {
      const savedGiftTag = String(localStorage.getItem('vinpop_saved_gift_tag') || '').trim().toLowerCase();
      if (savedGiftTag) tags.push(savedGiftTag);
    } catch(e) {}
    const taggedId = tags.includes('white gift')
      ? '15513213370755'
      : tags.includes('red gift')
        ? '15510612148611'
        : '';
    if (!taggedId || !catalog[taggedId]) return '';
    // The 0€ variant requirement is gone with the variant itself: the gift is a line item
    // property now, so any bottle can be given and the tag only has to name a real product.
    if (!this.isWineAllowedForColorPreference({ wine_id: taggedId }, catalog)) return '';
    return taggedId;
  }

  // Only the two bonus products can be gifted, because only they carry a 0€ variant.
  // Cheapest first, and never a bottle already in the kit.
  // The one place the pack price band is decided, so the two callers of
  // `buildCalibrationBottleSelection()` can no longer drift apart.
  //
  // Two rules from the offer, 2026-07-30:
  //   - the PAID bottles alone must always reach €32,00, so the box reads as a deal before the
  //     gift is even counted;
  //   - the paid bottles PLUS the gift must never exceed €40,00.
  // The gift is a real bottle with a real price and it already counts in the retail value, so
  // the ceiling on the paid pack is €40,00 minus whatever the gift actually costs. That price
  // is knowable up front: `reserveForFreeGift` keeps both bonus bottles out of the kit, so the
  // gift never depends on which wines the pack ended up with.
  //
  // Consequence to know: with the €6,49 gift the paid band is €32,00 to €33,51, only €1,51
  // wide but still 1233 valid trios. With the €7,99 gift it collapses to €0,01 and the
  // selection will fall back to its closest-match pack, which CAN exceed €40,00. The pair of
  // numbers therefore holds only while the €6,49 bottle is in stock.
  getPackPriceBand(catalog = window.shopCatalog || {}, isCatalogProductInStock = () => true) {
    const PAID_FLOOR = 3200;
    const PACK_TOLERANCE = 50;
    return {
      min: PAID_FLOOR,
      max: PAID_FLOOR + PACK_TOLERANCE,
      totalCeiling: PAID_FLOOR + PACK_TOLERANCE,
      giftBudget: 0,
      reserveId: '',
      reserveCents: 0
    };
  }

  // The bottle held back so there is ALWAYS a gift to give.
  //
  // Without it the guarantee breaks in a way that is easy to miss: the pack is free to take the
  // cheapest wine, and the leftover budget (ceiling minus pack) can then be smaller than every
  // remaining bottle, leaving nothing giftable. Reserving the cheapest one closes that hole,
  // because the band already reserves exactly its price, so it always fits the leftover budget.
  // This is the same job the old `reserveForFreeGift` did for the two 0€ products, generalised.
  getGiftReserve(catalog = window.shopCatalog || {}, isCatalogProductInStock = () => true) {
    let reserve = { id: '', cents: 0 };
    let cheapest = Infinity;
    Object.keys(catalog).forEach(id => {
      const product = catalog[id];
      if (!product || !isCatalogProductInStock(product)) return;
      if (!this.isWineAllowedForColorPreference({ wine_id: id }, catalog)) return;
      const price = this.getPaidDisplayPrice(product);
      if (price > 0 && price < cheapest) {
        cheapest = price;
        reserve = { id: String(id), cents: price };
      }
    });
    return reserve;
  }

  // The gifted bottle, chosen from the WHOLE catalogue.
  //
  // 2026-07-30 : the gift no longer needs a dedicated 0€ variant, because it is now a line item
  // PROPERTY on the Test Kit exactly like the paid bottles, not a cart line of its own. The
  // customer buys one product at €29,90 and the properties tell the warehouse what to pack, so
  // nothing is ever charged for a wine and the page can never disagree with the receipt.
  //
  // Consequence, accepted: the gift stops decrementing Shopify inventory. It was the only
  // bottle in the box that did; the three paid ones never have, since they are properties too.
  // Stock is updated by hand after each order, so the box is now consistent instead of having
  // three untracked bottles and one tracked.
  //
  // There must ALWAYS be a gift, so this can only return '' when the catalogue has nothing at
  // all in stock. `budgetCents` caps its retail value so the box respects the €40,00 total, and
  // `getPackPriceBand()` has already reserved room for the cheapest bottle, which guarantees at
  // least one candidate fits. Within the budget the closest taste match wins, so the gift stops
  // being "whatever was cheapest" as soon as the ceiling leaves any choice.
  chooseGiftProductId({ excludedIds, budgetCents, catalog = window.shopCatalog || {}, isCatalogProductInStock = () => true, rankedCandidates = [] } = {}) {
    const excluded = excludedIds instanceof Set ? excludedIds : new Set(excludedIds || []);
    const budget = Number(budgetCents) > 0 ? Number(budgetCents) : Infinity;
    const isEligible = (id, cap) => {
      const product = catalog[id];
      if (!product || excluded.has(String(id))) return false;
      const price = this.getPaidDisplayPrice(product);
      if (!(price > 0) || price > cap) return false;
      if (!isCatalogProductInStock(product)) return false;
      return this.isWineAllowedForColorPreference({ wine_id: id }, catalog);
    };

    const pick = (cap) => {
      // `rankedCandidates` is the scored, taste-distance-sorted list the kit was chosen from, so
      // the first eligible entry is the closest match the cap allows.
      for (const wine of rankedCandidates) {
        const id = String(wine?.wine_id || '');
        if (id && isEligible(id, cap)) return id;
      }
      // Nothing scored fits, so take the cheapest bottle the cap allows.
      return Object.keys(catalog)
        .filter(id => isEligible(id, cap))
        .sort((a, b) => this.getPaidDisplayPrice(catalog[a]) - this.getPaidDisplayPrice(catalog[b]))[0] || '';
    };

    // The gift is a promise, the €40,00 total is a target. If the budget covers nothing, give
    // the cheapest bottle anyway and let the total run over rather than ship a box with no gift.
    // This only fires when the pack itself already broke its band through `findPricedPack`'s
    // closest-match fallback, so it is rare, but "always a free bottle" has to mean always.
    return pick(budget) || pick(Infinity);
  }

  // 2026-07-30 : PLUS APPELÉ, remplacé par `chooseGiftProductId()`. Il ne savait offrir que les
  // deux produits portant une variante à 0 €, contrainte disparue avec la variante elle-même.
  // Conservé le temps de vérifier une commande réelle, à supprimer ensuite avec
  // `getFreeBonusVariantIdByProductId()` si la bouteille de calibration passe aussi en propriété.
  chooseCheapestFreeGiftProductId(excludedIds, catalog = window.shopCatalog || {}, isCatalogProductInStock = () => true) {
    const excluded = excludedIds instanceof Set ? excludedIds : new Set(excludedIds || []);
    const bonusIds = ['15513213370755', '15510612148611'];
    return bonusIds
      .map(id => {
        const product = catalog[id];
        return {
          id,
          product,
          price: product ? this.getPaidDisplayPrice(product) : Infinity
        };
      })
      .filter(item =>
        item.product &&
        !excluded.has(item.id) &&
        this.getFreeBonusVariantIdByProductId(item.id) &&
        this.isWineAllowedForColorPreference({ wine_id: item.id }, catalog) &&
        isCatalogProductInStock(item.product)
      )
      .sort((a, b) => a.price - b.price || a.id.localeCompare(b.id))[0]?.id || '';
  }

  getCalibrationAxisContext(wines) {
    const values = (key) => wines.map(w => parseFloat(w[key])).filter(Number.isFinite);
    const xs = values('map_x');
    const ys = values('map_y');
    const is = values('map_intensity');
    const minMax = (arr) => {
      const min = Math.min.apply(null, arr);
      const max = Math.max.apply(null, arr);
      return { min, max, range: (max - min) || 1, center: (min + max) / 2 };
    };
    const x = minMax(xs.length ? xs : [0, 1]);
    const y = minMax(ys.length ? ys : [0, 1]);
    const intensity = minMax(is.length ? is : [0, 1]);
    const quizRange = 1.2;
    const customer = {
      x: x.center + (this.targetX / quizRange) * (x.range / 2),
      y: y.center + (this.targetY / quizRange) * (y.range / 2),
      intensity: intensity.center + (this.targetZ / quizRange) * (intensity.range / 2)
    };
    const cfg = this.calibrationConfig;
    return {
      ranges: { x, y, intensity },
      customer,
      tasteZone: {
        xMin: customer.x - cfg.tasteZoneRatio * x.range,
        xMax: customer.x + cfg.tasteZoneRatio * x.range,
        yMin: customer.y - cfg.tasteZoneRatio * y.range,
        yMax: customer.y + cfg.tasteZoneRatio * y.range,
        intensityMin: customer.intensity - cfg.tasteZoneRatio * intensity.range,
        intensityMax: customer.intensity + cfg.tasteZoneRatio * intensity.range
      }
    };
  }

  decorateWineForCalibration(wine, context) {
    const cfg = this.calibrationConfig;
    const wx = parseFloat(wine.map_x) || 0;
    const wy = parseFloat(wine.map_y) || 0;
    const wi = parseFloat(wine.map_intensity) || 0;
    const dx = (wx - context.customer.x) / context.ranges.x.range;
    const dy = (wy - context.customer.y) / context.ranges.y.range;
    const di = (wi - context.customer.intensity) / context.ranges.intensity.range;
    const axisDistance = Math.sqrt(
      cfg.weights.x * Math.pow(dx, 2) +
      cfg.weights.y * Math.pow(dy, 2) +
      cfg.weights.intensity * Math.pow(di, 2)
    );
    const insideTasteZone =
      Math.abs(wx - context.customer.x) <= cfg.tasteZoneRatio * context.ranges.x.range &&
      Math.abs(wy - context.customer.y) <= cfg.tasteZoneRatio * context.ranges.y.range &&
      Math.abs(wi - context.customer.intensity) <= cfg.tasteZoneRatio * context.ranges.intensity.range;
    return { ...wine, _axisDistance: axisDistance, _insideTasteZone: insideTasteZone };
  }

  async getPerceptiveDistanceMap(productId) {
    const id = String(productId || '');
    if (!id) return new Map();
    if (this.distanceLookupCache.has(id)) return this.distanceLookupCache.get(id);
    try {
      const response = await fetch('https://api.vinpop.fr/api/wines/getDistance', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id })
      });
      if (!response.ok) throw new Error(`Distance lookup failed for ${id}`);
      const rows = await response.json();
      const map = new Map();
      (Array.isArray(rows) ? rows : []).forEach(row => {
        const id1 = String(row.productid1 || '');
        const id2 = String(row.productid2 || '');
        const otherId = id1 === id ? id2 : id1;
        const distance = Number(row.perceptive_distance ?? row.distance);
        if (otherId && Number.isFinite(distance)) map.set(otherId, distance);
      });
      this.distanceLookupCache.set(id, map);
      return map;
    } catch (error) {
      console.warn('⚠️ Could not fetch perceptive distances:', error);
      const empty = new Map();
      this.distanceLookupCache.set(id, empty);
      return empty;
    }
  }

  getDistanceBetweenSelected(distanceMap, wine) {
    return distanceMap.get(String(wine?.wine_id || ''));
  }

  similarityPctFromDistance(distance) {
    return Number.isFinite(Number(distance)) ? Math.max(0, Math.min(100, Math.round(100 - Number(distance)))) : null;
  }

  // On the taste map the customer must sit INSIDE the triangle of the 3 bottles,
  // not off to one side. Mathematically that holds when the three bearings seen
  // from the customer leave no gap wider than 180 degrees; a gap near 120 gives
  // the balanced triangle. Returns a penalty (0 = perfect) to add to the score.
  buildTasteMapGeometryScorer(context) {
    const customer = context?.customer;
    const xRange = context?.ranges?.x?.range || 0;
    const yRange = context?.ranges?.y?.range || 0;
    if (!customer || !xRange || !yRange) return () => 0;

    const bearingOf = (wine) => {
      const dx = ((parseFloat(wine?.map_x) || 0) - customer.x) / xRange;
      const dy = ((parseFloat(wine?.map_y) || 0) - customer.y) / yRange;
      if (!dx && !dy) return null;
      return { angle: Math.atan2(dy, dx) * (180 / Math.PI), dx, dy };
    };

    return (pack) => {
      const vectors = pack.map(bearingOf);
      if (vectors.some(vector => !vector)) return 1;

      const angles = vectors.map(vector => (vector.angle + 360) % 360).sort((a, b) => a - b);
      const gaps = [
        angles[1] - angles[0],
        angles[2] - angles[1],
        360 - (angles[2] - angles[0])
      ];
      const widestGap = Math.max(...gaps);

      // Customer outside the triangle: every wine sits on the same side.
      const enclosesCustomer = widestGap < 180;
      const spreadPenalty = Math.min(1, Math.abs(widestGap - 120) / 120);

      // How far the triangle's centre of mass drifts from the customer.
      const centroidOffset = Math.hypot(
        vectors.reduce((sum, vector) => sum + vector.dx, 0) / 3,
        vectors.reduce((sum, vector) => sum + vector.dy, 0) / 3
      );

      return (enclosesCustomer ? 0 : 4) + (spreadPenalty * 2) + (centroidOffset * 4);
    };
  }

  // Picks the 3 bottles so that every pair stays inside the lab similarity band.
  // Returns null when the lab data cannot back a pack, so the caller can fall back.
  async selectCalibrationTrioByLabSimilarity(candidates, options = {}) {
    const cfg = this.calibrationConfig;
    const bands = cfg.labSimilarityBands || [];
    if (candidates.length < 3 || !bands.length) return null;

    const requiredMix = options.requiredMix || null;
    const matchesRequiredMix = options.matchesRequiredMix || (() => true);
    const packTotal = options.packTotal || (() => 0);
    const minPackPrice = Number.isFinite(options.minPackPrice) ? options.minPackPrice : 0;
    const maxPackPrice = Number.isFinite(options.maxPackPrice) ? options.maxPackPrice : Infinity;
    const targetTotal = (minPackPrice + maxPackPrice) / 2;
    const idOf = (wine) => String(wine?.wine_id || '');
    const anchors = candidates.slice(0, cfg.labAnchorAttempts || 3);
    const geometryScore = this.buildTasteMapGeometryScorer(options.context);

    for (const band of bands) {
      let bandBest = null;
      const inBand = (distance) => Number.isFinite(distance) && distance >= band.min && distance <= band.max;
      // The lab never compares a red to a white, so a red/white pair has no
      // distance at all. Enforce the band on the pairs it does measure, and let
      // a cross-colour pair through untested rather than rejecting the pack.
      const pairHolds = (wineA, wineB, distance) => (
        Number.isFinite(distance)
          ? inBand(distance)
          : this.getWineColorCode(wineA) !== this.getWineColorCode(wineB)
      );

      for (const anchor of anchors) {
        const distanceFromAnchor = await this.getPerceptiveDistanceMap(idOf(anchor));
        if (!distanceFromAnchor.size) continue;

        const pool = candidates
          .filter(wine => idOf(wine) !== idOf(anchor) && pairHolds(anchor, wine, distanceFromAnchor.get(idOf(wine))))
          .slice(0, cfg.labPoolLimit || 14);
        if (pool.length < 2) continue;

        // One call per wine returns its distance to every other wine, so this
        // gives the full pairwise matrix inside the pool.
        const poolMaps = new Map();
        await Promise.all(pool.map(async (wine) => {
          poolMaps.set(idOf(wine), await this.getPerceptiveDistanceMap(idOf(wine)));
        }));

        let best = null;
        for (let i = 0; i < pool.length; i += 1) {
          for (let j = i + 1; j < pool.length; j += 1) {
            const bottleB = pool[i];
            const bottleC = pool[j];
            if (!pairHolds(bottleB, bottleC, poolMaps.get(idOf(bottleB))?.get(idOf(bottleC)))) continue;

            const pack = [anchor, bottleB, bottleC];
            if (!matchesRequiredMix(pack, requiredMix)) continue;

            const total = packTotal(pack);
            const pricePenalty = total < minPackPrice
              ? (minPackPrice - total) / 100
              : total > maxPackPrice
                ? (total - maxPackPrice) / 100
                : 0;
            const score =
              (anchor._axisDistance * 3) +
              bottleB._axisDistance +
              bottleC._axisDistance +
              (Math.abs(total - targetTotal) / 1000) +
              (pricePenalty * 10) +
              geometryScore(pack);

            if (!best || score < best.score) best = { pack, score };
          }
        }

        // Keep searching the other anchors: an anchor further from the customer
        // often wraps the trio around them, which is what the map must show.
        if (best && (!bandBest || best.score < bandBest.score)) bandBest = best;
      }

      // Bottle 1 must still read as the closest match, so order by taste distance.
      if (bandBest) return [...bandBest.pack].sort((a, b) => a._axisDistance - b._axisDistance);
    }

    return null;
  }

  addCalibrationMetadata(wine, role, index, isEN, extras = {}) {
    const copy = {
      closest_match: {
        title: isEN ? 'Your safest bet' : 'Je veiligste keuze',
        text: isEN
          ? 'Of all the wines in our lab, this one sits closest to the answers you gave.'
          : 'Van alle wijnen in ons lab ligt deze het dichtst bij de antwoorden die je gaf.'
      },
      same_direction_variation: {
        title: isEN ? 'One small step sideways' : 'Eén stapje opzij',
        text: isEN
          ? 'Same taste family, slightly different. If you love this one too, we know your taste is wide.'
          : 'Dezelfde smaakfamilie, net iets anders. Vind je deze ook lekker, dan weten we dat je smaak breed is.'
      },
      // 2026-07-30 : RETIRÉ du parcours. Trois clients n'ont pas aimé cette bouteille, celle
      // qui était choisie pour être la plus éloignée de leurs réponses. La copie reste ici
      // parce qu'un `vinpop_calibration_recommendation` déjà en sessionStorage peut encore
      // porter ce rôle, et `copy[role]` lèverait une exception s'il disparaissait.
      // Conséquence assumée : la box n'apprend plus où est la limite de goût du client. Cette
      // fonction de calibration doit se déplacer vers les notes Love / Like / Dislike après
      // livraison, où une déception ne coûte pas une bouteille sur quatre.
      controlled_exploration: {
        title: isEN ? 'One safe step further' : 'Eén veilige stap verder',
        text: isEN
          ? 'Still inside your taste zone, but the furthest one in your box. It tells us where your limit is.'
          : 'Nog steeds binnen jouw smaakzone, maar de verste in je box. Die vertelt ons waar jouw grens ligt.'
      },
      second_safe_match: {
        title: isEN ? 'Another safe choice' : 'Nog een veilige keuze',
        text: isEN
          ? 'Also very close to your answers. There is no risky bottle in this box.'
          : 'Ook heel dicht bij je antwoorden. Er zit geen risicofles in deze box.'
      },
      third_safe_match: {
        title: isEN ? 'One more safe choice' : 'Nog een veilige keuze erbij',
        text: isEN
          ? 'A fourth bottle, picked the same way as the others: inside your taste zone, never outside it.'
          : 'Een vierde fles, op dezelfde manier gekozen als de rest: binnen je smaakzone, nooit erbuiten.'
      }
    };
    return {
      ...wine,
      _calibrationRole: role,
      _calibrationIndex: index,
      reason_title: copy[role].title,
      reason_text: copy[role].text,
      axis_distance_to_customer: wine._axisDistance,
      ...extras
    };
  }

  // Demo override, for comparing the two outcomes on a preview theme without having to guess
  // which quiz answers produce which box: `?wr_pack=3` forces the three-bottle pass and
  // `?wr_pack=4` forces the four-bottle one. Anything else, including no parameter at all,
  // leaves the normal behaviour untouched, which is why this is safe to ship.
  getForcedPackSize() {
    try {
      const raw = new URLSearchParams(window.location.search).get('wr_pack');
      const size = parseInt(raw, 10);
      return size === 3 || size === 4 ? size : null;
    } catch (error) {
      return null;
    }
  }

  // `?wr_debug=1` turns on the offer breakdown in the console. Read-only, prints nothing when
  // absent, so it is safe to ship next to the real page.
  isOfferDebugEnabled() {
    try {
      return new URLSearchParams(window.location.search).get('wr_debug') === '1';
    } catch (error) {
      return false;
    }
  }

  // Companion override for the total ceiling, in euros (`?wr_cap=42`) or cents (`?wr_cap=4200`).
  // Exists so a box the live €40,00 ceiling makes impossible can still be previewed and judged
  // before the ceiling is actually moved. Clamped to 3200-6000 and null when absent.
  getForcedTotalCeiling() {
    try {
      const raw = new URLSearchParams(window.location.search).get('wr_cap');
      if (!raw) return null;
      const value = Number(String(raw).replace(',', '.'));
      if (!Number.isFinite(value) || value <= 0) return null;
      const cents = value < 200 ? Math.round(value * 100) : Math.round(value);
      return cents >= 3200 && cents <= 6000 ? cents : null;
    } catch (error) {
      return null;
    }
  }

  // The optional fourth paid bottle. Two hard rules, both from the offer rather than the
  // maths: it must sit in the taste zone (`_insideTasteZone`) and be closer to the customer
  // than the exploration bottle. A fourth bottle only ever fits when the wines matching the
  // customer's answers are cheap, so filling this slot with exploration would ship a wine
  // that is both cheap and far from those answers: the single bottle most likely to sour a
  // first order and kill the repeat purchase.
  //
  // 2026-07-30 : ENCORE DORMANTE, et il manque UNE seule chose, documentée ici.
  //
  // Les deux vins les moins chers du catalogue (6,49 et 7,99) sont exactement les deux
  // bouteilles cadeaux, que `reserveForFreeGift` exclut des candidats du kit. Le vin
  // éligible le moins cher est donc à 8,25. Avec MAX_PACK_PRICE = 3800, loger une 4e exige
  // donc un trio à 29,75 au plus, alors que le trio est sélectionné dans la bande
  // 3000-3800. Les fenêtres ne se croisent pas, la fonction renvoie toujours null.
  //
  // Ce qui manque n'est PAS une constante : c'est une sélection en deux passes. La bande du
  // trio doit être abaissée à 1990-2975 quand on VISE 4 bouteilles, puis on retombe sur la
  // bande normale si aucune 4e ne convient. Le plancher est une contrainte sur le pack
  // FINI, pas sur le trio : c'est ce qui autorise la 4e bouteille à être ce qui atteint le
  // plancher, au lieu d'obliger le trio à monter en prix.
  //
  // Conséquence à connaître avant de l'activer : un pack de 4 sous 3800 fait forcément
  // 8,15 à 8,90 de retail par bouteille, contre 11,12 à 3 bouteilles. Le badge est
  // identique dans les deux cas (-49%) puisque le plafond est le même. Le gain réel n'est
  // donc pas la remise, c'est la fidélité au goût : aujourd'hui le plancher force
  // l'algorithme à monter en gamme pour les clients dont les réponses pointent vers des
  // vins abordables, donc à choisir en partie sur le prix plutôt que sur le goût.
  // À 3800, 2007 combinaisons de 4 vins sont valides, dont 861 en mix équilibré 2R+2B.
  //
  // Called AFTER the trio is settled and it can never alter it. The taste maths choose the
  // wines and the bottle count is only a consequence of what they cost, never the reverse.
  // Returns null when nothing qualifies or the ceiling leaves no room, and the box stays at
  // three paid bottles.
  pickSafeFourthBottle({ trio, candidates, packTotal, minPackPrice, maxPackPrice, getColorCode }) {
    if (!Array.isArray(trio) || trio.length < 3 || !Array.isArray(candidates)) return null;
    const spent = packTotal(trio);
    // The floor exists so the retail value always beats the €29.90 we charge. It is a
    // constraint on the FINISHED pack, not on the trio, so the fourth bottle can be the
    // thing that satisfies it rather than an upgrade forced onto the trio's prices.
    const floorPrice = Math.max(0, (minPackPrice || 0) - spent);
    const room = maxPackPrice - spent;
    if (room <= 0 || floorPrice > room) return null;

    const takenIds = new Set(trio.map(wine => String(wine?.wine_id || '')));
    // No distance ceiling relative to the trio any more. It existed to keep the fourth bottle
    // safer than the exploration bottle, and that bottle is gone: all three trio slots are now
    // picked for closeness, so `_insideTasteZone` alone already carries the "never a risky
    // bottle" guarantee. Keeping the old ceiling on top of it would suppress the fourth bottle
    // almost always, since the trio has already taken the closest wines.

    const eligible = candidates.filter(wine => {
      if (!wine || takenIds.has(String(wine.wine_id))) return false;
      if (!wine._insideTasteZone) return false;
      const price = Number(wine._displayPrice) || 0;
      return price >= Math.max(1, floorPrice) && price <= room;
    });
    if (!eligible.length) return null;

    // Prefer the colour the trio has least of, so a 2+1 trio becomes a balanced 2+2 instead
    // of a lopsided 3+1. Preference only: a colour-locked visitor already had the other
    // colour filtered out of `candidates`, so there is nothing to force here.
    const counts = trio.reduce((acc, wine) => {
      const code = getColorCode(wine);
      if (code === 'R') acc.R += 1;
      if (code === 'W') acc.W += 1;
      return acc;
    }, { R: 0, W: 0 });
    const wanted = counts.R === counts.W ? null : (counts.R < counts.W ? 'R' : 'W');
    const preferred = wanted ? eligible.filter(wine => getColorCode(wine) === wanted) : [];

    return (preferred.length ? preferred : eligible)
      .slice()
      .sort((a, b) => a._axisDistance - b._axisDistance)[0] || null;
  }

  buildCalibrationVisualization(selectedWines, context, allWines = [], catalog = window.shopCatalog || {}) {
    const selectedIds = new Set(selectedWines.map(wine => String(wine.wine_id)));
    const selectedById = selectedWines.reduce((map, wine, index) => {
      map[String(wine.wine_id)] = { wine, index };
      return map;
    }, {});
    const allMapWines = allWines
      .filter(wine => Number.isFinite(parseFloat(wine.map_x)) && Number.isFinite(parseFloat(wine.map_y)))
      .map(wine => {
        const id = String(wine.wine_id || wine.product_id || wine.id || '');
        const selected = selectedById[id];
        return {
          product_id: id,
          title: catalog[id]?.title || wine.title || '',
          color: this.getWineColorCode(wine, catalog),
          x: parseFloat(wine.map_x) || 0,
          y: parseFloat(wine.map_y) || 0,
          intensity: parseFloat(wine.map_intensity) || 0,
          selected: selectedIds.has(id),
          role: selected?.wine?._calibrationRole || '',
          reason_title: selected?.wine?.reason_title || '',
          label: selected ? String(selected.index + 1) : ''
        };
      });
    return {
      customer_point: {
        x: context.customer.x,
        y: context.customer.y,
        intensity: context.customer.intensity
      },
      taste_zone: {
        x_min: context.tasteZone.xMin,
        x_max: context.tasteZone.xMax,
        y_min: context.tasteZone.yMin,
        y_max: context.tasteZone.yMax,
        intensity_min: context.tasteZone.intensityMin,
        intensity_max: context.tasteZone.intensityMax
      },
      map_bounds: {
        x_min: context.ranges.x.min,
        x_max: context.ranges.x.max,
        y_min: context.ranges.y.min,
        y_max: context.ranges.y.max,
        intensity_min: context.ranges.intensity.min,
        intensity_max: context.ranges.intensity.max
      },
      all_wines: allMapWines,
      selected_wines: selectedWines.map((wine, index) => ({
        product_id: String(wine.wine_id),
        role: wine._calibrationRole,
        reason_title: wine.reason_title,
        reason_text: wine.reason_text,
        title: catalog[String(wine.wine_id)]?.title || '',
        color: this.getWineColorCode(wine, catalog),
        x: parseFloat(wine.map_x) || 0,
        y: parseFloat(wine.map_y) || 0,
        intensity: parseFloat(wine.map_intensity) || 0,
        distance_to_bottle_1: wine.distance_to_bottle_1,
        distance_to_bottle_2: wine.distance_to_bottle_2,
        distance_to_bottle_3: wine.distance_to_bottle_3,
        similarity_to_bottle_1_pct: wine.similarity_to_bottle_1_pct,
        similarity_to_bottle_2_pct: wine.similarity_to_bottle_2_pct,
        similarity_to_bottle_3_pct: wine.similarity_to_bottle_3_pct,
        label: String(index + 1)
      }))
    };
  }

  async buildCalibrationBottleSelection(wines, options = {}) {
    const isEN = options.isEN ?? (document.documentElement.lang || '').toLowerCase().slice(0, 2) !== 'nl';
    const catalog = options.catalog || window.shopCatalog || {};
    const excludedCustomerProductIds = new Set(['15513192694147']);
    const minPackPrice = options.minPackPrice || 3000;
    const maxPackPrice = options.maxPackPrice || 3500;
    // Band the TRIO is selected in. It is not the pack band: pass 1 lowers it so a fourth
    // safe bottle still fits under `maxPackPrice`, pass 2 restores the pack band for a
    // three-bottle box. Everything that prices a trio reads these two, never the pack band.
    let trioMin = minPackPrice;
    let trioMax = maxPackPrice;
    const maxPrice = options.maxPrice || maxPackPrice;
    const isCatalogProductInStock = options.isCatalogProductInStock || (() => true);
    const getPaidDisplayPrice = options.getPaidDisplayPrice || ((product) => this.getPaidDisplayPrice(product));
    // Search the whole scored catalogue, not just the handful of top matches the
    // cards display: an 80-90% trio often needs a wine ranked below the top 10.
    const searchPool = this.uniqueWinesByProduct([
      ...(Array.isArray(wines) ? wines : []),
      ...(Array.isArray(this.calibrationSearchPool) ? this.calibrationSearchPool : [])
    ]);
    const context = this.getCalibrationAxisContext(searchPool);
    const giftReserveId = '';
    const candidates = this.uniqueWinesByProduct(searchPool
      .filter(wine => {
        const p = catalog[String(wine.wine_id)];
        const wineId = String(wine.wine_id);
        // Exactly ONE bottle is held out of the kit, the cheapest in stock, so there is always
        // something left to give. The band reserves precisely its price, so it always fits the
        // leftover budget. Without this the pack could take the cheapest wine and leave a budget
        // too small for any remaining bottle, and the box would silently ship with no gift.
        // Only one is reserved now, against two before, so the kit pool is wider than it was.
        const reservedForGift = giftReserveId && wineId === giftReserveId;
        return p &&
          !excludedCustomerProductIds.has(wineId) &&
          !reservedForGift &&
          getPaidDisplayPrice(p) <= maxPrice &&
          isCatalogProductInStock(p) &&
          this.isWineAllowedForColorPreference(wine, catalog);
      })
      .map(wine => {
        const decorated = this.decorateWineForCalibration(wine, context);
        const product = catalog[String(wine.wine_id)];
        return { ...decorated, _displayPrice: product ? getPaidDisplayPrice(product) : 0 };
      })
      .sort((a, b) => a._axisDistance - b._axisDistance));

    if (!candidates.length) return { selectedWines: [], visualization: this.buildCalibrationVisualization([], context, searchPool, catalog), candidates: [] };

    const cfg = this.calibrationConfig;
    const desiredMix = this.getDesiredColorMix();
    const enforceColorMix = (this.colorPreference === 'mix' || this.colorPreference === 'both') && !!desiredMix;
    const getColorCode = (wine) => this.getWineColorCode(wine, catalog);
    const countColors = (pack) => {
      const counts = { red: 0, white: 0 };
      (pack || []).forEach(wine => {
        const code = getColorCode(wine);
        if (code === 'R') counts.red += 1;
        if (code === 'W') counts.white += 1;
      });
      return counts;
    };
    const matchesRequiredMix = (pack, requiredMix) => {
      if (!requiredMix) return true;
      const counts = countColors(pack);
      return counts.red === requiredMix.red && counts.white === requiredMix.white;
    };
    const packTotal = (pack) => pack.reduce((sum, wine) => sum + (Number(wine?._displayPrice) || 0), 0);
    const inPackPriceRange = (pack) => {
      const total = packTotal(pack);
      return total >= trioMin && total <= trioMax;
    };
    const findPricedPack = (preferredPack, requiredMix = null) => {
      if (preferredPack.length === 3 && inPackPriceRange(preferredPack) && matchesRequiredMix(preferredPack, requiredMix)) return preferredPack;

      const currentIds = new Set(preferredPack.map(wine => String(wine?.wine_id || '')));
      const targetTotal = (trioMin + trioMax) / 2;
      const pool = candidates;
      let best = null;
      let closestFallback = null;

      for (let i = 0; i < pool.length; i += 1) {
        const a = pool[i];
        for (let j = 0; j < pool.length; j += 1) {
          if (j === i) continue;
          const b = pool[j];
          for (let k = j + 1; k < pool.length; k += 1) {
            if (k === i || k === j) continue;
            const c = pool[k];
            const ids = new Set([String(a.wine_id), String(b.wine_id), String(c.wine_id)]);
            if (ids.size !== 3) continue;
            const pack = [a, b, c];
            const colorCounts = countColors(pack);
            const total = packTotal(pack);
            const preserved = pack.reduce((count, wine) => count + (currentIds.has(String(wine.wine_id)) ? 1 : 0), 0);
            const score =
              (a._axisDistance * 3) +
              b._axisDistance +
              c._axisDistance +
              (Math.abs(total - targetTotal) / 1000) -
              (preserved * 0.08);
            const colorGap = requiredMix
              ? Math.abs(colorCounts.red - requiredMix.red) + Math.abs(colorCounts.white - requiredMix.white)
              : 0;
            const fallbackPenalty = total < trioMin
              ? (trioMin - total) / 100
              : total > trioMax
                ? (total - trioMax) / 100
                : 0;
            const fallbackScore = score + (fallbackPenalty * 10) + (colorGap * 10);
            if (!closestFallback || fallbackScore < closestFallback.score) {
              closestFallback = { pack, score: fallbackScore, total };
            }
            if (total < trioMin || total > trioMax) continue;
            if (!matchesRequiredMix(pack, requiredMix)) continue;
            if (!best || score < best.score) {
              best = { pack, score, total };
            }
          }
        }
      }

      return best?.pack || closestFallback?.pack || preferredPack;
    };
    let bottleA = null;
    let bottleB = null;
    let bottleC = null;
    let distA = new Map();
    let distB = new Map();
    let distanceFromA = () => null;
    let distanceFromB = () => null;

    // Replayable so the two passes can run it against different trio bands. Everything it
    // touches is local: the distance maps the caller needs are rebuilt below from whichever
    // trio finally wins, so a discarded pass leaves nothing behind.
    const selectTrio = async () => {
    const selectedIds = new Set();
    const pickClosest = (pool) => pool.find(wine => !selectedIds.has(String(wine.wine_id))) || null;

    // Lab similarity decides the pack. Axis distance and price only rank the
    // packs that already hold up, so the 3 bottles can never drift apart.
    const labTrio = await this.selectCalibrationTrioByLabSimilarity(candidates, {
      requiredMix: enforceColorMix ? desiredMix : null,
      matchesRequiredMix,
      packTotal,
      minPackPrice: trioMin,
      maxPackPrice: trioMax,
      context
    });

    if (labTrio) {
      return labTrio;
    } else if (enforceColorMix) {
      const usedIds = new Set();
      const pickFromPool = (pool, count) => {
        const selected = [];
        for (const wine of pool) {
          const id = String(wine.wine_id || '');
          if (!id || usedIds.has(id)) continue;
          selected.push(wine);
          usedIds.add(id);
          if (selected.length >= count) break;
        }
        return selected;
      };
      const redPool = candidates.filter(wine => getColorCode(wine) === 'R');
      const whitePool = candidates.filter(wine => getColorCode(wine) === 'W');
      const seedSelection = [
        ...pickFromPool(redPool, desiredMix.red),
        ...pickFromPool(whitePool, desiredMix.white)
      ];
      if (seedSelection.length < 3) {
        seedSelection.push(...pickFromPool(candidates, 3 - seedSelection.length));
      }
      seedSelection.sort((a, b) => a._axisDistance - b._axisDistance);
      return findPricedPack(this.uniqueWinesByProduct(seedSelection).slice(0, 3), desiredMix);
    } else {
      let localA = pickClosest(candidates);
      let localB = null;
      let localC = null;
      if (!localA) return [];
      selectedIds.add(String(localA.wine_id));

      const zonePoolBase = candidates.filter(wine => wine._insideTasteZone && !selectedIds.has(String(wine.wine_id)));
      const poolBase = zonePoolBase.length ? zonePoolBase : candidates.filter(wine => !selectedIds.has(String(wine.wine_id)));
      const localDistA = await this.getPerceptiveDistanceMap(localA.wine_id);
      const distanceFromA = (wine) => this.getDistanceBetweenSelected(localDistA, wine);
      let localDistB = new Map();
      const withinWindow = (distance, window) => Number.isFinite(distance) && distance >= window.min && distance <= window.max;
      const closestToTarget = (pool, scorer) => pool
        .map(wine => ({ wine, score: scorer(wine) }))
        .filter(item => Number.isFinite(item.score))
        .sort((a, b) => a.score - b.score || a.wine._axisDistance - b.wine._axisDistance)[0]?.wine || null;
      const pickBInWindow = (pool, window) => pool.find(wine => {
        const distance = distanceFromA(wine);
        return withinWindow(distance, window);
      });

      for (const window of cfg.similarityWindows) {
        localB = pickBInWindow(poolBase, window);
        if (localB) break;
      }
      if (!localB) {
        localB = closestToTarget(poolBase, wine => Math.abs((distanceFromA(wine) ?? Infinity) - cfg.targetPerceptiveDistance));
      }
      if (!localB) localB = pickClosest(poolBase);

      if (localB) selectedIds.add(String(localB.wine_id));

      const poolForC = (zonePoolBase.length ? zonePoolBase : candidates).filter(wine => !selectedIds.has(String(wine.wine_id)));
      if (localB) localDistB = await this.getPerceptiveDistanceMap(localB.wine_id);
      const distanceFromB = (wine) => this.getDistanceBetweenSelected(localDistB, wine);
      const pickCInWindow = (window) => poolForC.find(wine => {
        const a = distanceFromA(wine);
        const b = distanceFromB(wine);
        return withinWindow(a, window) && withinWindow(b, window);
      });

      // Bottle 3 used to be picked AT a target perceptive distance from both others, which is
      // what made it the furthest bottle in the box and what three customers disliked. It is
      // now picked for closeness to the customer inside the taste zone, like bottle 1, and the
      // similarity windows only cap how far it may drift, never how far it must go.
      localC = poolForC
        .filter(wine => {
          const a = distanceFromA(wine);
          const b = distanceFromB(wine);
          const widest = cfg.similarityWindows[cfg.similarityWindows.length - 1];
          return (!Number.isFinite(a) || a <= widest.max) && (!Number.isFinite(b) || b <= widest.max);
        })
        .sort((a, b) => a._axisDistance - b._axisDistance)[0] || null;
      if (!localC) localC = pickClosest(poolForC);

      return findPricedPack(this.uniqueWinesByProduct([localA, localB, localC].filter(Boolean)).slice(0, 3));
    }
    };

    // Two passes. Pass 1 aims for four bottles by lowering the trio band so the cheapest
    // eligible wine still fits under `maxPackPrice`; the pack floor is then satisfied by the
    // fourth bottle instead of being forced onto the trio's prices, which is what used to
    // push a cheap-tasting customer up into wines chosen for their price and not their match.
    // Pass 2 is the historical three-bottle band and always runs if pass 1 finds no fourth.
    const cheapestCandidate = candidates.reduce((min, wine) => {
      const price = Number(wine?._displayPrice) || 0;
      return price > 0 && price < min ? price : min;
    }, Infinity);
    const forcedPack = this.getForcedPackSize();
    const passes = [];
    if (forcedPack !== 3 && Number.isFinite(cheapestCandidate) && maxPackPrice - cheapestCandidate > 0) {
      const wantMax = maxPackPrice - cheapestCandidate;
      passes.push({ wantFourth: true, min: Math.max(0, wantMax - 1000), max: wantMax });
    }
    if (forcedPack !== 4) passes.push({ wantFourth: false, min: minPackPrice, max: maxPackPrice });
    if (!passes.length) passes.push({ wantFourth: false, min: minPackPrice, max: maxPackPrice });

    let bottleD = null;
    for (const pass of passes) {
      trioMin = pass.min;
      trioMax = pass.max;
      const trio = (await selectTrio()) || [];
      if (trio.length < 3) continue;
      if (!pass.wantFourth) {
        [bottleA, bottleB, bottleC] = trio;
        bottleD = null;
        break;
      }
      const fourth = this.pickSafeFourthBottle({
        trio,
        candidates,
        packTotal,
        minPackPrice,
        maxPackPrice,
        getColorCode
      });
      if (fourth) {
        [bottleA, bottleB, bottleC] = trio;
        bottleD = fourth;
        break;
      }
    }
    // Restore the pack band so anything reading it after the loop sees the real one.
    trioMin = minPackPrice;
    trioMax = maxPackPrice;

    if (bottleA) distA = await this.getPerceptiveDistanceMap(bottleA.wine_id);
    if (bottleB) distB = await this.getPerceptiveDistanceMap(bottleB.wine_id);
    distanceFromA = (wine) => this.getDistanceBetweenSelected(distA, wine);
    distanceFromB = (wine) => this.getDistanceBetweenSelected(distB, wine);

    // Only fetched when a fourth bottle exists, so a three-bottle box makes no extra
    // request and its distance figures keep the exact expression they had before.
    let distCMap = new Map();
    if (bottleD && bottleC) distCMap = await this.getPerceptiveDistanceMap(bottleC.wine_id);
    const distanceFromC = (wine) => this.getDistanceBetweenSelected(distCMap, wine);

    const selected = this.uniqueWinesByProduct([bottleA, bottleB, bottleC, bottleD].filter(Boolean)).slice(0, 4);
    const annotated = selected.map((wine, index) => {
      // Bottle 3 is no longer `controlled_exploration`: that bottle was chosen for being the
      // furthest from the customer's answers and three customers disliked exactly it.
      const role = index === 0
        ? 'closest_match'
        : index === 1
          ? 'same_direction_variation'
          : index === 2
            ? 'second_safe_match'
            : 'third_safe_match';
      const distanceToA = index === 0 ? 0 : distanceFromA(wine) ?? null;
      const distanceToB = index === 1 ? 0 : (index === 0 && bottleB ? distanceFromA(bottleB) : distanceFromB(wine)) ?? null;
      const distanceToC = index === 2
        ? 0
        : index === 3
          ? (bottleC ? distanceFromC(wine) ?? null : null)
          : (bottleC ? (index === 0 ? distanceFromA(bottleC) : distanceFromB(bottleC)) : null);
      return this.addCalibrationMetadata(wine, role, index + 1, isEN, {
        distance_to_bottle_1: distanceToA,
        distance_to_bottle_2: distanceToB,
        distance_to_bottle_3: distanceToC,
        similarity_to_bottle_1_pct: this.similarityPctFromDistance(distanceToA),
        similarity_to_bottle_2_pct: this.similarityPctFromDistance(distanceToB),
        similarity_to_bottle_3_pct: this.similarityPctFromDistance(distanceToC),
        perceptive_distance_from_bottle_A: index === 0 ? null : distanceToA,
        perceptive_distance_from_previous_selected_bottles: index === 2
          ? { from_bottle_A: distanceToA, from_bottle_B: distanceToB }
          : index === 3
            ? { from_bottle_A: distanceToA, from_bottle_B: distanceToB, from_bottle_C: distanceToC }
            : null
      });
    });

    const visualization = this.buildCalibrationVisualization(annotated, context, searchPool, catalog);
    sessionStorage.setItem('vinpop_calibration_recommendation', JSON.stringify({
      selected_wines: annotated.map(wine => ({
        product_id: String(wine.wine_id),
        title: catalog[String(wine.wine_id)]?.title || '',
        color: this.getWineColorCode(wine, catalog),
        map_x: parseFloat(wine.map_x) || 0,
        map_y: parseFloat(wine.map_y) || 0,
        map_intensity: parseFloat(wine.map_intensity) || 0,
        axis_distance_to_customer: wine.axis_distance_to_customer,
        role: wine._calibrationRole,
        reason_title: wine.reason_title,
        reason_text: wine.reason_text,
        distance_to_bottle_1: wine.distance_to_bottle_1,
        distance_to_bottle_2: wine.distance_to_bottle_2,
        distance_to_bottle_3: wine.distance_to_bottle_3,
        similarity_to_bottle_1_pct: wine.similarity_to_bottle_1_pct,
        similarity_to_bottle_2_pct: wine.similarity_to_bottle_2_pct,
        similarity_to_bottle_3_pct: wine.similarity_to_bottle_3_pct,
        perceptive_distance_from_bottle_A: wine.perceptive_distance_from_bottle_A,
        perceptive_distance_from_previous_selected_bottles: wine.perceptive_distance_from_previous_selected_bottles
      })),
      visualization
    }));
    // `candidates` is the full catalogue already scored and sorted by taste distance.
    // The +1 bottle upsell reads it so the extra bottle is ranked by the same maths
    // as the kit itself, instead of being a random cross-sell.
    return { selectedWines: annotated, visualization, candidates };
  }

  buildCalibrationMapHTML(visualization, isEN) {
    if (!visualization || !visualization.map_bounds || !Array.isArray(visualization.selected_wines)) return '';
    const bottle1 = visualization.selected_wines[0] || {};
    const bottle2 = visualization.selected_wines[1] || {};
    const bottle3 = visualization.selected_wines[2] || {};
    const similarityLabel = (value) => Number.isFinite(Number(value)) ? `<strong>${Math.round(Number(value))}%</strong>` : '<strong>--</strong>';
    const sim12 = similarityLabel(bottle1.similarity_to_bottle_2_pct ?? bottle2.similarity_to_bottle_1_pct);
    const sim13 = similarityLabel(bottle1.similarity_to_bottle_3_pct ?? bottle3.similarity_to_bottle_1_pct);
    const sim23 = similarityLabel(bottle2.similarity_to_bottle_3_pct ?? bottle3.similarity_to_bottle_2_pct);
    const triangleHTML = `
      <div class="wr-calibration-triangle" aria-label="${isEN ? 'Taste similarity between wines' : 'Smaakovereenkomst tussen wijnen'}">
        <em class="wr-calibration-triangle__caption">${isEN ? 'Taste similarity between wines' : 'Smaakovereenkomst tussen wijnen'}</em>
        <div class="wr-calibration-triangle__node wr-calibration-triangle__node--one"><strong>1</strong><span>${isEN ? 'Best match to your taste' : 'Beste match met jouw smaak'}</span></div>
        <div class="wr-calibration-triangle__node wr-calibration-triangle__node--two"><strong>2</strong><span>${isEN ? 'Close variation' : 'Nauwe variatie'}</span></div>
        <div class="wr-calibration-triangle__node wr-calibration-triangle__node--three"><strong>3</strong><span>${isEN ? 'Safe exploration' : 'Veilig ontdekken'}</span></div>
        <span class="wr-calibration-triangle__line wr-calibration-triangle__line--12">${sim12}</span>
        <span class="wr-calibration-triangle__line wr-calibration-triangle__line--13">${sim13}</span>
        <span class="wr-calibration-triangle__line wr-calibration-triangle__line--23">${sim23}</span>
      </div>
    `;
    return `
      <div class="wr-calibration-map" aria-label="${isEN ? 'Your calibration wines' : 'Jouw kalibratiewijnen'}">
        <div class="wr-calibration-map__head">
          <strong>${isEN ? 'Your calibration wines' : 'Jouw kalibratiewijnen'}</strong>
          <span>${isEN ? 'Three wines chosen to confirm your taste, reduce the risk of guessing, and guide you to a better match.' : 'Drie wijnen gekozen om jouw smaak te bevestigen, gokwerk te verminderen en je naar een betere match te begeleiden.'}</span>
        </div>
        ${triangleHTML}
      </div>
    `;
  }

  // Full-screen wine-by-wine reveal played over the (already built) bundle page.
  // Auto-advances so cold mobile traffic never has to click to see their result.
  buildWineRevealHTML(packWines, isEN, catalog, giftProductId) {
    const wines = (packWines || []).filter(w => w && catalog[String(w.wine_id)]);
    if (wines.length < 2) return '';
    const escHTML = (s) => String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    // No "Discovery" label any more: bottle 3 stopped being the far bottle when three
    // customers reported disliking exactly that one. Every slot now reads as a match.
    const roleLabels = isEN
      ? { 1: 'Best match', 2: 'Close match', 3: 'Safe match', 4: 'Safe match' }
      : { 1: 'Beste match', 2: 'Dichte match', 3: 'Veilige match', 4: 'Veilige match' };

    const items = wines.map((wine, i) => {
      const p = catalog[String(wine.wine_id)];
      const idx = wine._calibrationIndex || (i + 1);
      let label = roleLabels[idx] || (isEN ? 'Your match' : 'Jouw match');
      if (wine._isBonus) label = isEN ? 'Bonus bottle' : 'Bonusfles';
      else if (wine._isSimilar) label = isEN ? 'Surprise match' : 'Verrassingsmatch';
      let reason = wine.reason_text || wine.reason_title || '';
      if (reason.length > 150) reason = reason.slice(0, 147).trim() + '…';
      return {
        badge: `#${idx} · ${label}`,
        badgeClass: '',
        img: p.image && p.image.url ? p.image.url : '',
        title: p.title,
        reason
      };
    });

    // Free gift bottle as a distinct FINAL card: a gift, never a taste match, no % shown.
    const gift = giftProductId ? catalog[String(giftProductId)] : null;
    if (gift) {
      items.push({
        badge: isEN ? '🎁 Free gift' : '🎁 Gratis cadeau',
        badgeClass: 'wr-reveal__badge--gift',
        img: gift.image && gift.image.url ? gift.image.url : '',
        title: gift.title,
        reason: isEN
          ? 'A bottle on us. Our gift for trying VinPop, with no strings attached.'
          : 'Een fles van ons. Ons cadeau omdat je VinPop probeert, zonder verplichtingen.'
      });
    }

    const cards = items.map((it, i) => (
      `<article class="wr-reveal__card${i === 0 ? ' is-active' : ''}" data-idx="${i}">` +
      `<div class="wr-reveal__badge ${it.badgeClass}">${escHTML(it.badge)}</div>` +
      `<div class="wr-reveal__imgwrap">${it.img ? `<img src="${escHTML(it.img)}" alt="${escHTML(it.title)}">` : ''}</div>` +
      `<h3 class="wr-reveal__name">${escHTML(it.title)}</h3>` +
      `<p class="wr-reveal__reason">${escHTML(it.reason)}</p>` +
      `</article>`
    )).join('');
    const segments = items.map(() => `<span class="wr-reveal__seg"><span class="wr-reveal__seg-fill"></span></span>`).join('');
    const eyebrow = isEN
      ? 'Your matches, chosen from 10,000 lab-analyzed wines:'
      : 'Jouw matches, gekozen uit 10.000 lab-geanalyseerde wijnen:';
    const hint = isEN ? 'Tap to go faster' : 'Tik om sneller te gaan';
    const introTitle = isEN ? 'Analysing your taste…' : 'Je smaak analyseren…';
    return `
      <div class="wr-reveal" id="wr-reveal" role="dialog" aria-label="${isEN ? 'Your wine matches' : 'Jouw wijnmatches'}">
        <button class="wr-reveal__skip" type="button">${isEN ? 'Skip' : 'Overslaan'}</button>
        <div class="wr-reveal__intro">
          <canvas class="wr-reveal__intro-canvas"></canvas>
          <div class="wr-reveal__intro-text"><p class="wr-reveal__intro-title">${introTitle}</p></div>
        </div>
        <div class="wr-reveal__inner">
          <div class="wr-reveal__progress">${segments}</div>
          <div class="wr-reveal__eyebrow">${eyebrow}</div>
          <div class="wr-reveal__stage">${cards}</div>
          <button class="wr-reveal__next" type="button" data-last-label="${isEN ? 'See my box →' : 'Bekijk mijn box →'}" data-next-label="${isEN ? 'Next →' : 'Volgende →'}">${isEN ? 'Next →' : 'Volgende →'}</button>
          <button class="wr-reveal__hint" type="button" style="background:none;border:0;padding:10px 16px;margin-top:0;cursor:pointer;font:inherit;font-size:12px;text-decoration:underline;text-underline-offset:3px;">${hint}</button>
        </div>
      </div>
    `;
  }

  playWineReveal(overlay, onDone) {
    const cards = Array.from(overlay.querySelectorAll('.wr-reveal__card'));
    const segFills = Array.from(overlay.querySelectorAll('.wr-reveal__seg-fill'));
    const nextBtn = overlay.querySelector('.wr-reveal__next');
    const skipBtn = overlay.querySelector('.wr-reveal__skip');
    if (!cards.length || !nextBtn) { overlay.remove(); if (onDone) onDone(); return; }
    const DURATION = 1800; // ms per card (stories-style, snappier than before)
    const last = cards.length - 1;
    let idx = 0, timer = null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const clearTimer = () => { if (timer) { clearTimeout(timer); timer = null; } };
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      clearTimer();
      overlay.classList.add('is-closing');
      document.body.style.overflow = prevOverflow;
      setTimeout(() => { overlay.remove(); if (onDone) onDone(); }, 360);
    };
    const paintProgress = (n) => {
      segFills.forEach((f, i) => {
        f.style.transition = 'none';
        f.style.transform = i < n ? 'scaleX(1)' : 'scaleX(0)';
      });
      const active = segFills[n];
      if (active) {
        void active.offsetWidth; // reflow so the fill transition actually runs
        active.style.transition = `transform ${DURATION}ms linear`;
        active.style.transform = 'scaleX(1)';
      }
    };
    const show = (n) => {
      cards.forEach((c, i) => c.classList.toggle('is-active', i === n));
      nextBtn.textContent = n >= last ? nextBtn.dataset.lastLabel : nextBtn.dataset.nextLabel;
      nextBtn.classList.toggle('is-final', n >= last);
      paintProgress(n);
      clearTimer();
      // Auto-advance every card, including the last → then go straight to the results page (no click needed).
      timer = setTimeout(() => advance(), DURATION);
    };
    const advance = () => { if (idx < last) { idx += 1; show(idx); } else { finish(); } };

    nextBtn.addEventListener('click', advance);
    // 2026-09-28 : « Tap to go faster » est un vrai bouton (il n'etait qu'un texte : le toucher
    // ne faisait rien). Il avance au vin suivant, comme « Volgende », puis ouvre la box.
    const hintBtn = overlay.querySelector('.wr-reveal__hint');
    if (hintBtn) hintBtn.addEventListener('click', advance);
    if (skipBtn) skipBtn.addEventListener('click', finish);
    overlay.querySelector('.wr-reveal__stage').addEventListener('click', advance);

    // 3D analysis intro: wine points appear in depth, then hand off to the cards.
    const intro = overlay.querySelector('.wr-reveal__intro');
    const canvas = intro ? intro.querySelector('canvas') : null;
    if (intro && canvas) {
      const isEN = (document.documentElement.lang || '').toLowerCase().slice(0, 2) !== 'nl';
      overlay.classList.add('wr-reveal--analyzing');
      let intoCards = false, stopIntro = null;
      const startCards = () => {
        if (intoCards) return; intoCards = true;
        if (stopIntro) stopIntro();
        overlay.classList.remove('wr-reveal--analyzing');
        show(0);
      };
      stopIntro = this.runAnalysisIntro(canvas, intro.querySelector('.wr-reveal__intro-title'), isEN, startCards);
      intro.addEventListener('click', startCards);
    } else {
      show(0);
    }
  }

  _roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }

  // Living taste-map intro: a drifting cloud of analyzed wines, the real wines settling into
  // place, then "you" and the 3 matched wines lit up with their names. Then hands off to cards.
  runAnalysisIntro(canvas, titleEl, isEN, onDone) {
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    let done = false, raf = 0, nameTimer = 0;
    const finishIntro = () => { if (done) return; done = true; if (raf) cancelAnimationFrame(raf); if (nameTimer) clearInterval(nameTimer); onDone(); };
    const ctx = canvas.getContext('2d');
    if (!ctx) { finishIntro(); return finishIntro; }
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    canvas.width = (canvas.clientWidth || window.innerWidth) * dpr;
    canvas.height = (canvas.clientHeight || window.innerHeight) * dpr;
    const W = canvas.width, H = canvas.height, cxc = W / 2, cyc = H / 2;

    const cleanName = (s) => String(s || '')
      .replace(/[\u{1F1E6}-\u{1F1FF}]/gu, '')
      .replace(/[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{2190}-\u{21FF}]/gu, '')
      .replace(/\s+/g, ' ').trim();
    const truncate = (s, n) => (s.length > n ? s.slice(0, n - 1).trim() + '…' : s);

    // Real wine positions from the taste-map visualization.
    const viz = this._lastVisualization;
    const dots = []; let you = null; const matches = []; const names = [];
    const b = viz && viz.map_bounds;
    if (b && Array.isArray(viz.all_wines) && viz.all_wines.length) {
      const xR = (b.x_max - b.x_min) || 1, yR = (b.y_max - b.y_min) || 1;
      const pad = Math.min(W, H) * 0.17;
      const fx = (x) => pad + ((x - b.x_min) / xR) * (W - 2 * pad);
      const fy = (y) => pad + (1 - (y - b.y_min) / yR) * (H - 2 * pad);
      const selIds = new Set((viz.selected_wines || []).map(w => String(w.product_id)));
      viz.all_wines.forEach(w => {
        dots.push({ x: fx(w.x), y: fy(w.y), col: String(w.color).toUpperCase() === 'W' ? '#E7C662' : '#D45D4A', sel: selIds.has(String(w.product_id)) });
        const nm = cleanName(w.title); if (nm) names.push(truncate(nm, 30));
      });
      if (viz.customer_point) you = { x: fx(viz.customer_point.x), y: fy(viz.customer_point.y) };
      (viz.selected_wines || []).forEach((w, i) => matches.push({ x: fx(w.x), y: fy(w.y), label: String(w.label || i + 1), title: truncate(cleanName(w.title) || (isEN ? 'Your match' : 'Jouw match'), 22) }));
    } else {
      let s = 1234567; const rnd = () => (s = (s * 1103515245 + 12345) & 0x7fffffff) / 0x7fffffff;
      for (let i = 0; i < 70; i++) dots.push({ x: W * (0.12 + 0.76 * rnd()), y: H * (0.12 + 0.76 * rnd()), col: rnd() > 0.5 ? '#E7C662' : '#D45D4A', sel: false });
      you = { x: cxc, y: cyc };
    }
    const APPEAR = reduce ? 0 : 1500;
    dots.forEach((d, i) => { d.appear = reduce ? 0 : (i / Math.max(1, dots.length - 1)) * APPEAR; });

    // Decorative "all analyzed wines" cloud, drifting in parallax for a living map.
    const deco = [];
    let s2 = 90210; const rnd2 = () => (s2 = (s2 * 1103515245 + 12345) & 0x7fffffff) / 0x7fffffff;
    const DECO = reduce ? 50 : 150;
    for (let i = 0; i < DECO; i++) deco.push({ bx: rnd2(), by: rnd2(), depth: 0.3 + rnd2() * 0.7, vx: rnd2() - 0.5, vy: rnd2() - 0.5, tw: rnd2() * Math.PI * 2, col: rnd2() > 0.6 ? '#E7C662' : '#D45D4A' });

    const ease = (p) => 1 - Math.pow(1 - p, 3);
    const cf = (v) => Math.max(0, Math.min(1, v));
    const DURATION = reduce ? 800 : 3300;
    const t0 = performance.now();
    const rot2 = (x, y, a) => { const dx = x - cxc, dy = y - cyc, c = Math.cos(a), sn = Math.sin(a); return [cxc + dx * c - dy * sn, cyc + dx * sn + dy * c]; };

    const draw = (now) => {
      if (done) return;
      if (!document.body.contains(canvas)) { finishIntro(); return; }
      const t = now - t0;
      const rot = reduce ? 0 : 0.10 * Math.sin(t * 0.00045);
      ctx.clearRect(0, 0, W, H);

      // drifting analyzed-wines cloud
      for (const p of deco) {
        let bx = (p.bx + p.vx * t * 0.000018) % 1; if (bx < 0) bx += 1;
        let by = (p.by + p.vy * t * 0.000018) % 1; if (by < 0) by += 1;
        const [x, y] = rot2(bx * W, by * H, rot * 0.5 * p.depth);
        ctx.globalAlpha = (0.08 + 0.16 * (0.5 + 0.5 * Math.sin(t * 0.002 + p.tw))) * p.depth;
        ctx.fillStyle = p.col;
        ctx.beginPath(); ctx.arc(x, y, (0.6 + 1.3 * p.depth) * dpr, 0, Math.PI * 2); ctx.fill();
      }

      // real wines settle into their map positions
      for (const d of dots) {
        const p = reduce ? 1 : cf((t - d.appear) / 480);
        if (p <= 0) continue;
        const e = ease(p);
        const [x, y] = rot2(cxc + (d.x - cxc) * e, cyc + (d.y - cyc) * e, rot);
        const r = (d.sel ? 3.4 : 2.2) * dpr * (0.45 + 0.55 * e);
        ctx.globalAlpha = p * (d.sel ? 1 : 0.82);
        ctx.fillStyle = d.col;
        ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
        if (d.sel && p > 0.85) { ctx.globalAlpha = p; ctx.strokeStyle = '#fff'; ctx.lineWidth = 1.2 * dpr; ctx.stroke(); }
      }

      // you + lines + match name labels
      const yp = reduce ? 1 : cf((t - 1500) / 500);
      if (you && yp > 0) {
        const [yx, yy] = rot2(you.x, you.y, rot);
        const lp = reduce ? 1 : cf((t - 1850) / 550);
        if (lp > 0) {
          ctx.globalAlpha = 0.6 * lp; ctx.strokeStyle = '#8FE3D0'; ctx.lineWidth = 1 * dpr; ctx.setLineDash([3 * dpr, 3 * dpr]);
          for (const m of matches) { const [mx, my] = rot2(m.x, m.y, rot); ctx.beginPath(); ctx.moveTo(yx, yy); ctx.lineTo(yx + (mx - yx) * lp, yy + (my - yy) * lp); ctx.stroke(); }
          ctx.setLineDash([]);
        }
        const np = reduce ? 1 : cf((t - 2150) / 450);
        if (np > 0) {
          ctx.font = `700 ${11 * dpr}px system-ui, -apple-system, sans-serif`;
          ctx.textAlign = 'center'; ctx.textBaseline = 'alphabetic';
          for (const m of matches) {
            const [mx, my] = rot2(m.x, m.y, rot);
            const label = `${m.label}. ${m.title}`;
            const tw = ctx.measureText(label).width, padx = 6 * dpr, h = 17 * dpr;
            const lx = Math.max(tw / 2 + 8 * dpr, Math.min(W - tw / 2 - 8 * dpr, mx));
            const ly = Math.max(24 * dpr, my - 10 * dpr);
            ctx.globalAlpha = np;
            ctx.fillStyle = 'rgba(12,20,37,0.85)';
            this._roundRect(ctx, lx - tw / 2 - padx, ly - h, tw + padx * 2, h, 4 * dpr); ctx.fill();
            ctx.fillStyle = '#eafff9';
            ctx.fillText(label, lx, ly - 5 * dpr);
          }
          ctx.textAlign = 'start';
        }
        const pulse = 1 + 0.16 * Math.sin(t * 0.006);
        const g = ctx.createRadialGradient(yx, yy, 0, yx, yy, 28 * dpr * pulse);
        g.addColorStop(0, `rgba(111,224,200,${0.55 * yp})`); g.addColorStop(1, 'rgba(111,224,200,0)');
        ctx.globalAlpha = 1; ctx.fillStyle = g;
        ctx.beginPath(); ctx.arc(yx, yy, 28 * dpr * pulse, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = '#c7f9ee'; ctx.globalAlpha = yp;
        ctx.beginPath(); ctx.arc(yx, yy, 4.5 * dpr, 0, Math.PI * 2); ctx.fill();
      }

      ctx.globalAlpha = 1;
      if (t >= DURATION) { finishIntro(); return; }
      raf = requestAnimationFrame(draw);
    };

    // Title: scan through real wine names while analysing, then announce the matches.
    if (titleEl) {
      const analysing = isEN ? 'Analysing' : 'Analyseren';
      const finalTitle = isEN ? 'Your 3 closest wines' : 'Jouw 3 dichtste wijnen';
      if (reduce || !names.length) {
        titleEl.textContent = finalTitle;
      } else {
        let k = 0;
        titleEl.textContent = `${analysing}: ${names[0]}`;
        nameTimer = setInterval(() => {
          if (done) return;
          k = (k + 1) % names.length;
          titleEl.textContent = `${analysing}: ${names[k]}`;
        }, 110);
        setTimeout(() => { if (nameTimer) { clearInterval(nameTimer); nameTimer = 0; } if (!done && titleEl) titleEl.textContent = finalTitle; }, 2050);
      }
    }
    raf = requestAnimationFrame(draw);
    return finishIntro;
  }

  logCalibrationMapDebug(payload) {
    const isDevelopmentTheme = /preview_theme_id=196622516611/.test(window.location.search) || window.Shopify?.designMode;
    if (!isDevelopmentTheme) return;
    console.log('Vinpop calibration map debug', {
      all_wines_shown: payload.allWines.length,
      customer_point: payload.customer,
      selected_wines: payload.selectedWines.map(wine => ({ product_id: wine.product_id, x: wine.x, y: wine.y, intensity: wine.intensity })),
      map_bounds: payload.bounds,
      taste_zone_bounds: payload.zone
    });
  }

  async restoreQuizFromApiOrStart(isInline) {
    if (this.isForceRetakeEnabled()) {
      this.openPopup();
      return;
    }

    try {
      this.showQuizLoading();
      const payload = await this.fetchSavedQuizPayloadFromApi();
      if (this.isForceRetakeEnabled()) {
        this.openPopup();
        return;
      }
      if (payload && this.applySavedQuizPayload(payload)) {
        console.log('TasteProfileQuiz: restored quiz answers from API', payload);
        if (this.isForceRetakeEnabled()) {
          this.openPopup();
          return;
        }
        await this.fetchResults({ skipQuizSave: true, preserveScroll: true });
        return;
      }
      console.log('TasteProfileQuiz: no saved quiz answers found in API');
    } catch (error) {
      console.warn('TasteProfileQuiz: could not restore quiz answers from API:', error);
    }

    this.openPopup();
  }

  isForceRetakeEnabled() {
    try {
      return sessionStorage.getItem(this.forceRetakeStorageKey) === '1';
    } catch (error) {
      return false;
    }
  }

  setForceRetake(enabled) {
    try {
      if (enabled) {
        sessionStorage.setItem(this.forceRetakeStorageKey, '1');
      } else {
        sessionStorage.removeItem(this.forceRetakeStorageKey);
      }
    } catch (error) {
      console.warn('TasteProfileQuiz: could not update retake flag', error);
    }
  }

  showQuizLoading() {
    this.popup.querySelectorAll('.taste-profile-question').forEach(q => {
      q.style.display = 'none';
    });
    const progressEl = this.popup.querySelector('.taste-profile-progress');
    if (progressEl) progressEl.style.display = 'none';
    if (this.loadingEl) this.loadingEl.style.display = 'block';
  }

  async fetchSavedQuizPayloadFromApi() {
    const apiBase = (this.getMetaContent('api-url') || 'https://api.vinpop.fr').replace(/\/+$/, '');
    const urls = [
      `${apiBase}/api/quizz/get?quizz_type=test-kit`,
      `${apiBase}/api/quizz/get`
    ];
    let lastError = null;

    for (const url of urls) {
      try {
        const response = await fetch(url, {
          method: 'GET',
          headers: this.buildVinpopAuthHeaders(),
          credentials: 'omit',
          mode: 'cors'
        });

        if (response.status === 204 || response.status === 404) {
          lastError = null;
          continue;
        }

        if (!response.ok) {
          lastError = new Error('quiz_get_failed_' + response.status);
          continue;
        }

        const data = await response.json().catch(() => null);
        return this.normalizeSavedQuizPayload(data);
      } catch (error) {
        lastError = error;
      }
    }

    if (lastError) {
      throw lastError;
    }
    return null;
  }

  normalizeSavedQuizPayload(data) {
    const unwrap = value => {
      if (!value) return null;
      if (Array.isArray(value)) {
        return value.find(item => {
          const type = String(item?.quizz_type || item?.quiz_type || '').toLowerCase();
          return type === 'test-kit';
        }) || value[0] || null;
      }
      return value;
    };

    const candidates = [
      unwrap(data),
      unwrap(data?.data),
      unwrap(data?.result),
      unwrap(data?.quiz),
      unwrap(data?.quizz),
      unwrap(data?.answers)
    ];

    for (const candidate of candidates) {
      if (!candidate || typeof candidate !== 'object') {
        continue;
      }

      const payload = { quizz_type: 'test-kit' };
      ['color', 'coffee', 'chocolate', 'salad', 'cheese', 'mood'].forEach(field => {
        const value = Number(candidate[field] ?? candidate[field.toUpperCase()]);
        if (value >= 1 && value <= 3) {
          payload[field] = value;
        }
      });

      if (['color', 'coffee', 'chocolate', 'salad', 'cheese', 'mood'].some(field => payload[field])) {
        return payload;
      }
    }

    return null;
  }

  applySavedQuizPayload(payload) {
    const fieldByQuestion = {
      1: 'color',
      2: 'coffee',
      3: 'chocolate',
      4: 'salad',
      5: 'cheese',
      6: 'mood'
    };

    this.currentQuestion = this.totalQuestions;
    this.targetX = 0;
    this.targetY = 0;
    this.targetZ = 0;
    this.colorPreference = 'both';
    this.colorMixPreference = null;
    this.answers = [];
    this.isProcessing = false;

    Object.keys(fieldByQuestion).forEach(question => {
      const questionNumber = Number(question);
      const apiField = fieldByQuestion[questionNumber];
      const choice = Number(payload[apiField]) - 1;
      const qEl = this.popup.querySelector(`.taste-profile-question[data-question="${questionNumber}"]`);
      const button = qEl ? qEl.querySelectorAll('.taste-profile-answer')[choice] : null;
      if (!button || choice < 0) {
        return;
      }

      const color = button.dataset.color;
      if (color) {
        this.colorPreference = color;
        this.colorMixPreference = null;
        this.answers.push({ question: questionNumber, apiField, color, choice, apiChoice: color === 'mix' ? 2 : choice, label: this.getAnswerButtonLabel(button) });
        return;
      }

      const x = parseFloat(button.dataset.x);
      const y = parseFloat(button.dataset.y);
      const z = parseFloat(button.dataset.z) || 0;
      if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) {
        return;
      }

      this.targetX += x;
      this.targetY += y;
      this.targetZ += z;
      this.answers.push({ question: questionNumber, apiField, x, y, z, choice, label: this.getAnswerButtonLabel(button) });
    });

    const rebuiltPayload = this.buildQuizSavePayload('test-kit');
    const requiredFields = ['color', 'coffee', 'chocolate', 'salad', 'cheese', 'mood'];
    return requiredFields.every(field => rebuiltPayload[field] >= 1 && rebuiltPayload[field] <= 3);
  }

  openPopup() {
    const isInline = this.popup.classList.contains('taste-profile-popup--inline');
    if (!isInline) {
      this.popup.style.display = 'block';
      document.body.style.overflow = 'hidden';
    }
    this.reset();
  }

  closePopup() {
    const isInline = this.popup.classList.contains('taste-profile-popup--inline');
    if (!isInline) {
      this.popup.style.display = 'none';
      document.body.style.overflow = '';
    }

    // If quiz results exist, refresh the page so the product grid updates
    if (sessionStorage.getItem('vinpop_last_results')) {
      window.location.href = window.location.pathname + '?taste_profile=true';
    }
  }

  reset() {
    const hasIntro = !!this.popup.querySelector('[data-question="0"]');
    this.currentQuestion = hasIntro ? 0 : 1;
    this.targetX = 0;
    this.targetY = 0;
    this.targetZ = 0;
    this.colorPreference = 'both';
    this.colorMixPreference = null;
    this.answers = [];
    this.isProcessing = false;
    sessionStorage.removeItem('vinpop_quiz_state');
    
    // Hide loading (critical for quiz re-open)
    if (this.loadingEl) this.loadingEl.style.display = 'none';
    
    // Hide old wine reveal sequence
    const seqEl = document.querySelector('.wine-reveal-sequence');
    if (seqEl) seqEl.style.display = 'none';
    const cardsEl = document.querySelector('.wine-reveal-cards');
    if (cardsEl) cardsEl.innerHTML = '';

    // Show/hide progress bar depending on whether intro screen exists
    const progressEl = this.popup.querySelector('.taste-profile-progress');
    if (progressEl) progressEl.style.display = hasIntro ? 'none' : '';
    
    this.showQuestion(this.currentQuestion);
  }

  // Retake: reload the page so the intro (and its Start button) are freshly bound and the
  // page opens at the top. The force-retake flag persists in sessionStorage across reload.
  _reloadToQuizTop() {
    try { if ('scrollRestoration' in history) history.scrollRestoration = 'manual'; } catch (e) {}
    try { window.scrollTo(0, 0); } catch (e) {}
    window.location.reload();
  }

  handleAnswer(e) {
    if (this.isProcessing) {
      return;
    }

    const button = e.currentTarget;
    const questionEl = button.closest('.taste-profile-question');
    const clickedQuestion = questionEl ? Number(questionEl.dataset.question) : this.currentQuestion;
    if (!clickedQuestion || clickedQuestion !== this.currentQuestion) {
      console.warn('TasteProfileQuiz: ignored answer for inactive question', {
        clickedQuestion,
        currentQuestion: this.currentQuestion
      });
      return;
    }

    const apiFieldByQuestion = {
      1: 'color',
      2: 'coffee',
      3: 'chocolate',
      4: 'salad',
      5: 'cheese',
      6: 'mood'
    };
    const apiField = apiFieldByQuestion[clickedQuestion] || '';
    if (!apiField) {
      console.warn('TasteProfileQuiz: ignored answer without API field', { clickedQuestion });
      return;
    }

    if (clickedQuestion >= 1) {
      this.setForceRetake(false);
    }

    window.VinPopTracking?.trackQuizStarted({ quiz_type: 'test-kit' });
    
    // Determine answer index (A=0, B=1, C=2)
    const allButtons = Array.from(button.parentElement.querySelectorAll('.taste-profile-answer'));
    const answerIndex = allButtons.indexOf(button);
    if (answerIndex < 0) {
      console.warn('TasteProfileQuiz: ignored answer with invalid choice', { clickedQuestion });
      return;
    }

    this.isProcessing = true;
    
    // Q1 uses data-color instead of x/y/z
    const color = button.dataset.color;
    if (color) {
      this.colorPreference = color; // 'red', 'white', 'both', or 'mix'
      const redCount = parseInt(button.dataset.redCount || '', 10);
      const whiteCount = parseInt(button.dataset.whiteCount || '', 10);
      this.colorMixPreference = color === 'mix' && Number.isFinite(redCount) && Number.isFinite(whiteCount)
        ? { red: redCount, white: whiteCount }
        : null;
      this.answers = this.answers.filter(answer => Number(answer.question) !== clickedQuestion);
      this.answers.push({
        question: clickedQuestion,
        apiField,
        color,
        redCount: this.colorMixPreference ? this.colorMixPreference.red : null,
        whiteCount: this.colorMixPreference ? this.colorMixPreference.white : null,
        choice: answerIndex,
        apiChoice: color === 'mix' ? 2 : answerIndex,
        label: this.getAnswerButtonLabel(button)
      });
      this.adaptQuestionsToColor(color === 'mix' ? 'both' : color);
    } else {
      const x = parseFloat(button.dataset.x);
      const y = parseFloat(button.dataset.y);
      const z = parseFloat(button.dataset.z) || 0;
      if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) {
        this.isProcessing = false;
        console.warn('TasteProfileQuiz: ignored answer with invalid coordinates', {
          clickedQuestion,
          x: button.dataset.x,
          y: button.dataset.y,
          z: button.dataset.z
        });
        return;
      }
      
      // Accumulate coordinates (X, Y, Z)
      this.targetX += x;
      this.targetY += y;
      this.targetZ += z;
      
      // Store answer with choice index
      this.answers = this.answers.filter(answer => Number(answer.question) !== clickedQuestion);
      this.answers.push({ question: clickedQuestion, apiField, x, y, z, choice: answerIndex, label: this.getAnswerButtonLabel(button) });
    }
    
    // Animate button selection
    button.classList.add('selected');
    
    setTimeout(() => {
      if (!this.hasAnswerForQuestion(clickedQuestion)) {
        this.isProcessing = false;
        button.classList.remove('selected');
        console.warn('TasteProfileQuiz: answer was not stored, staying on current question', {
          clickedQuestion,
          currentQuestion: this.currentQuestion
        });
        return;
      }

      if (this.currentQuestion < this.totalQuestions) {
        // Move to next question
        this.currentQuestion++;
        this.updateProgress();
        this.showQuestion(this.currentQuestion);
        button.classList.remove('selected');
        // Save state so language switch doesn't lose progress
        this.saveQuizState();
        this.isProcessing = false;
      } else {
        // All questions answered. Clear in-progress state, fetch results
        window.VinPopTracking?.trackQuizCompleted({ quiz_type: 'test-kit' });
        sessionStorage.removeItem('vinpop_quiz_state');
        this.saveCompletedQuizToApi('final_answer')
          .catch(error => {
            console.warn('TasteProfileQuiz: could not save quiz answers:', error);
          })
          .finally(() => this.fetchResults());
      }
    }, 300);
  }

  // options.skipScroll: page-load restores pass this so the visitor stays on the
  // hero instead of being yanked down to the resumed question. User-driven
  // navigation (answering, back/next) omits it and keeps the scroll-into-view.
  showQuestion(questionNumber, options = {}) {
    const questions = this.popup.querySelectorAll('.taste-profile-question');
    let visibleEl = null;
    questions.forEach(q => {
      const qNum = parseInt(q.dataset.question);
      const show = qNum === questionNumber;
      q.style.display = show ? 'block' : 'none';
      if (show) visibleEl = q;
      // Remove selected class from all answers
      q.querySelectorAll('.taste-profile-answer').forEach(btn => {
        btn.classList.remove('selected');
      });
    });

    // In inline mode, scroll the question into view
    const isInline = this.popup && this.popup.classList.contains('taste-profile-popup--inline');
    if (isInline && visibleEl && questionNumber > 0 && !options.skipScroll) {
      requestAnimationFrame(() => {
        const headerH = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-group-height')) || 120;
        const top = visibleEl.getBoundingClientRect().top + window.pageYOffset - headerH - 16;
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      });
    }
  }

  startQuestions() {
    this.currentQuestion = 1;
    const progressEl = this.popup.querySelector('.taste-profile-progress');
    if (progressEl) progressEl.style.display = '';
    this.updateProgress();
    this.showQuestion(1);
    this.saveQuizState();
  }

  // Not a question: currentQuestion stays 0 so no answer can register, and the
  // progress bar stays hidden. showQuestion() never matches data-question="path".
  showPathChooser() {
    this.currentQuestion = 0;
    this.showQuestion(-1, { skipScroll: true });
    const progressEl = this.popup.querySelector('.taste-profile-progress');
    if (progressEl) progressEl.style.display = 'none';
    this.pathChooser.style.display = 'block';
    requestAnimationFrame(() => {
      const headerH = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-group-height')) || 120;
      const top = this.pathChooser.getBoundingClientRect().top + window.pageYOffset - headerH - 16;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    });
  }

  setQuizPath(path) {
    this.quizPath = path === 'expert' ? 'expert' : 'guided';
    if (this.popup) this.popup.classList.toggle('is-expert-path', this.quizPath === 'expert');
    try { sessionStorage.setItem('vinpop_quiz_path', this.quizPath); } catch (e) {}
  }

  hasAnswerForQuestion(questionNumber) {
    return this.answers.some(answer => Number(answer.question) === Number(questionNumber));
  }

  /**
   * After Q1 (color), adapt the explanation text of Q2-Q6
   * to mention the selected wine color for a more personalized feel
   */
  adaptQuestionsToColor(color) {
    if (color === 'mix') color = 'both';
    const isEN = (document.documentElement.lang || '').toLowerCase().slice(0, 2) !== 'nl';
    const explanations = {};

    if (color === 'red') {
      explanations[2] = isEN
        ? '☕ This reveals your sensitivity to bitterness and tannins. Key elements in red wines.'
        : '☕ Dit onthult je gevoeligheid voor bitterheid en tannines. Sleutelelementen in rode wijnen.';
      explanations[3] = isEN
        ? '🍫 Your chocolate preference shows your taste for sweetness vs. structure in red wines.'
        : '🍫 Je chocoladevoorkeur onthult je smaak voor zoetheid vs. structuur in rode wijnen.';
      explanations[4] = isEN
        ? '🥗 This measures your acidity tolerance. Important for fresh, balanced reds.'
        : '🥗 Dit meet je zuurtolerantie. Belangrijk voor frisse, evenwichtige rode wijnen.';
      explanations[5] = isEN
        ? '🧀 Cheese maturity mirrors wine complexity. Aged cheese lovers often enjoy aged reds.'
        : '🧀 Kaasrijping weerspiegelt wijn complexiteit. Liefhebbers van oude kaas genieten vaak van gerijpte rode wijnen.';
      explanations[6] = isEN
        ? '🎯 Final calibration: this helps us find your perfect red wine style.'
        : '🎯 Laatste kalibratie: dit helpt ons je perfecte rode wijnstijl te vinden.';
    } else if (color === 'white') {
      explanations[2] = isEN
        ? '☕ This reveals your sensitivity to bitterness. Helping us match the right white wine style.'
        : '☕ Dit onthult je gevoeligheid voor bitterheid. Zo vinden we de juiste witte wijnstijl.';
      explanations[3] = isEN
        ? '🍫 Your chocolate preference reveals your taste for sweetness. Essential for your ideal white wine.'
        : '🍫 Je chocoladevoorkeur onthult je smaak voor zoetheid. Essentieel voor jouw ideale witte wijn.';
      explanations[4] = isEN
        ? '🥗 This measures your acidity tolerance. The heart of crisp, refreshing whites.'
        : '🥗 Dit meet je zuurtolerantie. Het hart van heldere, verfrissende witte wijnen.';
      explanations[5] = isEN
        ? '🧀 Cheese pairing reveals your complexity preference in white wines.'
        : '🧀 Kaasvoorkeur onthult je complexiteitsvoorkeur in witte wijnen.';
      explanations[6] = isEN
        ? '🎯 Final calibration: this helps us find your perfect white wine style.'
        : '🎯 Laatste kalibratie: dit helpt ons je perfecte witte wijnstijl te vinden.';
    }
    // 'both' = restore original neutral explanations
    if (color === 'both') {
      explanations[2] = isEN
        ? '☕ This reveals your sensitivity to bitterness and tannins. Key elements in red wines.'
        : '☕ Dit onthult je gevoeligheid voor bitterheid en tannines. Sleutelelementen in rode wijnen.';
      explanations[3] = isEN
        ? '🍫 Chocolate preference correlates with your taste for sweetness vs. complex, structured wines.'
        : '🍫 Chocoladevoorkeur correleert met je smaak voor zoetheid vs. complexe, gestructureerde wijnen.';
      explanations[4] = isEN
        ? '🥗 This measures your acidity tolerance. The backbone of fresh, crisp wines.'
        : '🥗 Dit meet je zuurtolerantie. De ruggengraat van frisse, heldere wijnen.';
      explanations[5] = isEN
        ? '🧀 Cheese maturity mirrors wine complexity. Aged cheese lovers often enjoy aged wines.'
        : '🧀 Kaasrijping weerspiegelt wijncomplexiteit. Liefhebbers van oude kaas genieten vaak van gerijpte wijnen.';
      explanations[6] = isEN
        ? '🎯 Final calibration: this helps us fine-tune your perfect wine style.'
        : '🎯 Laatste kalibratie: dit helpt ons je perfecte wijnstijl te bepalen.';
    }

    Object.entries(explanations).forEach(([qNum, text]) => {
      const qEl = document.querySelector(`.taste-profile-question[data-question="${qNum}"]`);
      if (qEl) {
        const expEl = qEl.querySelector('.taste-profile-explanation');
        if (expEl) expEl.textContent = text;
      }
    });

    // Q6 (mood) images follow the chosen color. White uses the white set;
    // red and both keep the red set (the Liquid default).
    // Expert answers name grapes and styles, which only make sense per colour.
    const subAttr = color === 'red' ? 'data-sub-red' : color === 'white' ? 'data-sub-white' : 'data-sub-both';
    this.popup.querySelectorAll('.tpq-expert .answer-sub[data-sub-both]').forEach(el => {
      const text = el.getAttribute(subAttr);
      if (text) el.textContent = text;
    });

    const moodAttr = color === 'white' ? 'data-img-white' : 'data-img-red';
    const q6 = document.querySelector('.taste-profile-question[data-question="6"]');
    if (q6) {
      q6.querySelectorAll('.answer-img[data-img-red]').forEach(img => {
        const url = img.getAttribute(moodAttr);
        if (url) img.src = url;
      });
    }
  }

  updateProgress() {
    const progress = (this.currentQuestion / this.totalQuestions) * 100;
    this.progressFill.style.width = progress + '%';
    this.progressText.textContent = this.currentQuestion;
    
    // Show/hide back button
    if (this.backBtn) {
      const canGoBack = this.currentQuestion > 1 || (this.currentQuestion === 1 && !!this.pathChooser);
      this.backBtn.style.display = canGoBack ? 'flex' : 'none';
    }
  }

  goBack() {
    if (this.isProcessing) {
      return;
    }

    if (this.currentQuestion === 1 && this.pathChooser) {
      this.showPathChooser();
      return;
    }

    if (this.currentQuestion > 1) {
      // Remove last answer
      const lastAnswer = this.answers.pop();
      if (lastAnswer) {
        if (lastAnswer.color) {
          // Q1: reset color preference and restore original explanations
          this.colorPreference = 'both';
          this.colorMixPreference = null;
          const mixAnswer = this.popup.querySelector('.taste-profile-answer--mix');
          const mixTrigger = mixAnswer ? mixAnswer.querySelector('[data-mix-trigger]') : null;
          const mixDropdown = mixAnswer ? mixAnswer.querySelector('[data-mix-dropdown]') : null;
          const mixTotal = mixAnswer ? mixAnswer.querySelector('[data-mix-total]') : null;
          if (mixTrigger) mixTrigger.textContent = mixDropdown?.dataset?.mixPlaceholder || 'Choose';
          if (mixDropdown) mixDropdown.classList.remove('is-open');
          if (mixAnswer) {
            mixAnswer.dataset.redCount = '0';
            mixAnswer.dataset.whiteCount = '0';
          }
          if (mixTotal) mixTotal.textContent = '0 / 3';
          this.adaptQuestionsToColor('both');
        } else {
          // Q2-Q6: subtract the coordinates
          this.targetX -= lastAnswer.x;
          this.targetY -= lastAnswer.y;
          this.targetZ -= lastAnswer.z || 0;
        }
      }
      
      // Go to previous question
      this.currentQuestion--;
      this.updateProgress();
      this.showQuestion(this.currentQuestion);
      this.saveQuizState();
    }
  }

  saveQuizState() {
    const state = {
      currentQuestion: this.currentQuestion,
      targetX: this.targetX,
      targetY: this.targetY,
      targetZ: this.targetZ,
      colorPreference: this.colorPreference,
      colorMixPreference: this.colorMixPreference,
      answers: this.answers,
      quizPath: this.quizPath
    };
    sessionStorage.setItem('vinpop_quiz_state', JSON.stringify(state));
  }

  // Texte visible d'un bouton reponse (titre + sous-titre), pour la trace dans la commande.
  getAnswerButtonLabel(button) {
    if (!button) return '';
    const clean = (text) => String(text || '').replace(/\s+/g, ' ').trim();
    const parts = [...button.querySelectorAll('*')]
      .filter(el => !el.children.length)
      .map(el => clean(el.textContent))
      .filter(Boolean);
    return (parts.length ? parts.join(' / ') : clean(button.textContent)).slice(0, 100);
  }

  // TRACE DU KIT DANS LA COMMANDE (2026-09-27). Les reponses ne sont sauvees par l'API que pour
  // un client connecte, et `/api/kits/create` n'existe pas cote API : sans ces proprietes, les
  // reponses d'un kit achete par le trafic pub sont perdues (cas de la commande #1034).
  // Prefixe `_` : invisible dans le panier et le checkout, mais stocke sur la commande et lu
  // par la base du dashboard (`public.orders.line_items[].properties`).
  // `_Kit algo` dit quelles regles de selection ont produit le kit : voir la section
  // « Versions de l'algorithme du kit » de CHANGELOG.md pour les rejouer.
  buildKitTraceProperties() {
    const answers = [...(this.answers || [])]
      .sort((a, b) => Number(a.question) - Number(b.question))
      .map(answer => {
        const label = answer.label || `choix ${Number(answer.choice) + 1}`;
        if (answer.color) return `Q${answer.question}=${label}`;
        return `Q${answer.question}=${label} (${answer.x},${answer.y},${answer.z})`;
      });
    const fmt = (v) => (Number(v) || 0).toFixed(2);
    return {
      '_Quiz answers': answers.join(' | '),
      '_Quiz axes': `x=${fmt(this.targetX)} y=${fmt(this.targetY)} z=${fmt(this.targetZ)} couleur=${this.colorPreference || '?'} parcours=${this.quizPath || 'standard'}`,
      '_Kit algo': KIT_ALGO_VERSION
    };
  }

  getMetaContent(name) {
    const meta = document.querySelector(`meta[name="${name}"]`);
    return meta ? String(meta.getAttribute('content') || '').trim() : '';
  }

  buildVinpopAuthHeaders() {
    const customerId = this.getMetaContent('customer-id');
    const timestamp = this.getMetaContent('friends-auth-timestamp');
    const signature = this.getMetaContent('friends-auth-signature');
    if (!customerId || !timestamp || !signature) {
      throw new Error('vinpop_auth_unavailable');
    }

    return {
      'Content-Type': 'application/json',
      'X-Customer-Id': customerId,
      'X-Timestamp': timestamp,
      'X-Signature': signature
    };
  }

  buildQuizSavePayload(quizzType) {
    const fieldByQuestion = {
      1: 'color',
      2: 'coffee',
      3: 'chocolate',
      4: 'salad',
      5: 'cheese',
      6: 'mood'
    };
    const payload = { quizz_type: quizzType };

    this.answers.forEach(answer => {
      const field = answer.apiField || fieldByQuestion[Number(answer.question)];
      const value = Number.isFinite(Number(answer.apiChoice)) ? Number(answer.apiChoice) + 1 : Number(answer.choice) + 1;
      if (field && value >= 1 && value <= 3) {
        payload[field] = value;
      }
    });

    if (!payload.color) {
      const colorChoiceByPreference = {
        red: 1,
        white: 2,
        both: 3,
        mix: 3
      };
      const colorValue = colorChoiceByPreference[this.colorPreference];
      if (colorValue) {
        payload.color = colorValue;
      }
    }

    Object.keys(fieldByQuestion).forEach(question => {
      if (payload[fieldByQuestion[question]]) {
        return;
      }
      const answer = this.answers.find(item => Number(item.question) === Number(question));
      const value = answer ? Number(answer.choice) + 1 : NaN;
      if (value >= 1 && value <= 3) {
        payload[fieldByQuestion[question]] = value;
      }
    });

    return payload;
  }

  async saveQuizToApi() {
    const payload = this.buildQuizSavePayload('test-kit');
    console.log('TasteProfileQuiz: saving quiz answers', {
      quizz_type: payload.quizz_type,
      hasCompletePayload: ['color', 'coffee', 'chocolate', 'salad', 'cheese', 'mood'].every(field => payload[field] >= 1 && payload[field] <= 3),
      payloadFields: Object.keys(payload).filter(field => field !== 'quizz_type'),
      authAvailable: {
        customerId: !!this.getMetaContent('customer-id'),
        timestamp: !!this.getMetaContent('friends-auth-timestamp'),
        signature: !!this.getMetaContent('friends-auth-signature')
      }
    });
    const requiredFields = ['color', 'coffee', 'chocolate', 'salad', 'cheese', 'mood'];
    if (!requiredFields.every(field => payload[field] >= 1 && payload[field] <= 3)) {
      throw new Error('quiz_answers_incomplete');
    }

    const apiBase = (this.getMetaContent('api-url') || 'https://api.vinpop.fr').replace(/\/+$/, '');
    const response = await fetch(`${apiBase}/api/quizz/save`, {
      method: 'POST',
      headers: this.buildVinpopAuthHeaders(),
      credentials: 'omit',
      mode: 'cors',
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error('quiz_save_failed_' + response.status);
    }

    return response;
  }

  ensureQuizSavedToApi() {
    const payload = this.buildQuizSavePayload('test-kit');
    const signature = JSON.stringify(payload);
    if (this.quizSavePromise && this.lastQuizSaveSignature === signature) {
      return this.quizSavePromise;
    }

    this.lastQuizSaveSignature = signature;
    this.quizSavePromise = this.saveQuizToApi();
    return this.quizSavePromise;
  }

  saveCompletedQuizToApi(source) {
    const payload = this.buildQuizSavePayload('test-kit');
    const requiredFields = ['color', 'coffee', 'chocolate', 'salad', 'cheese', 'mood'];
    const missingFields = requiredFields.filter(field => !(payload[field] >= 1 && payload[field] <= 3));

    if (missingFields.length) {
      console.warn('TasteProfileQuiz: not saving incomplete quiz payload', {
        source,
        answersCount: this.answers.length,
        expectedAnswers: this.totalQuestions,
        payloadFields: Object.keys(payload).filter(field => field !== 'quizz_type'),
        missingFields
      });
      return Promise.reject(new Error('quiz_answers_incomplete'));
    }

    console.log('TasteProfileQuiz: completed quiz, queueing API save', {
      source,
      answers: this.answers.map(answer => ({
        question: answer.question,
        apiField: answer.apiField,
        choice: Number(answer.choice) + 1
      }))
    });

    return this.ensureQuizSavedToApi();
  }

  /**
   * KIT SNAPSHOT — everything the printed recap sheet and the guest rating page need,
   * frozen at the moment the customer adds the kit to the cart. See `KIT_RECAP_REFERRAL.md`.
   *
   * Why this exists even though the API already stores quizzes and Shopify stores orders:
   *
   * 1. **The quiz is only saved for logged-in visitors.** `saveQuizToApi()` goes through
   *    `buildVinpopAuthHeaders()`, which throws `vinpop_auth_unavailable` when the
   *    `customer-id` / `friends-auth-*` meta tags are absent — and `layout/theme.liquid`
   *    only prints those inside `{%- if customer -%}`. Both call sites swallow that error
   *    with a `console.warn`. On paid Meta traffic, where most buyers check out without an
   *    account, `/api/quizz/get` therefore has nothing for the order that just came in.
   * 2. **The order only stores wine titles**, not ids (`Quiz Match #n` = `product.title`),
   *    so rebuilding the four fiches from the order means fuzzy title matching.
   * 3. **The fingerprint axes are not reproducible.** `_barWineX/Y/Z` are min-max
   *    normalised against the candidate set of one quiz run; recomputing them later draws
   *    a different triangle than the customer saw.
   *
   * The DOM is the source for role label and axes rather than `_packWines` alone, because
   * `.wr-bundle-item` is literally what the customer looked at and what `openProductModal`
   * reads — the sheet and the popup then cannot disagree.
   */
  buildKitSnapshotPayload(properties) {
    const isEN = (document.documentElement.lang || '').toLowerCase().slice(0, 2) !== 'nl';
    const catalog = window.shopCatalog || {};
    const cardById = {};
    document.querySelectorAll('.wr-bundle-item[data-wine-id]').forEach(el => {
      cardById[String(el.dataset.wineId)] = el;
    });

    // Same mapping `openProductModal()` uses for the "your taste" triangle. Note it is a
    // different normalisation from the wine axes (raw target clamped to ±1.2, not min-max);
    // that asymmetry is pre-existing and is reproduced here on purpose so the printed chart
    // matches the on-screen one exactly.
    const targetToPct = (v) => Math.round(((Math.max(-1.2, Math.min(1.2, Number(v) || 0)) + 1.2) / 2.4) * 100);

    const wines = (this._packWines || []).map((wine, index) => {
      const id = String(wine && wine.wine_id ? wine.wine_id : '');
      const product = catalog[id] || {};
      const card = cardById[id];
      // null rather than 0 when an axis was never measured: `wine-fiche.js` omits the whole
      // chart on null, but would draw a fully collapsed triangle on 0, which reads as "this
      // wine scores zero on everything". `Number(null)` is 0, hence the explicit guard.
      const axisFrom = (key, fallback) => {
        const fromCard = card ? parseInt(card.dataset[key], 10) : NaN;
        if (Number.isFinite(fromCard)) return fromCard;
        if (fallback === null || fallback === undefined || fallback === '') return null;
        return Number.isFinite(Number(fallback)) ? Math.round(Number(fallback)) : null;
      };

      return {
        wine_id: id,
        title: product.title || '',
        handle: product.handle || '',
        image: (product.image && product.image.url) || '',
        role_label: (card && card.dataset.typeLabel) || '',
        why: (card && card.dataset.molecularText) || '',
        price_cents: card ? parseInt(card.dataset.displayPrice || '0', 10) : null,
        is_gift: Boolean(wine && wine._isBonus),
        position: index + 1,
        axes: {
          wx: axisFrom('axisX', wine && wine._barWineX),
          wy: axisFrom('axisY', wine && wine._barWineY),
          wz: axisFrom('axisZ', wine && wine._barWineZ)
        }
      };
    }).filter(entry => entry.wine_id);

    // The gifted fourth bottle is a line item property, not a `_packWines` entry, so it is
    // appended here or the sheet would print three fiches for a four-bottle box.
    const giftId = String(this._fastBonusProductId || '');
    if (giftId && !wines.some(entry => entry.wine_id === giftId)) {
      const giftProduct = catalog[giftId] || {};
      wines.push({
        wine_id: giftId,
        title: giftProduct.title || '',
        handle: giftProduct.handle || '',
        image: (giftProduct.image && giftProduct.image.url) || '',
        role_label: isEN ? '🎁 Free bottle' : '🎁 Gratis fles',
        why: '',
        price_cents: null,
        is_gift: true,
        position: wines.length + 1,
        axes: { wx: null, wy: null, wz: null }
      });
    }

    return {
      locale: isEN ? 'en' : 'nl',
      quizz_type: 'test-kit',
      // Same 1-3 encoding `/api/quizz/save` uses, so a snapshot and an API-stored quiz can
      // be read by one renderer.
      quiz: this.buildQuizSavePayload('test-kit'),
      color_preference: this.colorPreference || null,
      taste: {
        ux: targetToPct(this.targetX),
        uy: targetToPct(this.targetY),
        uz: targetToPct(this.targetZ)
      },
      wines,
      // Echoed back so the warehouse sheet and the recap sheet can be reconciled if the
      // property names ever change.
      line_item_properties: properties || {},
      customer_id: this.getMetaContent('customer-id') || null,
      created_at: new Date().toISOString()
    };
  }

  /**
   * POSTs the snapshot and returns its short token, or null.
   *
   * Never throws and never blocks for long: this runs on the add-to-cart path of the
   * highest-intent button on the site, so an API outage must cost the recap sheet, not the
   * sale. A local copy is kept so a snapshot lost to a timeout can still be replayed.
   */
  async persistKitSnapshot(properties) {
    let payload;
    try {
      payload = this.buildKitSnapshotPayload(properties);
    } catch (error) {
      console.warn('TasteProfileQuiz: could not build kit snapshot', error);
      return null;
    }

    if (!payload.wines.length) return null;

    try {
      localStorage.setItem('vinpop_kit_snapshot', JSON.stringify(payload));
    } catch (error) {
      // Private mode or a full quota. The POST below is what matters.
    }

    const apiBase = (this.getMetaContent('api-url') || 'https://api.vinpop.fr').replace(/\/+$/, '');
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 4000);

    try {
      const response = await fetch(`${apiBase}/api/kits/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'omit',
        mode: 'cors',
        signal: controller.signal,
        body: JSON.stringify(payload)
      });
      if (!response.ok) throw new Error('kit_snapshot_failed_' + response.status);

      const data = await response.json();
      const token = String((data && (data.token || data.kit_token)) || '').trim();
      if (!token) throw new Error('kit_snapshot_no_token');

      try {
        localStorage.setItem('vinpop_kit_token', token);
      } catch (error) { /* see above */ }

      return token;
    } catch (error) {
      console.warn('TasteProfileQuiz: kit snapshot not stored, order will ship without a recap sheet', error);
      return null;
    } finally {
      clearTimeout(timeout);
    }
  }

  restoreQuizState(state) {
    this.currentQuestion = state.currentQuestion;
    this.targetX = state.targetX;
    this.targetY = state.targetY;
    this.targetZ = state.targetZ;
    this.colorPreference = state.colorPreference;
    this.colorMixPreference = state.colorMixPreference || null;
    this.answers = state.answers;
    this.setQuizPath(state.quizPath);

    // Open popup and show the correct question
    const isInline = this.popup.classList.contains('taste-profile-popup--inline');
    this.popup.style.display = 'block';
    if (!isInline) {
      document.body.style.overflow = 'hidden';
    }

    // Hide loading screen
    if (this.loadingEl) this.loadingEl.style.display = 'none';

    // Show progress bar and update it
    const progressEl = this.popup.querySelector('.taste-profile-progress');
    if (progressEl) progressEl.style.display = '';
    this.updateProgress();
    // Page-load restore: render the resumed question but leave the scroll position
    // alone, so the visitor still lands on the hero. This is the root cause the
    // hero's pin-to-top guard was compensating for.
    this.showQuestion(this.currentQuestion, { skipScroll: true });

    // Restore adapted explanations if color was already answered
    if (this.colorPreference !== 'both') {
      this.adaptQuestionsToColor(this.colorPreference);
    }

    // Re-highlight previously selected answer for each answered question
    this.answers.forEach(a => {
      const qEl = this.popup.querySelector(`.taste-profile-question[data-question="${a.question}"]`);
      if (qEl) {
        const scope = (this.quizPath === 'expert' && qEl.querySelector('.tpq-expert')) || qEl;
        const btns = scope.querySelectorAll('.taste-profile-answer');
        if (btns[a.choice]) btns[a.choice].classList.add('selected');
      }
    });
  }

  markQuizCompleted() {
    // Store completion in localStorage (permanent) and sessionStorage (current session)
    localStorage.setItem('vinpop-quiz-completed', 'true');
    sessionStorage.setItem('vinpop-quiz-completed', 'true');
    console.log('✅ Quiz marked as completed');
  }

  isQuizCompleted() {
    // Check if quiz has been completed (in either storage)
    return localStorage.getItem('vinpop-quiz-completed') === 'true' || 
           sessionStorage.getItem('vinpop-quiz-completed') === 'true';
  }

  async fetchResults(options = {}) {
    const silentUpdate = !!options.silentUpdate;
    const preserveScrollOpt = !!options.preserveScroll;
    const axisUpdateRequestId = silentUpdate ? ++this.axisUpdateRequestId : 0;
    // Show loading state
    if (!silentUpdate) {
      this.popup.querySelectorAll('.taste-profile-question').forEach(q => q.style.display = 'none');
      // Calibration: skip the old spinner/DNA screens — show a dark analysis bridge that flows
      // straight into the reveal's 3D map intro.
      if (/\/collections\/(?:test-kit|tasting-kit)(?:\/|$)/.test(window.location.pathname)) {
        const _en = (document.documentElement.lang || '').toLowerCase().slice(0, 2) !== 'nl';
        this.loadingEl.classList.add('taste-profile-loading--calib');
        this.loadingEl.innerHTML = `<div class="wr-analysis-bridge"><span class="wr-analysis-bridge__pulse"></span><p>${_en ? 'Analysing your taste…' : 'Je smaak analyseren…'}</p></div>`;
      }
      this.loadingEl.style.display = 'block';
    }

    try {
      if (!options.skipQuizSave && !silentUpdate) {
        await this.saveCompletedQuizToApi('fetch_results').catch(error => {
          console.warn('TasteProfileQuiz: could not save quiz answers before results:', error);
        });
      }

      // Get API URL from meta tag or default
      const apiUrl = document.querySelector('meta[name="api-url"]')?.content || 'https://api.vinpop.fr';
      
      // Fetch all wines with coordinates
      const response = await fetch(`${apiUrl}/api/wines/getMapAll`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch wine data');
      }
      
      const wines = await response.json();
      
      // Exclude wines that are too extreme for the quiz's Overton window
      const excludedIds = ['15510626763139'];
      const eligibleWines = wines.filter(w => !excludedIds.includes(String(w.wine_id)));
      
      // Filter by color preference (Q6) BEFORE scoring
      // API field: wine.color = 'R' (red) or 'W' (white)
      let filteredWines = eligibleWines;
      if (this.colorPreference === 'red') {
        const redWines = eligibleWines.filter(w => (w.color || '').toUpperCase() === 'R');
        filteredWines = redWines;
        if (redWines.length < 3) console.warn('⚠️ Only', redWines.length, 'red wines found; keeping strict red filter');
      } else if (this.colorPreference === 'white') {
        const whiteWines = eligibleWines.filter(w => (w.color || '').toUpperCase() === 'W');
        filteredWines = whiteWines;
        if (whiteWines.length < 3) console.warn('⚠️ Only', whiteWines.length, 'white wines found; keeping strict white filter');
      }
      // 'both' and 'mix' = no hard filter; final ordering respects the requested mix.
      console.log(`🍷 Color filter: ${this.colorPreference} → ${filteredWines.length}/${eligibleWines.length} eligible wines`);

      // Filter by price: only wines under €14
      const MAX_PRICE_CENTS = 1400;
      const catalog = window.shopCatalog || {};
      const beforePriceCount = filteredWines.length;
      const priceFiltered = filteredWines.filter(w => {
        const p = catalog[String(w.wine_id)];
        return p && this.getPaidDisplayPrice(p) <= MAX_PRICE_CENTS;
      });
      filteredWines = priceFiltered.length >= 3 ? priceFiltered : filteredWines;
      console.log(`💰 Price filter (<€14): ${priceFiltered.length}/${beforePriceCount} wines`);

      // SÉCURITÉ: si 0 vins après tous les filtres, utiliser TOUS les vins bruts de l'API
      if (filteredWines.length === 0 && wines.length > 0) {
        const strictFallback = this.colorPreference === 'red'
          ? wines.filter(w => (w.color || '').toUpperCase() === 'R')
          : this.colorPreference === 'white'
            ? wines.filter(w => (w.color || '').toUpperCase() === 'W')
            : wines;
        console.warn('⚠️ 0 wines after all filters, using strict color fallback:', strictFallback.length);
        filteredWines = strictFallback;
      }

      if (filteredWines.length === 0) {
        throw new Error(`No wines available for color preference: ${this.colorPreference}`);
      }
      
      // Calculate scores
      const winesWithScores = this.calculateScores(filteredWines);

      // The cards only show the top matches, but the calibration pack must be
      // free to reach further down the list to hold the lab similarity band.
      this.calibrationSearchPool = winesWithScores;
      
      // Normalize map_intensity (Z axis) to same [-1,1] range as X/Y
      const rawZs = filteredWines.map(w => parseFloat(w.map_intensity) || 0);
      const zMin = Math.min.apply(null, rawZs);
      const zMax = Math.max.apply(null, rawZs);
      const zCenter = (zMin + zMax) / 2;
      const zHalfRange = (zMax - zMin) / 2 || 1;
      console.log('🗺️ Z normalization: raw range', zMin.toFixed(2), '→', zMax.toFixed(2), ', center:', zCenter.toFixed(2), ', halfRange:', zHalfRange.toFixed(2));

      // ── Calibrate user XYZ to wine data range ──
      // The quiz accumulates small deltas (±0.1 to ±0.4 per question, 5 questions)
      // Wine map_x/map_y come from the API with their own range
      // We must map the user's accumulated XYZ into the same coordinate space as the wines
      const rawXs = filteredWines.map(w => parseFloat(w.map_x) || 0);
      const rawYs = filteredWines.map(w => parseFloat(w.map_y) || 0);
      const xWineMin = Math.min.apply(null, rawXs), xWineMax = Math.max.apply(null, rawXs);
      const yWineMin = Math.min.apply(null, rawYs), yWineMax = Math.max.apply(null, rawYs);
      const xWineCenter = (xWineMin + xWineMax) / 2, xWineHalf = (xWineMax - xWineMin) / 2 || 1;
      const yWineCenter = (yWineMin + yWineMax) / 2, yWineHalf = (yWineMax - yWineMin) / 2 || 1;

      // Quiz theoretical range for each axis (sum of max possible deltas across 5 questions)
      // X: [-0.8, 0.8], Y: [-0.9, 1.0], Z: [-1.0, 1.1] approximately
      // We use a safe ±1.2 range for the quiz output
      const quizRange = 1.2;

      // Map user's quiz coordinates into wine data space
      const calibratedX = xWineCenter + (this.targetX / quizRange) * xWineHalf;
      const calibratedY = yWineCenter + (this.targetY / quizRange) * yWineHalf;
      // Z: user targetZ is already accumulated from quiz, map to normalized [-1,1] wine Z space
      const calibratedZ = this.targetZ / quizRange;

      console.log('🗺️ User calibration: raw(' + this.targetX.toFixed(2) + ',' + this.targetY.toFixed(2) + ',' + this.targetZ.toFixed(2) + ') → calibrated(' + calibratedX.toFixed(2) + ',' + calibratedY.toFixed(2) + ',' + calibratedZ.toFixed(2) + ')');
      console.log('🗺️ Wine X range: [' + xWineMin.toFixed(2) + ', ' + xWineMax.toFixed(2) + '], Y range: [' + yWineMin.toFixed(2) + ', ' + yWineMax.toFixed(2) + ']');

      // Store data for 3D taste map visualization
      sessionStorage.setItem('vinpop_user_xyz', JSON.stringify({
        x: calibratedX, y: calibratedY, z: calibratedZ
      }));
      const allWinesXYZ = filteredWines.map(w => {
        const rawZ = parseFloat(w.map_intensity) || 0;
        const normZ = (rawZ - zCenter) / zHalfRange;
        return {
          id: String(w.wine_id),
          x: parseFloat(w.map_x) || 0,
          y: parseFloat(w.map_y) || 0,
          z: normZ,
          color: (w.color || '').toUpperCase()
        };
      });
      sessionStorage.setItem('vinpop_all_wines_xyz', JSON.stringify(allWinesXYZ));
      
      // Debug: show Z distribution
      const zVals = allWinesXYZ.map(w => w.z);
      console.log('🗺️ Stored', allWinesXYZ.length, 'wines for 3D viz. Z range:', Math.min.apply(null, zVals).toFixed(2), '→', Math.max.apply(null, zVals).toFixed(2));
      console.log('🗺️ Sample wines:', allWinesXYZ.slice(0, 3).map(w => '(' + w.x.toFixed(2) + ',' + w.y.toFixed(2) + ',' + w.z.toFixed(2) + ')').join(' '));
      
      // Max products: 8 for single color, buffer of 10+10 for both (manager does final 8+8 from Shopify-matched)
      let topWines;
      const desiredMix = this.getDesiredColorMix();
      if (desiredMix && this.colorPreference === 'mix') {
        const sorted = winesWithScores.sort((a, b) => b.score - a.score);
        const reds = sorted.filter(w => (w.color || '').toUpperCase() === 'R');
        const whites = sorted.filter(w => (w.color || '').toUpperCase() === 'W');
        const mixed = [];
        for (let i = 0; i < Math.max(desiredMix.red, desiredMix.white, 1); i++) {
          if (i < desiredMix.red && reds[i]) mixed.push(reds[i]);
          if (i < desiredMix.white && whites[i]) mixed.push(whites[i]);
        }
        const fallback = sorted.filter(w => !mixed.some(item => String(item.wine_id) === String(w.wine_id))).slice(0, 12);
        topWines = [...mixed, ...fallback].slice(0, 20);
        console.log(`🍷 Mixed color buffer: ${desiredMix.red} red + ${desiredMix.white} white preference, ${topWines.length} candidates`);
      } else if (this.colorPreference === 'both') {
        // Send larger buffer: top 10 red + top 10 white
        // Manager will pick final 8+8 from products that exist in Shopify catalog
        const sorted = winesWithScores.sort((a, b) => b.score - a.score);
        const topRed = sorted.filter(w => (w.color || '').toUpperCase() === 'R').slice(0, 10);
        const topWhite = sorted.filter(w => (w.color || '').toUpperCase() === 'W').slice(0, 10);
        // Combine and re-sort by score
        topWines = [...topRed, ...topWhite].sort((a, b) => b.score - a.score);
        console.log(`🍷 Balanced both (buffer): ${topRed.length} red + ${topWhite.length} white = ${topWines.length} candidates`);
      } else {
        topWines = winesWithScores
          .sort((a, b) => b.score - a.score)
          .slice(0, 10); // Buffer of 10, manager caps to 8
        console.log(`🍷 Showing top wines (color: ${this.colorPreference}), ${topWines.length} candidates`);
      }
      
      // Store wines for reveal
      this.recommendedWines = topWines;

      if (silentUpdate && axisUpdateRequestId !== this.axisUpdateRequestId) {
        return;
      }

      // DNA loading animation (rotating steps, ~3s auto-transition)
      const _isEN_dna = (document.documentElement.lang || '').toLowerCase().slice(0, 2) !== 'nl';
      const _dnaSteps = _isEN_dna ? [
        { title: 'Analysing your Wine DNA…', sub: 'Reading your answers against our lab-analyzed database.' },
        { title: 'Calculating taste similarity…', sub: 'No opinions, no scores. Just measurable matches.' },
        { title: 'Finalizing your matches…', sub: 'Selecting the wines closest to what you already love.' }
      ] : [
        { title: 'Analyse van je Wijn DNA…', sub: 'Je antwoorden worden vergeleken met onze laboratoriumdata.' },
        { title: 'Smaakgelijkenis berekenen…', sub: 'Geen meningen, geen scores. Enkel meetbare matches.' },
        { title: 'Jouw matches worden afgerond…', sub: 'We selecteren de wijnen die het dichtst bij jouw smaak liggen.' }
      ];
      const _isCalib_dna = /\/collections\/(?:test-kit|tasting-kit)(?:\/|$)/.test(window.location.pathname);
      let _dnaStepTimer = null;
      // Calibration: no orbs screen — the reveal's 3D map intro is the analysis. Other flows keep the DNA loader.
      if (!silentUpdate && this.loadingEl && !_isCalib_dna) {
        let _dnaStepIndex = 0;
        const _renderDnaStep = () => {
          const step = _dnaSteps[_dnaStepIndex];
          this.loadingEl.innerHTML = `
            <div class="wr-dna-loading">
              <div class="wr-dna-orbs">
                <span class="wr-dna-orb wr-dna-orb--1"></span>
                <span class="wr-dna-orb wr-dna-orb--2"></span>
                <span class="wr-dna-orb wr-dna-orb--3"></span>
              </div>
              <p class="wr-dna-loading__title">${step.title}</p>
              <p class="wr-dna-loading__sub">${step.sub}</p>
            </div>
          `;
        };
        _renderDnaStep();
        this.loadingEl.style.display = 'block';
        _dnaStepTimer = setInterval(() => {
          _dnaStepIndex = (_dnaStepIndex + 1) % _dnaSteps.length;
          _renderDnaStep();
        }, 1000);
      }
      if (!silentUpdate && !_isCalib_dna) {
        await new Promise(resolve => setTimeout(resolve, 3000));
      }
      if (_dnaStepTimer) clearInterval(_dnaStepTimer);
      this.markQuizCompleted();
      if (!silentUpdate && this.loadingEl) this.loadingEl.style.display = 'none';
      const _progressEl_dna = this.popup.querySelector('.taste-profile-progress');
      if (!silentUpdate && _progressEl_dna) _progressEl_dna.style.display = 'none';
      if (/\/collections\/(?:test-kit|tasting-kit)(?:\/|$)/.test(window.location.pathname)) {
        if (silentUpdate && typeof this.updateBundleItemsFromRecommendations === 'function') {
          await this.updateBundleItemsFromRecommendations(this.recommendedWines, { requestId: axisUpdateRequestId });
        } else {
          this.startWineRevealSequence(this.recommendedWines, { preserveScroll: silentUpdate || preserveScrollOpt });
        }
      } else {
        this.displayResults(this.recommendedWines);
      }
      
    } catch (error) {
      console.error('Error fetching wine recommendations:', error);
      if (!silentUpdate) this.showError();
    }
  }

  calculateScores(wines) {
    // Normalize map_intensity (Z axis) to same scale as X/Y for distance calc
    const rawZvals = wines.map(w => parseFloat(w.map_intensity) || 0);
    const zMinS = Math.min.apply(null, rawZvals);
    const zMaxS = Math.max.apply(null, rawZvals);
    const zCenterS = (zMinS + zMaxS) / 2;
    const zHalfS = (zMaxS - zMinS) / 2 || 1;

    // Calibrate user XYZ into wine data coordinate space
    const rawXvals = wines.map(w => parseFloat(w.map_x) || 0);
    const rawYvals = wines.map(w => parseFloat(w.map_y) || 0);
    const xMin = Math.min.apply(null, rawXvals), xMax = Math.max.apply(null, rawXvals);
    const yMin = Math.min.apply(null, rawYvals), yMax = Math.max.apply(null, rawYvals);
    const xCenter = (xMin + xMax) / 2, xHalf = (xMax - xMin) / 2 || 1;
    const yCenter = (yMin + yMax) / 2, yHalf = (yMax - yMin) / 2 || 1;
    const quizRange = 1.2;
    const calX = xCenter + (this.targetX / quizRange) * xHalf;
    const calY = yCenter + (this.targetY / quizRange) * yHalf;
    const calZ = this.targetZ / quizRange;

    // Step 1: Calculate all distances (using calibrated user coordinates)
    const withDistances = wines.map(wine => {
      const wineX = parseFloat(wine.map_x) || 0;
      const wineY = parseFloat(wine.map_y) || 0;
      const wineZ = ((parseFloat(wine.map_intensity) || 0) - zCenterS) / zHalfS;
      
      const distance = Math.sqrt(
        Math.pow(calX - wineX, 2) + 
        Math.pow(calY - wineY, 2) +
        Math.pow(calZ - wineZ, 2)
      );
      
      return { ...wine, distance };
    });
    
    // Step 2: Dynamic multiplier. Guarantee top 16 wines at 80%+
    // (buffer of 10+10 to account for wines missing from Shopify catalog)
    const GUARANTEE_COUNT = 16;
    const MIN_SCORE = 80;
    let multiplier = 15; // default
    
    const sorted = [...withDistances].sort((a, b) => a.distance - b.distance);
    if (sorted.length >= GUARANTEE_COUNT) {
      const distanceAtGuarantee = sorted[GUARANTEE_COUNT - 1].distance;
      if (distanceAtGuarantee > 0) {
        const maxMultiplier = (100 - MIN_SCORE) / distanceAtGuarantee;
        if (multiplier > maxMultiplier) {
          multiplier = Math.max(1, maxMultiplier); // never below 1
          console.log(`📊 Dynamic multiplier adjusted: ${multiplier.toFixed(2)} (6th wine distance: ${distanceAtGuarantee.toFixed(3)})`);
        }
      }
    } else if (sorted.length > 0) {
      // Fewer than 6 wines total. Adjust to last wine
      const distanceLast = sorted[sorted.length - 1].distance;
      if (distanceLast > 0) {
        const maxMultiplier = (100 - MIN_SCORE) / distanceLast;
        if (multiplier > maxMultiplier) {
          multiplier = Math.max(1, maxMultiplier);
        }
      }
    }
    
    console.log(`📊 Score multiplier: ${multiplier.toFixed(2)}`);

    // Pre-compute taste bar percentages for reveal cards
    const clampPct = (v) => Math.max(3, Math.min(97, v));
    const xRange = (xMax - xMin) || 1;
    const yRange = (yMax - yMin) || 1;
    const zRangeS = (zMaxS - zMinS) || 1;
    const userRawZ = calZ * zHalfS + zCenterS; // convert normalized Z back to raw intensity space
    const _barUserX = clampPct((calX - xMin) / xRange * 100);
    const _barUserY = clampPct((calY - yMin) / yRange * 100);
    const _barUserZ = clampPct((userRawZ - zMinS) / zRangeS * 100);
    console.log(`📊 Taste bars. User pcts: X=${_barUserX.toFixed(1)}% Y=${_barUserY.toFixed(1)}% Z=${_barUserZ.toFixed(1)}%`);
    
    // Step 3: Apply scores with dynamic multiplier + bar data
    return withDistances.map(wine => {
      let score = 100 - (wine.distance * multiplier);
      score = Math.max(0, Math.min(100, score));
      const wX = parseFloat(wine.map_x) || 0;
      const wY = parseFloat(wine.map_y) || 0;
      const wZ = parseFloat(wine.map_intensity) || 0;
      return {
        ...wine,
        score: Math.round(score),
        _barUserX, _barUserY, _barUserZ,
        _barWineX: clampPct((wX - xMin) / xRange * 100),
        _barWineY: clampPct((wY - yMin) / yRange * 100),
        _barWineZ: clampPct((wZ - zMinS) / zRangeS * 100),
      };
    });
  }

  /**
   * Build a human-readable taste profile from quiz answers
   * Uses X (sweetness/acidity), Y (body/structure), Z (intensity) axes
   * Used to generate justification text on product cards
   */
  buildTasteProfile() {
    const profile = {
      tanninTolerance: 'medium',    // Q1: coffee → Y+Z
      sweetnessPreference: 'balanced', // Q2: chocolate → X+Z
      acidityTolerance: 'medium',   // Q3: dressing → X+Z
      complexityPreference: 'medium', // Q4: cheese → Y+Z
      moodPreference: 'balanced',   // Q5: mood → X+Y+Z
      intensityPreference: 'medium', // Overall Z accumulation
      colorPreference: this.colorPreference || 'both', // Q6: wine color
      traits: [],
      choices: {} // Q1-Q6 answer index (0=A, 1=B, 2=C)
    };

    // Store raw answer choices for justification
    this.answers.forEach(a => {
      if (a.choice !== undefined) profile.choices[`q${a.question}`] = a.choice;
    });

    // Calculate overall intensity from Z accumulation
    const totalZ = this.targetZ;
    if (totalZ > 0.3) {
      profile.intensityPreference = 'high';
    } else if (totalZ < -0.3) {
      profile.intensityPreference = 'low';
    }

    this.answers.forEach(answer => {
      const z = answer.z || 0;
      switch (answer.question) {
        case 2: // Coffee → tannin/bitterness (Y=body, Z=intensity)
          if (answer.y > 0.1 && z > 0.1) {
            profile.tanninTolerance = 'high';
            profile.traits.push('bold tannins');
          } else if (answer.y < -0.1 && z <= 0) {
            profile.tanninTolerance = 'low';
            profile.traits.push('smooth & soft');
          } else if (z < -0.1) {
            profile.tanninTolerance = 'low';
            profile.traits.push('light & delicate');
          }
          break;
        case 3: // Chocolate → sweetness vs structure (X=sweetness, Z=intensity)
          if (answer.x > 0.2 && z < 0) {
            profile.sweetnessPreference = 'sweet';
            profile.traits.push('fruity & sweet');
          } else if (answer.y > 0.1 && z > 0.1) {
            profile.sweetnessPreference = 'dry';
            profile.traits.push('intense & structured');
          } else if (z < 0) {
            profile.sweetnessPreference = 'fruity';
            profile.traits.push('fruity flavors');
          }
          break;
        case 4: // Dressing → acidity (X=acidity, Z=intensity)
          if (answer.x < -0.2) {
            profile.acidityTolerance = 'high';
            profile.traits.push('crisp acidity');
          } else if (answer.x > 0.1) {
            profile.acidityTolerance = 'low';
            profile.traits.push('rich & round');
          } else {
            profile.traits.push('balanced acidity');
          }
          break;
        case 5: // Cheese → complexity (Y=structure, Z=intensity)
          if (answer.y > 0.2 && z > 0.2) {
            profile.complexityPreference = 'complex';
            profile.traits.push('complex & aged');
          } else if (answer.y < -0.2 && z < -0.2) {
            profile.complexityPreference = 'simple';
            profile.traits.push('fresh & easy');
          }
          break;
        case 6: // Mood → final calibration (X+Y+Z combined)
          if (answer.x < -0.1 && z < 0) {
            profile.moodPreference = 'fresh';
          } else if (answer.y > 0.1 && z > 0.2) {
            profile.moodPreference = 'bold';
          } else {
            profile.moodPreference = 'smooth';
          }
          break;
      }
    });

    if (this.axisTweakActive) {
      profile.traits = [];
      if (this.targetX < -0.2) {
        profile.acidityTolerance = 'high';
        profile.sweetnessPreference = 'fruity';
        profile.moodPreference = 'fresh';
        profile.traits.push('crisp acidity', 'fresh & bright');
      } else if (this.targetX > 0.2) {
        profile.acidityTolerance = 'low';
        profile.sweetnessPreference = 'sweet';
        profile.moodPreference = 'smooth';
        profile.traits.push('round & smooth', 'soft fruit');
      }
      if (this.targetY > 0.2) {
        profile.tanninTolerance = 'high';
        profile.complexityPreference = 'complex';
        profile.traits.push('structured', 'complex & aged');
      } else if (this.targetY < -0.2) {
        profile.tanninTolerance = 'low';
        profile.complexityPreference = 'simple';
        profile.traits.push('smooth & soft', 'fresh & easy');
      }
      if (this.targetZ > 0.3) {
        profile.intensityPreference = 'high';
        profile.moodPreference = 'bold';
        profile.traits.push('bold intensity');
      } else if (this.targetZ < -0.3) {
        profile.intensityPreference = 'low';
        profile.traits.push('light & delicate');
      }
    }

    // Deduplicate traits
    profile.traits = [...new Set(profile.traits)];
    return profile;
  }

  displayResults(wines) {
    // Store results in sessionStorage BEFORE closing popup
    sessionStorage.setItem('tasteProfileResults', JSON.stringify(wines));
    
    // ============ STORE TASTE PROFILE for justification text ============
    const profile = this.buildTasteProfile();
    sessionStorage.setItem('vinpop_taste_profile', JSON.stringify(profile));
    console.log('🍷 Taste profile saved:', profile);
    
    // Save results under the manager's key so checkAndRestoreResults() can render them on reload
    sessionStorage.setItem('vinpop_last_results', JSON.stringify(wines));
    
    // Mark quiz as completed
    this.markQuizCompleted();
    
    // Close the popup
    this.closePopup();
    
    // RELIABLE APPROACH: Reload the page with ?taste_profile=true
    // On reload, checkAndRestoreResults() in wine-recommendation-manager.js detects
    // vinpop_last_results in sessionStorage and renders the results cleanly.
    // This avoids all Alpine.js / Filterable / data-island interference issues
    // that occur when trying to mutate the DOM after popup close.
    console.log('🍷 Reloading page to display quiz results...');
    window.location.href = window.location.pathname + '?taste_profile=true';
  }

  /**
   * Wine-by-wine reveal sequence inside the popup (for starterpack)
   * Starter Pack: 2 quiz matches + 1 algorithmic discovery (+ optional bonus)
   */
  async startWineRevealSequence(wines, options = {}) {
    const preserveScroll = !!options.preserveScroll;
    const preservedWindowY = preserveScroll ? window.scrollY : null;
    const preservedPopupContent = this.popup ? this.popup.querySelector('.taste-profile-popup-content') : null;
    const preservedPopupY = preserveScroll && preservedPopupContent ? preservedPopupContent.scrollTop : null;
    const isEN = (document.documentElement.lang || '').toLowerCase().slice(0, 2) !== 'nl';
    const catalog = window.shopCatalog || {};
    const isCatalogProductInStock = (product) => {
      if (!product || product.available === false) return false;
      const variants = Array.isArray(product.variants) ? product.variants : [];
      if (!variants.length) return !!product.available;
      // `available` stays true for a tracked variant that is set to "continue
      // selling when out of stock" (backorder) even at 0 units. Treat a tracked
      // variant with 0 or fewer units on hand as out of stock so it is never
      // recommended, matching the merchant's real inventory. Untracked variants
      // (inventoryManaged === false) keep relying on `available`.
      return variants.some(v => {
        if (!v || v.available === false) return false;
        if (v.inventoryManaged && Number(v.inventoryQuantity) <= 0) return false;
        return true;
      });
    };
    const getPaidDisplayPrice = (product) => this.getPaidDisplayPrice(product);

    // Paid bottles always reach €32,00 on their own, and paid plus gift never passes €40,00.
    // Same band for every visitor: the old 2600 floor for paid-ad traffic is gone, since the
    // gift now ships to everyone and the floor is what makes the box read as a deal.
    const { min: MIN_PACK_PRICE, max: MAX_PACK_PRICE } = this.getPackPriceBand(catalog, isCatalogProductInStock);
    const calibrationSelection = await this.buildCalibrationBottleSelection(wines, {
      isEN,
      catalog,
      minPackPrice: MIN_PACK_PRICE,
      maxPackPrice: MAX_PACK_PRICE,
      maxPrice: MAX_PACK_PRICE,
      isCatalogProductInStock,
      getPaidDisplayPrice
    });
    const packWines = [...calibrationSelection.selectedWines];
    const calibrationVisualization = calibrationSelection.visualization;
    this._lastVisualization = calibrationVisualization;

    if (packWines.length < 2) {
      console.warn('⚠️ Not enough catalog matches for starter pack, falling back');
      this.displayResults(wines);
      return;
    }

    // Store results for the page
    sessionStorage.setItem('tasteProfileResults', JSON.stringify(wines));
    const profile = this.buildTasteProfile();
    sessionStorage.setItem('vinpop_taste_profile', JSON.stringify(profile));
    sessionStorage.setItem('vinpop_last_results', JSON.stringify(wines));

    // Hide progress bar
    const progressEl = this.popup.querySelector('.taste-profile-progress');
    if (progressEl) progressEl.style.display = 'none';

    // Hide only quiz question steps while showing the final wine list/bundle
    this.popup.querySelectorAll('.taste-profile-question').forEach(q => {
      q.style.display = 'none';
    });

    // Show wine reveal container
    const seqEl = this.popup.querySelector('.wine-reveal-sequence');
    const cardsEl = this.popup.querySelector('.wine-reveal-cards');
    if (!seqEl || !cardsEl) {
      this.displayResults(wines);
      return;
    }
    seqEl.style.display = 'block';
    cardsEl.innerHTML = '';

    // Store pack info for add-to-cart
    this._packWines = packWines;
    this._packReplacementCandidates = calibrationSelection.candidates || [];

    // Next best matches after the kit. `added` holds one entry per extra bottle, each
    // owning a private block of alternatives that Replace cycles through.
    this._upsell = {
      pool: this.buildUpsellCandidates(calibrationSelection.candidates, packWines, catalog),
      added: []
    };

    // i18n labels
    const labels = {
      match:    isEN ? 'match'           : 'match',
      region:   isEN ? 'Region'          : 'Regio',
      country:  isEN ? 'Country'         : 'Land',
      grape:    isEN ? 'Grape'           : 'Druif',
      color:    isEN ? 'Type'            : 'Type',
      body:     isEN ? 'Body'            : 'Body',
      food:     isEN ? 'Food pairing'    : 'Eten & drinken',
      vintage:  isEN ? 'Vintage'         : 'Jaar',
      next:     isEN ? 'Next wine →'     : 'Volgende wijn →',
      prev:     isEN ? '← Previous'      : '← Vorige',
      addCart:  isEN ? 'Add to cart'      : 'In winkelwagen',
      adding:   isEN ? 'Adding…'         : 'Toevoegen…',
      added:    isEN ? 'Added!'           : 'Toegevoegd!',
      seeSel:   isEN ? 'See your Test Kit' : 'Bekijk jouw Test Kit',
      shipping: isEN ? 'Shipping' : 'Verzending',
      free:     isEN ? 'FREE'            : 'GRATIS',
    };

    // Progress dots (wine pages + bundle)
    const totalDots = packWines.length + 1;
    const dotsHTML = `<div class="wr-dots">${Array.from({length: totalDots}, (_, i) =>
      `<span class="wr-dot${i === 0 ? ' wr-dot--active' : ''}" data-dot="${i}"></span>`
    ).join('')}</div>`;
    cardsEl.insertAdjacentHTML('beforeend', dotsHTML);

    // Helper: update dots state
    const updateDots = (activeIdx) => {
      cardsEl.querySelectorAll('.wr-dot').forEach(dot => {
        const di = parseInt(dot.dataset.dot);
        dot.classList.toggle('wr-dot--active', di === activeIdx);
        dot.classList.toggle('wr-dot--done', di < activeIdx);
      });
    };

    // Helper: navigate to a page
    const goToPage = (pageKey) => {
      cardsEl.querySelectorAll('.wr-page').forEach(p => p.style.display = 'none');
      const target = cardsEl.querySelector(`[data-page="${pageKey}"]`);
      if (target) {
        target.style.display = 'block';
        target.classList.add('wr-page--enter');
        const popupContent = this.popup.querySelector('.taste-profile-popup-content');
        if (popupContent && !this.popup.classList.contains('taste-profile-popup--inline')) {
          popupContent.scrollTop = 0;
        }
      }
      const dotIdx = pageKey === 'bundle' ? packWines.length : parseInt(pageKey);
      updateDots(dotIdx);
    };

    // Build wine page cards
    packWines.forEach((wine, idx) => {
      const p = catalog[String(wine.wine_id)];
      const imgUrl = p.image && p.image.url ? p.image.url : '';
      const prodUrl = p.url || `/products/${p.handle}`;
      const isLast = idx === packWines.length - 1;
      const isFirst = idx === 0;
      const nextLabel = isLast ? `🎁 ${labels.seeSel}` : labels.next;

      // Determine card type
      let rankLabel, tagHTML;
      if (wine._isBonus) {
        rankLabel = isEN ? '🎁 Bonus' : '🎁 Bonus';
        tagHTML = `<span class="wr-page__tag wr-page__tag--bonus">${isEN ? 'Bonus unlocked!' : 'Bonus ontgrendeld!'}</span>`;
      } else if (wine._calibrationRole) {
        rankLabel = `#${wine._calibrationIndex || idx + 1}`;
        tagHTML = `<span class="wr-page__tag">${wine.reason_title || (isEN ? 'Calibration pick' : 'Kalibratiekeuze')}</span>`;
      } else if (wine._isSimilar) {
        const similarToTitle = catalog[String(wine._similarTo)] ? catalog[String(wine._similarTo)].title : '';
        rankLabel = '🔗 Algorithmic Discovery';
        tagHTML = `<span class="wr-page__tag wr-page__tag--similar">${isEN
          ? `Because you love ${similarToTitle || 'your pick'}, our algorithm found ${wine.score}% similarity`
          : `Omdat jij van ${similarToTitle || 'jouw keuze'} houdt, vond ons algoritme ${wine.score}% gelijkenis`}</span>`;
      } else {
        rankLabel = `#${idx + 1}`;
        tagHTML = '';
      }

      // Detail rows
      let detailRows = '';
      const addRow = (icon, label, value) => {
        if (value) detailRows += `<div class="wr-page__detail"><span class="wr-page__detail-label">${icon} ${label}</span><span class="wr-page__detail-value">${value}</span></div>`;
      };
      addRow('🍇', labels.grape, p.grapeVariety);
      addRow('📍', labels.region, p.region);
      addRow('🌍', labels.country, p.country);
      addRow('🍷', labels.color, p.wineColor);
      addRow('📅', labels.vintage, p.vintage);
      addRow('💪', labels.body, p.body);

      // Food pairing
      let foodHTML = '';
      if (p.foodPairing) {
        const foods = p.foodPairing.split(',').map(f => f.trim()).filter(Boolean);
        if (foods.length) {
          foodHTML = `<div class="wr-page__food"><span class="wr-page__detail-label">🍽️ ${labels.food}</span><div class="wr-page__food-tags">${foods.map(f => `<span class="wr-page__food-tag">${f}</span>`).join('')}</div></div>`;
        }
      }

      const pageHTML = `
        <div class="wr-page" data-page="${idx}" style="display: none;">
          <div class="wr-page__hero">
            <div class="wr-page__rank">${rankLabel}</div>
            <span class="wr-page__score-badge">⭐ ${wine.score}% ${labels.match}</span>
            <a href="${prodUrl}" class="wr-page__img-wrap">
              <img src="${imgUrl}" alt="${p.title}" class="wr-page__img" loading="lazy">
            </a>
          </div>
          ${tagHTML}
          <h3 class="wr-page__title"><a href="${prodUrl}">${p.title}</a></h3>
          ${p.vendor ? `<div class="wr-page__vendor">${p.vendor}</div>` : ''}
          <div class="wr-page__details">${detailRows}</div>
          ${foodHTML}
          <div class="wr-page__actions">
            <div class="wr-page__actions-nav">
              ${!isFirst ? `<button class="wr-page__prev push-btn" type="button" data-prev="${idx - 1}"><span class="push-btn__surface">${labels.prev}</span></button>` : ''}
              <button class="wr-page__next push-btn" type="button" data-next="${isLast ? 'bundle' : idx + 1}"><span class="push-btn__surface">${nextLabel}</span></button>
            </div>
          </div>
        </div>
      `;
      cardsEl.insertAdjacentHTML('beforeend', pageHTML);
    });

    // Build Starter Pack bundle page

    // Read taste profile from sessionStorage for molecular match text
    const savedProfile = (() => { try { return JSON.parse(sessionStorage.getItem('vinpop_taste_profile') || '{}'); } catch(e) { return {}; } })();
    const getMolecularText = (profile, wineIdx, isEN) => {
      const tannin = profile.tanninTolerance || 'medium';
      const acidity = profile.acidityTolerance || 'medium';
      const sweetness = profile.sweetnessPreference || 'balanced';
      const complexity = profile.complexityPreference || 'medium';
      const mood = profile.moodPreference || 'smooth';
      const en = [
        tannin === 'high'
          ? 'You drink it black. Same bold intensity as your espresso.'
          : tannin === 'low'
          ? 'You prefer cappuccino or tea. No harsh tannins here.'
          : 'Your bitterness threshold guided this pick.',
        acidity === 'high'
          ? 'You chose lemon vinaigrette. This wine has that same crisp bite.'
          : acidity === 'low'
          ? 'You went for creamy dressing. This wine is round, no sharp edges.'
          : 'You picked olive oil & salt. Clean and balanced.',
        sweetness === 'dry'
          ? 'You chose dark chocolate 70%+. Same dry, structured finish.'
          : sweetness === 'sweet'
          ? 'You prefer milk chocolate. This wine is soft and fruity.'
          : 'You picked fruit jellies. Same bright, fruity character.',
        complexity === 'complex'
          ? 'You chose aged Gouda. This wine has the same layered depth.'
          : complexity === 'simple'
          ? 'You chose Brie. Light, creamy, no rough edges.'
          : 'Your cheese choice points to a balanced complexity.',
        mood === 'bold'
          ? 'You asked for Bold & Powerful. This wine delivers exactly that.'
          : mood === 'fresh'
          ? 'You wanted Fresh & Fruity. This is exactly it.'
          : 'You picked Smooth & Round. That\'s precisely what this is.',
      ];
      const nl = [
        tannin === 'high'
          ? 'Jij drinkt het puur zwart. Dezelfde krachtige intensiteit als jouw espresso.'
          : tannin === 'low'
          ? 'Jij prefereert cappuccino of thee. Geen zware tannines hier.'
          : 'Jouw bitterheidsdrempel stuurde deze keuze.',
        acidity === 'high'
          ? 'Jij koos citroendressing. Deze wijn heeft dezelfde frisse bite.'
          : acidity === 'low'
          ? 'Jij ging voor romige dressing. Ronde wijn, geen scherpe kanten.'
          : 'Jij koos olijfolie & zout. Clean en uitgebalanceerd.',
        sweetness === 'dry'
          ? 'Jij koos pure chocolade 70%+. Dezelfde droge, gestructureerde afdronk.'
          : sweetness === 'sweet'
          ? 'Jij houdt van melkchocolade. Deze wijn is zacht en fruitig.'
          : 'Jij koos fruitgummies. Hetzelfde levendige, fruitige karakter.',
        complexity === 'complex'
          ? 'Jij koos oude Gouda. Deze wijn heeft dezelfde gelaagde diepte.'
          : complexity === 'simple'
          ? 'Jij koos Brie. Licht, romig, geen ruwe randjes.'
          : 'Jouw kaaskeuze wijst op een uitgebalanceerde complexiteit.',
        mood === 'bold'
          ? 'Jij vroeg om Krachtig & Koel. Deze wijn levert dat precies.'
          : mood === 'fresh'
          ? 'Jij wilde Fris & Fruitig. Dit is precies dat.'
          : 'Jij koos Zacht & Rond. Dat is precies wat dit is.',
      ];
      const pool = isEN ? en : nl;
      return pool[wineIdx % pool.length];
    };

    const bundleSummaryHTML = this.buildBundleItemCardsHTML(packWines, isEN);

    // Calculate total real retail value of the selected wines for price anchoring.
    // 2026-08-03 : RAPPELÉ, mais pour une seule chose, la ligne "tu économises au total" du
    // bloc prix. Le port y est nommé à part, jamais fondu dans le prix barré ni dans le badge,
    // qui restent sur le vin seul. Un prix de référence qui mélange un produit et un service
    // est le maillon faible qu'un contrôle ACM attaque en premier, et il gonflait tout seul :
    // le port passe de 15,00 € à 17,00 € à cinq bouteilles, donc ajouter une bouteille
    // fabriquait 2,00 € d'"économie" qui était du fret, pas du vin. Séparer les deux montants
    // garde le chiffre total (le seul argument que le client additionne) sans cette ancre.
    const getConfiguredShippingCents = (bottleCount) => {
      const fallbackCents = 1500;
      const config = window.VinpopShippingConfig;
      if (!config || !config.zones) return fallbackCents;

      const customerData = window.VinpopCustomerData || {};
      const countryCode = customerData.countryCode;
      const countryName = customerData.country;
      let zone = null;

      Object.values(config.zones).some(candidate => {
        const countries = candidate.countries || [];
        if (countries.includes(countryCode) || countries.includes(countryName)) {
          zone = candidate;
          return true;
        }
        return false;
      });

      if (!zone && config.defaultZone && config.zones[config.defaultZone]) {
        zone = config.zones[config.defaultZone];
      }
      if (!zone || !Array.isArray(zone.rates)) return fallbackCents;

      const rate = zone.rates.find(item => bottleCount >= item.minBottles && bottleCount <= item.maxBottles) || zone.rates[zone.rates.length - 1];
      const price = rate && typeof rate.price === 'number' ? rate.price : null;
      return Number.isFinite(price) ? Math.round(price * 100) : fallbackCents;
    };

    const packRetailCents = packWines.reduce(
      (sum, wine) => sum + getPaidDisplayPrice(catalog[String(wine.wine_id)]),
      0
    );
    const priceBand = this.getPackPriceBand(catalog, isCatalogProductInStock);

    if (this.isOfferDebugEnabled()) {
      const euros = (cents) => `€${(Math.round(cents) / 100).toFixed(2).replace('.', ',')}`;
      console.log(
        `%c🍷 OFFRE  3 vins ${euros(packRetailCents)}` +
        `  ${packRetailCents <= priceBand.max ? '✅ dans la bande' : '⚠️ HORS BANDE'} (${euros(priceBand.min)} à ${euros(priceBand.max)})`,
        'font-weight:bold'
      );
      console.table(packWines.map((wine, i) => ({
          role: wine._calibrationRole || (wine._isBonus ? 'calibration_bonus' : wine._isSimilar ? 'similar' : `bottle_${i + 1}`),
          bouteille: catalog[String(wine.wine_id)]?.title || String(wine.wine_id),
          prix: euros(getPaidDisplayPrice(catalog[String(wine.wine_id)])),
          paye: 'oui'
        })));
      console.log(
        `   bande du pack payant : ${euros(priceBand.min)} a ${euros(priceBand.max)}\n` +
        `   remise affichee : -${this.discountPercent(packRetailCents, 2990)}%  (sur le vin seul, hors livraison)`
      );
    }

    const totalCents = packRetailCents;
    const baseBottleCount = packWines.length;
    const displayBottleCount = baseBottleCount;
    // The header sells the MECHANISM, not the box. "Taste 4 wines. Buy the box that matches
    // your taste." described the transaction and stopped there, so the box read as a one-off
    // purchase competing on price with a supermarket shelf.
    //
    // These bottles are step one of a loop: the customer rates them, and every later box is
    // built from those ratings. That is the only thing here a supermarket cannot sell him, so
    // it belongs in the biggest text on the page. The kicker names the step, the title names
    // what he stops doing (buying blind), and nothing else: a subtitle spelling out the rating
    // words was tried here and removed, the header carries more weight without it.
    //
    // Count stays interpolated for the dormant optional fourth paid bottle.
    const packKicker = isEN
      ? 'Step 1 of your taste profile'
      : 'Stap 1 van je smaakprofiel';
    const packTitle = isEN
      ? `The last ${displayBottleCount} bottles you'll ever buy blind.`
      : `De laatste ${displayBottleCount} flessen die je blind koopt.`;
    const totalEuros = (totalCents / 100).toFixed(2).replace('.', ',');
    // Badge, struck price and per-bottle prices stay computed on WINE ONLY. Delivery is named
    // once, on its own line, as part of the total saving, and never folded into any of them.
    //
    // A struck-through figure that mixes a product price with a service is the weakest kind of
    // reference price, and it is exactly what the ACM looks at. Worse, it inflated itself with
    // our own logistics: shipping goes from €15,00 to €17,00 at five bottles, so adding a
    // bottle used to buy €2,00 of extra "savings" that were freight, not wine. Hence the split:
    // one reference price on the wine, one line that adds the delivery the customer really
    // does not pay, each with its own amount visible.
    const savedPercent = this.discountPercent(totalCents, 2990);
    // 2026-08-03 : the total saving comes back, as an amount. Stripping every figure out of
    // this block cost a conversion rate that was working: "-22%" and "gratis bezorgd" are two
    // soft claims the visitor has to add up himself, and he does not. About €8,86 off the wine
    // plus €15,00 of delivery is one number he cannot reach alone, and it is the largest
    // honest figure on the page.
    const shippingCents = getConfiguredShippingCents(displayBottleCount);
    const wineSavingCents = Math.max(0, totalCents - 2990);
    // Per-bottle prices are the hero of this block: they are the only figures in the customer's
    // own currency. He buys at about €7 in the supermarket, so €5,98 lands without him having
    // to trust any total.
    const normalPerBottleEuros = (Math.round(totalCents / Math.max(1, displayBottleCount)) / 100).toFixed(2).replace('.', ',');
    const paidPerBottleEuros = (Math.round(2990 / Math.max(1, displayBottleCount)) / 100).toFixed(2).replace('.', ',');
    const hasCalibrationBonus = packWines.some(wine => wine && wine._isBonus);
    // The "your box contains" list spells out one line per bottle, so it has to know when
    // the optional fourth paid bottle is in the pack. Read from the role the selection set,
    // not from a count, so the line can never appear against a three-bottle box.
    const hasFourthBottle = packWines.some(wine => wine && wine._calibrationRole === 'third_safe_match');

    // Kept so the per-bottle prices, the badge and the saving line can be recomputed when the
    // pack contents change: showing a 3-bottle price next to a 4-bottle count would misstate
    // both. The shipping reader is carried along because delivery is tiered by bottle count,
    // so an added upsell bottle can push the box from €15,00 to €17,00 of freight and the
    // saving line has to follow, or it understates itself the moment the customer adds one.
    this._bundleMath = {
      wineCents: totalCents,
      baseCount: displayBottleCount,
      getShippingCents: getConfiguredShippingCents
    };

    const checkoutCardHTML =
      `<div class="wr-bundle__checkout-card" style="background:#FDFBF7;border:1px solid #EAE6DF;padding:20px;border-radius:12px;margin:0;text-align:center;box-shadow:0 4px 12px rgba(0,0,0,0.02);">` +
      `<div style="margin:0 0 12px;padding:9px 11px;background:#fff;border:1px solid #EAE6DF;border-radius:10px;text-align:left;">` +
      // Real, verifiable Trustpilot review. Never invent a customer on a page whose whole
      // argument is that we do not sell opinions.
      `<div style="display:flex;align-items:center;gap:7px;margin-bottom:3px;"><span style="color:#00b67a;font-size:13px;letter-spacing:1px;">★★★★★</span><span style="font-size:12px;color:#6B7280;font-weight:700;">Trustpilot</span></div>` +
      `<div style="font-size:12px;color:#374151;line-height:1.4;font-style:italic;">${isEN ? '“The quiz worked amazing. Without any expectations, I have become really impressed.”' : '“De quiz werkte geweldig. Zonder verwachtingen ben ik echt onder de indruk geraakt.”'}</div>` +
      `<div style="font-size:11px;color:#9CA3AF;margin-top:2px;">${isEN ? 'Maria, Netherlands' : 'Maria, Nederland'}</div>` +
      `</div>` +
      `<div class="wr-bundle__pricing-storybrand" style="margin-bottom:15px;padding-bottom:14px;border-bottom:1px solid #EAE6DF;text-align:left;">` +
      `<div style="margin-bottom:12px;"><div style="font-size:11px;font-weight:700;color:#E07A5F;letter-spacing:0.05em;text-transform:uppercase;margin-bottom:2px;">${isEN ? 'Test Kit' : 'Test Kit'}</div><div style="font-size:15px;color:#1F2937;line-height:1.25;font-weight:800;">${isEN ? 'Your box contains' : 'Je box bevat'}</div></div>` +
      `<div style="display:grid;gap:7px;margin:0 0 12px;">` +
      `<div style="display:flex;gap:8px;align-items:flex-start;font-size:13px;color:#374151;line-height:1.35;"><span style="color:#E07A5F;font-weight:800;line-height:1.35;">✓</span><span>1 ${isEN ? 'safe match based on your answers' : 'veilige match op basis van je antwoorden'}</span></div>` +
      `<div style="display:flex;gap:8px;align-items:flex-start;font-size:13px;color:#374151;line-height:1.35;"><span style="color:#E07A5F;font-weight:800;line-height:1.35;">✓</span><span>1 ${isEN ? 'close match with a bit more variation' : 'dichte match met iets meer variatie'}</span></div>` +
      `<div style="display:flex;gap:8px;align-items:flex-start;font-size:13px;color:#374151;line-height:1.35;"><span style="color:#E07A5F;font-weight:800;line-height:1.35;">✓</span><span>1 ${isEN ? 'second safe match, also very close to your answers' : 'tweede veilige match, ook heel dicht bij je antwoorden'}</span></div>` +
      `<div style="display:${hasFourthBottle ? 'flex' : 'none'};gap:8px;align-items:flex-start;font-size:13px;color:#374151;line-height:1.35;"><span style="color:#E07A5F;font-weight:800;line-height:1.35;">✓</span><span>1 ${isEN ? 'fourth bottle, picked the same way, never outside your taste zone' : 'vierde fles, op dezelfde manier gekozen, nooit buiten je smaakzone'}</span></div>` +
      `<div style="display:${hasCalibrationBonus ? 'flex' : 'none'};gap:8px;align-items:flex-start;font-size:13px;color:#374151;line-height:1.35;"><span style="color:#E07A5F;font-weight:800;line-height:1.35;">✓</span><span>1 ${isEN ? 'extra calibration bottle to improve future recommendations' : 'extra kalibratiefles om toekomstige aanbevelingen te verbeteren'}</span></div>` +
      `<div class="wr-bundle__math-upsell" style="display:none;gap:8px;align-items:flex-start;font-size:13px;color:#374151;line-height:1.35;"><span style="color:#E07A5F;font-weight:800;line-height:1.35;">✓</span><span><span class="wr-bundle__math-upsell-count">1</span> <span class="wr-bundle__math-upsell-noun">${isEN ? 'extra bottle you picked, at 20% off' : 'extra fles die je koos, met 20% korting'}</span></span></div>` +
      `</div>` +
      // One block, two lines, five numbers. The previous version was a two-column grid with
      // nine figures for three actual facts, and it read as confusing. Everything that made
      // the customer do arithmetic is gone: the normal total, the duplicated bottle counts,
      // the separate savings line. What is left is the price per bottle he can compare to his
      // supermarket habit, and the one sentence that says what he pays for what.
      `<div style="background:linear-gradient(180deg,#FFFFFF 0%,#FDFBF7 100%);border:1px solid #EAE6DF;border-radius:10px;padding:13px 14px;margin-bottom:10px;">` +
      `<div style="display:flex;align-items:baseline;flex-wrap:wrap;gap:8px;">` +
      `<strong class="wr-bundle__math-paid-bottle" style="font-size:30px;line-height:1;font-weight:900;color:#1F2937;letter-spacing:-0.02em;">€${paidPerBottleEuros}</strong>` +
      `<span style="font-size:12px;color:#4B5563;">${isEN ? 'per bottle' : 'per fles'}</span>` +
      `<s class="wr-bundle__math-normal-bottle" style="font-size:13px;color:#9CA3AF;font-weight:700;">€${normalPerBottleEuros}</s>` +
      `<span class="wr-bundle__math-percent" style="display:${savedPercent > 0 ? 'inline-block' : 'none'};background:#E07A5F;color:#fff;font-size:11px;font-weight:800;line-height:1;padding:4px 7px;border-radius:999px;white-space:nowrap;">-${savedPercent}%</span>` +
      `</div>` +
      `<div style="font-size:12px;color:#4B5563;line-height:1.4;margin-top:5px;">` +
      `<span class="wr-bundle__math-total-count">${displayBottleCount}</span> ${isEN ? 'bottles for' : 'flessen voor'} <strong class="wr-bundle__math-total-price" style="color:#1F2937;font-weight:700;">€29,90</strong>, ${isEN ? 'delivered free on your first order' : 'gratis bezorgd bij je eerste bestelling'}` +
      `</div>` +
      // The total saving, and the only place delivery is ever given an amount. It is written
      // as a sum in plain sight, wine + delivery, for two reasons. The customer trusts a
      // number he can verify far more than one he is handed, and the breakdown is what keeps
      // the freight out of the €38,76 struck price above: the two amounts stay separate
      // things that happen to be added, not one inflated reference price.
      //
      // The delivery figure is only safe here because it is the second term of a sum. Printed
      // alone as "otherwise €17,00" it read as a delivery costing more than half the order,
      // which is what got it removed. Inside the total it does the opposite job.
      `<div class="wr-bundle__math-saving" style="display:${wineSavingCents + shippingCents > 0 ? 'block' : 'none'};font-size:12.5px;color:#8C5A4A;line-height:1.4;margin-top:6px;font-weight:700;">` +
      `${isEN ? 'You save' : 'Je bespaart'} <strong class="wr-bundle__math-saving-total" style="font-weight:800;">€${this.formatEurosFromCents(wineSavingCents + shippingCents)}</strong> ${isEN ? 'in total' : 'in totaal'}` +
      `<span style="display:block;font-weight:600;color:#6B7280;font-size:11.5px;margin-top:1px;">` +
      `<span class="wr-bundle__math-saving-wine">€${this.formatEurosFromCents(wineSavingCents)}</span> ${isEN ? 'on the wine' : 'op de wijn'} + <span class="wr-bundle__math-saving-shipping">€${this.formatEurosFromCents(shippingCents)}</span> ${isEN ? 'delivery, on us (first order)' : 'verzendkosten, van ons (eerste bestelling)'}` +
      `</span>` +
      `</div>` +
      // No gift strip here. The free bottle was tried as a highlighted line in this block and
      // removed: the contents list above already states it, and repeating it next to the price
      // made the block argue instead of just stating the number.
      //
      // What follows is the only line here that sells something other than the price, and it
      // closes the villain block the visitor read before the quiz ("critic scores, gold medals,
      // wine jargon" against "your palate does not need their approval"). Buying is the moment
      // that argument turns into a side he picks, so it belongs at the price, not earlier.
      //
      // Deliberately a hairline, not a filled callout: a boxed strip was tried here for the
      // gift and read as a second advertisement inside the price. No membership count either,
      // since there is no real figure in the codebase to cite and inventing one would be the
      // exact kind of unearned authority this sentence attacks.
      `<div style="margin-top:9px;padding-top:8px;border-top:1px solid #F0EBE3;font-size:11.5px;line-height:1.4;color:#8C5A4A;font-weight:600;">` +
      `🍷 ${isEN ? 'Welcome to the rebellion: a growing community that trusts its own palate over critic scores.' : 'Welkom bij de rebellie: een groeiende community die haar eigen smaak meer vertrouwt dan punten van critici.'}` +
      `</div>` +
      `</div>` +
      `</div>` +
      `<div class="relative z-10 pb-button-shadow" style="width:100%;margin:0 auto 0.6rem;">` +
      `<button class="wr-bundle__add push-btn" type="button" style="width:100%;display:block !important;">` +
      `<span class="push-btn__surface push-btn__surface--cta-primary" style="display:block;width:100%;font-weight:bold;">${isEN ? 'Get my Taste Kit - €29.90' : 'Ontvang mijn Taste Kit - €29,90'}</span>` +
      `</button>` +
      `</div>` +
      `<span style="font-size:12px;color:#374151;display:block;margin-top:6px;line-height:1.4;text-align:center;font-weight:600;">${isEN ? '✅ Not your taste? Every wine we picked that you don\'t like is replaced for free in your next order.' : '✅ Niet jouw smaak? Elke wijn die wij kozen en die je niet lekker vindt, vervangen we gratis in je volgende bestelling.'}</span>` +
      `<span style="font-size:12px;color:#4B5563;display:block;margin-top:8px;line-height:1.45;text-align:center;">${isEN ? '🔒 One-time purchase. No subscription.' : '🔒 Eenmalige aankoop. Geen abonnement.'}</span>` +
      // "Free" is deliberately NOT repeated here: the math block above already makes that
      // claim with its real value. This line answers a different question, how fast and from
      // where, so the two never say the same thing twice.
      `<span class="wr-checkout-delivery" style="font-size:12px;color:#4B5563;display:block;margin-top:6px;line-height:1.45;text-align:center;">${isEN ? '🚚 Delivered within 72h from our Enschede warehouse via Bulpe Fulfilment' : '🚚 Binnen 72u bezorgd vanuit ons warehouse in Enschede via Bulpe Fulfilment'}</span>` +
      `<span class="wr-checkout-security" aria-label="${isEN ? 'Secure checkout with iDEAL, credit card and Apple Pay' : 'Veilig afrekenen met iDEAL, creditcard en Apple Pay'}" style="display:flex;align-items:center;justify-content:center;gap:8px;flex-wrap:wrap;margin-top:8px;line-height:1.2;text-align:center;">` +
      `<span style="font-size:12px;color:#4B5563;margin-right:1px;">🔒</span>` +
      `<span class="wr-checkout-payment-icons" aria-hidden="true" style="display:inline-flex;align-items:center;justify-content:center;gap:7px;flex-wrap:wrap;">` +
      `<span style="display:inline-flex;align-items:center;justify-content:center;min-width:44px;height:24px;padding:0 8px;border:1px solid #D9DDE5;border-radius:5px;background:#fff;color:#111827;font-size:12px;font-weight:800;letter-spacing:-0.01em;">iDEAL</span>` +
      `<span style="display:inline-flex;align-items:center;justify-content:center;min-width:56px;height:24px;padding:0 8px;border:1px solid #D9DDE5;border-radius:5px;background:#fff;color:#1F2937;font-size:10px;font-weight:800;letter-spacing:0.03em;">VISA&nbsp;/&nbsp;MC</span>` +
      `<span style="display:inline-flex;align-items:center;justify-content:center;min-width:64px;height:24px;padding:0 9px;border:1px solid #111827;border-radius:5px;background:#111827;color:#fff;font-size:11px;font-weight:700;letter-spacing:0;"> Pay</span>` +
      `</span>` +
      `</span>` +
      `</div>`;

    // The free bonus bottle is already added to the cart when the customer clicks the main
    // CTA (see addBtn.onclick / FAST_BONUS_VARIANT_ID below), so there is no separate
    // "enter your email to secure it" card competing with the buy button.
    const urgencyCardHTML = '';

    this._fastBonusProductId = '';

    // No email capture under the buy button: it asked for a second decision right where the
    // customer is making the first one, and the gift is already secured by the CTA itself.
    const emailCaptureHTML = '';

    const TESTIMONIAL_VIDEO_NL_MOBILE = 'https://cdn.shopify.com/videos/c/o/v/c054d21e56b94c3aa6b3469db8ca4bd2.mp4';
    const TESTIMONIAL_VIDEO_NL_DESKTOP = 'https://cdn.shopify.com/videos/c/o/v/9c7a2d255c74449a914e56966306f8a6.mp4';
    const TESTIMONIAL_VIDEO_EN_MOBILE = 'https://cdn.shopify.com/videos/c/o/v/59f1cea513bf4ed7b7dc6bf517a101ee.mp4';
    const TESTIMONIAL_VIDEO_EN_DESKTOP = 'https://cdn.shopify.com/videos/c/o/v/cf751c31ad014100a0f8d110f2e93926.mp4';
    const FOUNDER_VIDEO_MOBILE = 'https://cdn.shopify.com/videos/c/o/v/41cf5aaef9584ffd8f5d89b447065f94.mp4';
    const FOUNDER_VIDEO_DESKTOP = 'https://cdn.shopify.com/videos/c/o/v/e2a20473efe74fa4914660a06101e0dd.mp4';
    // Testimonial video, shown right under the checkout card on the results page.
    // Click-to-play (no autoplay): the customer decides to start it. #t=0.5 makes the
    // still frame a moment at 0.5s so it is never a black first frame. NL/EN by the
    // language selector, mobile/desktop by viewport.
    const isMobileTestimonial = !!(window.matchMedia && window.matchMedia('(max-width: 749px)').matches);
    const testimonialVideoSrc = isEN
      ? (isMobileTestimonial ? TESTIMONIAL_VIDEO_EN_MOBILE : TESTIMONIAL_VIDEO_EN_DESKTOP)
      : (isMobileTestimonial ? TESTIMONIAL_VIDEO_NL_MOBILE : TESTIMONIAL_VIDEO_NL_DESKTOP);
    const testimonialVideoHTML =
      `<div class="wr-bundle__testimonial-video" style="margin:14px 0 0;border-radius:12px;overflow:hidden;border:1px solid #EAE6DF;background:#FDFBF7;box-shadow:0 4px 12px rgba(0,0,0,0.02);">` +
      `<video class="wr-bundle__testimonial-video-el" src="${testimonialVideoSrc}#t=0.5" controls playsinline preload="metadata" style="display:block;width:100%;height:auto;background:#FDFBF7;"></video>` +
      `</div>`;

    const bundlePageHTML = `
      <div class="wr-page wr-page--bundle wr-page--enter" data-page="bundle" style="display: block;">
        <div class="wr-bundle__header" style="text-align:center;margin:15px 0 20px;display:flex;flex-direction:column;align-items:center;gap:8px;">
          <span class="wr-bundle__kicker" style="font-size:11px;font-weight:800;letter-spacing:0.08em;text-transform:uppercase;color:#E07A5F;line-height:1.2;">${packKicker}</span>
          <h3 class="wr-bundle__title" style="margin:0;padding:0;line-height:1.1;">${packTitle}</h3>
        </div>
        <div class="wr-bundle-layout">
          <div class="wr-bundle__author" style="display:flex;align-items:center;gap:14px;background:#fff;border:1px solid #EAE6DF;padding:14px;border-radius:12px;margin:0 0 18px;">
            <div style="flex-shrink:0;">
              <img src="https://cdn.shopify.com/s/files/1/0950/0757/2355/files/Adrien-Circle.png?v=1770890484" alt="Adrien" class="wr-founder-photo" style="width:100px;height:100px;border-radius:50%;object-fit:cover;border:2px solid #E07A5F;">
            </div>
            <div style="text-align:left;flex:1;min-width:0;">
              <span style="font-size:11px;font-weight:600;color:#888;display:block;letter-spacing:0.3px;margin-bottom:4px;">
                ${isEN ? 'Adrien, founder &amp; former winemaker' : 'Adrien, oprichter &amp; oud-wijnmaker'}
              </span>
              <p class="wr-founder-quote" style="margin:0 0 7px;font-size:13px;color:#333;line-height:1.45;font-weight:500;">
                ${isEN
                  ? `&ldquo;I chose these ${baseBottleCount} bottles for your palate myself. Not your style? <strong style="color:#1f2937;">I replace it for free in your next order</strong>, that is my promise.&rdquo;`
                  : `&ldquo;Ik koos deze ${baseBottleCount} flessen zelf voor jouw smaak. Niet jouw stijl? <strong style="color:#1f2937;">Ik vervang hem gratis in je volgende bestelling</strong>, dat beloof ik.&rdquo;`}
              </p>
              <a
                href="${FOUNDER_VIDEO_DESKTOP}"
                class="wr-founder-video-link"
                data-video-mobile="${FOUNDER_VIDEO_MOBILE}"
                data-video-desktop="${FOUNDER_VIDEO_DESKTOP}"
                style="font-size:11px;color:#E07A5F;text-decoration:underline;font-weight:600;"
              >
                ${isEN ? 'Meet Adrien (30 sec)' : 'Maak kennis met Adrien (30 sec)'}
              </a>
            </div>
          </div>
          <div class="wr-bundle-col-left" style="min-width:0;">
            <div class="wr-bundle-items">
              <div class="wr-bundle__tweak-panel-mount wr-bundle__tweak-panel-mount--desktop"></div>
              ${bundleSummaryHTML}
              <div class="wr-bundle__tweak-panel-mount wr-bundle__tweak-panel-mount--mobile"></div>
            </div>
          </div>
          <div class="wr-bundle-col-right">
            ${checkoutCardHTML}
            ${testimonialVideoHTML}
            ${emailCaptureHTML}
          </div>
          ${urgencyCardHTML}
        <div class="wr-sticky-buy" role="region" aria-label="${isEN ? 'Quick checkout' : 'Snel afrekenen'}">
          <div class="wr-sticky-buy__info">
            <span class="wr-sticky-buy__label">${isEN ? 'Your Test Kit' : 'Jouw Test Kit'}</span>
            <span class="wr-sticky-buy__price">€29,90</span>
          </div>
          <button class="wr-sticky-buy__btn" type="button">${isEN ? 'Get it now →' : 'Bestel nu →'}</button>
        </div>
      </div>
    `;
    cardsEl.insertAdjacentHTML('beforeend', bundlePageHTML);

    // Sticky mobile buy bar → triggers the real add-to-cart button.
    const stickyBtn = cardsEl.querySelector('.wr-sticky-buy__btn');
    const mainAddBtn = cardsEl.querySelector('.wr-bundle__add');
    if (stickyBtn && mainAddBtn) {
      stickyBtn.addEventListener('click', () => mainAddBtn.click());
    }

    this.setupPackReplacement(cardsEl, isEN);
    this.setupUpsellBlock(cardsEl, isEN);

    // Wine-by-wine reveal, played over the bundle (initial reveal only, not silent axis updates).
    if (!preserveScroll) {
      try {
        const revealHTML = this.buildWineRevealHTML(this._packWines, isEN, catalog, '');
        if (revealHTML) {
          document.body.insertAdjacentHTML('beforeend', revealHTML);
          const overlay = document.getElementById('wr-reveal');
          if (overlay) {
            this.playWineReveal(overlay, () => {
              // Scroll to the TOP of the results, offset by the sticky header height
              // so nothing (pack title) hides behind it.
              const target = cardsEl.querySelector('.wr-page--bundle');
              if (target) {
                const headerH = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-group-height')) || 100;
                const top = target.getBoundingClientRect().top + window.pageYOffset - headerH - 12;
                window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
              }
            });
          }
        }
      } catch (e) { console.warn('Wine reveal skipped:', e); }
    }

    const paymentIconMount = cardsEl.querySelector('.wr-checkout-payment-icons');
    if (paymentIconMount) {
      const footerIcons = Array.from(document.querySelectorAll('.payment-icons svg'));
      const wantedPayments = [
        { key: 'ideal', match: /ideal/i },
        { key: 'visa', match: /^visa$/i },
        { key: 'mastercard', match: /mastercard/i },
        { key: 'apple-pay', match: /apple pay/i }
      ];
      const clonedIcons = wantedPayments
        .map(({ match }) => footerIcons.find(svg => {
          const title = svg.querySelector('title');
          return title && match.test(title.textContent.trim());
        }))
        .filter(Boolean)
        .map(svg => {
          const clone = svg.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          clone.setAttribute('focusable', 'false');
          clone.style.width = '38px';
          clone.style.height = '24px';
          clone.style.display = 'block';
          return clone;
        });

      if (clonedIcons.length >= 3) {
        paymentIconMount.replaceChildren(...clonedIcons);
      }
    }

    const closeFounderVideoModal = () => {
      const modal = document.querySelector('.wr-founder-video-modal');
      if (!modal) return;
      const video = modal.querySelector('video');
      if (video) {
        video.pause();
        video.removeAttribute('src');
        video.load();
      }
      modal.remove();
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onFounderVideoEscClose);
    };

    const onFounderVideoEscClose = (event) => {
      if (event.key === 'Escape') closeFounderVideoModal();
    };

    const openFounderVideoModal = (videoUrl) => {
      closeFounderVideoModal();

      const modal = document.createElement('div');
      modal.className = 'wr-founder-video-modal';
      modal.innerHTML = `
        <div style="position:fixed;inset:0;z-index:9999;background:rgba(15,23,42,0.72);display:flex;align-items:center;justify-content:center;padding:16px;">
          <div style="position:relative;width:min(92vw,860px);max-height:88vh;background:#000;border-radius:12px;overflow:hidden;box-shadow:0 20px 60px rgba(0,0,0,0.35);">
            <button type="button" aria-label="Close video" data-founder-video-close style="position:absolute;top:8px;right:8px;z-index:2;border:0;border-radius:999px;width:34px;height:34px;background:rgba(17,24,39,0.75);color:#fff;font-size:20px;line-height:1;cursor:pointer;">×</button>
            <video controls playsinline autoplay style="display:block;width:100%;height:auto;max-height:88vh;background:#000;">
              <source src="${videoUrl}" type="video/mp4">
            </video>
          </div>
        </div>
      `;

      modal.addEventListener('click', (event) => {
        if (event.target === modal.firstElementChild) closeFounderVideoModal();
      });

      const closeBtn = modal.querySelector('[data-founder-video-close]');
      if (closeBtn) closeBtn.addEventListener('click', closeFounderVideoModal);

      document.body.appendChild(modal);
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', onFounderVideoEscClose);
    };

    const founderVideoLink = cardsEl.querySelector('.wr-founder-video-link');
    if (founderVideoLink) {
      founderVideoLink.addEventListener('click', (event) => {
        event.preventDefault();
        const isMobileVideo = window.matchMedia && window.matchMedia('(max-width: 749px)').matches;
        const mobileVideoUrl = founderVideoLink.getAttribute('data-video-mobile') || FOUNDER_VIDEO_MOBILE;
        const desktopVideoUrl = founderVideoLink.getAttribute('data-video-desktop') || FOUNDER_VIDEO_DESKTOP;
        const videoUrl = isMobileVideo ? mobileVideoUrl : desktopVideoUrl;
        openFounderVideoModal(videoUrl);
      });
    }

    // Keep exact checkout plan copy even if a third-party script mutates text nodes.
    const enforceCheckoutPlanCopy = () => {
      const planEl = cardsEl.querySelector('.wr-bundle__plan--checkout');
      if (!planEl) return;
      const expected = isEN
        ? [
          '1. Taste your personalized box',
          '2. Rate them in 1-click on the product page',
          '3. Unlock perfect wines tailored for life'
        ]
        : [
          '1. Proef je gepersonaliseerde box',
          '2. Beoordeel ze met 1 klik op de productpagina',
          '3. Ontgrendel perfecte wijnen op maat voor het leven'
        ];
      const textEls = planEl.querySelectorAll('.wr-bundle__plan-text');
      if (!textEls || textEls.length < expected.length) return;
      textEls.forEach((el, index) => {
        if (index >= expected.length) return;
        if ((el.textContent || '').trim() !== expected[index]) {
          el.textContent = expected[index];
        }
      });
    };
    enforceCheckoutPlanCopy();
    setTimeout(enforceCheckoutPlanCopy, 0);
    setTimeout(enforceCheckoutPlanCopy, 400);

    // Render tweak panel (open by default) into the mount point
    const tweakMounts = cardsEl.querySelectorAll('.wr-bundle__tweak-panel-mount');
    if (tweakMounts.length && typeof this.renderTweakPanel === 'function') {
      tweakMounts.forEach(tweakMount => this.renderTweakPanel(tweakMount, isEN));
    }

    // ---- Event listeners ----

    // Next wine / see selection buttons
    cardsEl.querySelectorAll('.wr-page__next').forEach(btn => {
      btn.addEventListener('click', () => goToPage(btn.dataset.next));
    });

    // Previous buttons
    cardsEl.querySelectorAll('.wr-page__prev').forEach(btn => {
      btn.addEventListener('click', () => goToPage(btn.dataset.prev));
    });

    // Bundle item → open product modal
    const closeProductModal = () => {
      const modal = document.querySelector('.wr-product-modal');
      if (modal) modal.remove();
      document.body.style.overflow = '';
    };
    const openProductModal = (item) => {
      closeProductModal();
      const wineId = item.dataset.wineId;
      const p = catalog[String(wineId)] || {};
      const productUrl = item.dataset.productUrl || p.url || (p.handle ? `/products/${p.handle}` : '');
      const imgEl = item.querySelector('.wr-bundle-item__img');
      const titleEl = item.querySelector('.wr-bundle-item__title');
      const imgSrc = (p.image && p.image.url) ? p.image.url : (imgEl ? imgEl.getAttribute('src') : '');
      const title = titleEl ? titleEl.textContent.trim() : (p.title || '');
      const tag = item.dataset.typeLabel || '';
      const priceCents = parseInt(item.dataset.displayPrice || '0', 10);
      // Discounted bottles carry their pre-discount price, so the modal can show what the
      // wine normally costs next to what this customer pays. Kit cards have no compare
      // price and keep a single figure.
      const comparePriceCents = parseInt(item.dataset.comparePrice || '0', 10);
      const molTitle = item.dataset.molecularTitle || '';
      const molText = item.dataset.molecularText || '';
      const tasteAxesHTML = this.buildProductModalTasteAxes(item, isEN);
      const closeText = isEN ? 'Close' : 'Sluiten';
      const kitInclusionLabel = isEN ? 'Included in your Taste Kit' : 'Inbegrepen in je Taste Kit';

      // Taste fingerprint: this wine's 3 lab axes vs your quiz taste, plus a match %.
      const targetToPct = (v) => Math.round(((Math.max(-1.2, Math.min(1.2, Number(v) || 0)) + 1.2) / 2.4) * 100);
      const ux = targetToPct(this.targetX), uy = targetToPct(this.targetY), uz = targetToPct(this.targetZ);
      const wx = parseInt(item.dataset.axisX, 10), wy = parseInt(item.dataset.axisY, 10), wz = parseInt(item.dataset.axisZ, 10);
      const hasAxes = [wx, wy, wz].every(Number.isFinite);
      // No customer-to-wine match % here on purpose: it is derived from the 6 quiz answers,
      // not from the lab, and it collided with the lab-measured "80-90% alike" promise
      // (a 72% here read as a broken promise). The fingerprint chart shows closeness instead.
      // Lab block, fingerprint, winemaker quote and technical sheet all come from the
      // shared renderer in `assets/wine-fiche.js`, so this modal and the printed Taste Kit
      // recap sheet (`/pages/kit-recap`) can never drift apart. theme.liquid loads that
      // file immediately before this one; the guard below is only there so a missing asset
      // degrades to no modal instead of a thrown error mid-checkout-funnel.
      if (!window.VinPopWineFiche) {
        console.warn('TasteProfileQuiz: wine-fiche.js not loaded, product modal skipped');
        return;
      }

      const ficheHTML = window.VinPopWineFiche.build({
        isEN,
        wineId,
        title,
        imageUrl: imgSrc,
        role: tag,
        product: p,
        price: priceCents
          ? {
              cents: priceCents,
              compareCents: comparePriceCents,
              discountRate: this.upsellDiscountRate,
              kitLabel: kitInclusionLabel
            }
          : null,
        axes: hasAxes ? { wx, wy, wz, ux, uy, uz } : null,
        // `data-molecular-text` is the exact string the card the customer just clicked
        // already shows in `.wr-bundle-item__why`; it is only a fallback for bottles whose
        // winemaker has no note written yet.
        why: molText || molTitle,
        closeButton: true,
        closeLabel: closeText
      });

      const modalHTML = `
        <div class="wr-product-modal is-open">
          <div class="wr-product-modal__backdrop" data-close-modal="1"></div>
          <div class="wr-product-modal__dialog" role="dialog" aria-modal="true" aria-label="Taste analysis">${ficheHTML}</div>
        </div>
      `;
      document.body.insertAdjacentHTML('beforeend', modalHTML);
      document.body.style.overflow = 'hidden';
      const modal = document.querySelector('.wr-product-modal');

      // Enforce clean label/value separation in the exact facts container path.
      const normalizeFactsSpacing = () => {
        const factsRoot = document.querySelector('body > div.wr-product-modal.is-open > div.wr-product-modal__dialog > div > div.wr-product-modal__facts')
          || modal.querySelector('.wr-product-modal__facts');
        if (!factsRoot) return;
        factsRoot.querySelectorAll('div').forEach((row) => {
          const span = row.querySelector('span');
          const strong = row.querySelector('strong');
          if (!span || !strong) return;
          const between = span.nextSibling;
          if (!between || between.nodeType !== Node.TEXT_NODE || between.textContent !== ' ') {
            row.insertBefore(document.createTextNode(' '), strong);
          }
        });
      };
      normalizeFactsSpacing();

      modal.querySelectorAll('[data-close-modal]').forEach(el => {
        el.addEventListener('click', closeProductModal);
      });
      const onEsc = (e) => {
        if (e.key === 'Escape') { closeProductModal(); document.removeEventListener('keydown', onEsc); }
      };
      document.addEventListener('keydown', onEsc);

      // Enrich missing facts from product page content (region/year/abv/closure often live there).
      if (productUrl) {
        fetch(productUrl, { credentials: 'same-origin' })
          .then(r => r.ok ? r.text() : '')
          .then(html => {
            if (!html) return;
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const text = (doc.body ? doc.body.innerText : '')
              .replace(/\u00a0/g, ' ')
              .replace(/\r/g, '');

            const lines = text
              .split('\n')
              .map(l => l.replace(/\s+/g, ' ').trim())
              .filter(Boolean);

            const byLine = (labels) => {
              const labelSet = labels.map(l => l.toLowerCase());
              for (let i = 0; i < lines.length; i++) {
                const line = lines[i];
                const low = line.toLowerCase();
                for (const label of labelSet) {
                  if (low === label && lines[i + 1]) return lines[i + 1];
                  if (low.startsWith(label + ':')) {
                    const val = line.slice(label.length + 1).trim();
                    if (val) return val;
                    if (lines[i + 1]) return lines[i + 1];
                  }
                  if (low.startsWith(label + ' :')) {
                    const val = line.slice(label.length + 2).trim();
                    if (val) return val;
                    if (lines[i + 1]) return lines[i + 1];
                  }
                }
              }
              return '';
            };

            const byRegex = (pattern) => {
              const m = text.match(pattern);
              return m ? m[1].trim() : '';
            };

            const fetched = {
              country: byLine(['country of origin', 'land van herkomst']) || byRegex(/(?:Country of origin|Land van herkomst)\s*:?\s*([^\n]+)/i),
              region: byLine(['wine region', 'regio']) || byRegex(/(?:Wine region|Regio)\s*:?\s*([^\n]+)/i),
              grape: byLine(['grape variety', 'druivensoort']) || byRegex(/(?:Grape variety|Druivensoort)\s*:?\s*([^\n]+)/i),
              winemaker: byLine(['wine maker', 'wijnmaker']) || byRegex(/(?:Wine maker|Wijnmaker)\s*:?\s*([^\n]+)/i),
              year: byLine(['year']) || byRegex(/Year\s*:?\s*([0-9]{4})/i),
              alcohol: byLine(['abv', 'alcoholpercentage']) || byRegex(/(?:ABV|Alcoholpercentage)\s*:?\s*([0-9]+(?:\.[0-9]+)?%?)/i),
              capacity: byLine(['capacity']) || byRegex(/Capacity\s*:?\s*([^\n]+)/i),
              closing: byLine(['closing']) || byRegex(/Closing\s*:?\s*([^\n]+)/i),
            };

            const rows = Array.from(document.querySelectorAll('.wr-product-modal__facts > div'));
            const map = {
              'land van herkomst': fetched.country,
              'country of origin': fetched.country,
              'wine region': fetched.region,
              'wijnregio': fetched.region,
              'druivensoort': fetched.grape,
              'grape variety': fetched.grape,
              'wijnmaker': fetched.winemaker,
              'winemaker': fetched.winemaker,
              'year': fetched.year,
              'jaar': fetched.year,
              'alcoholpercentage': fetched.alcohol,
              'alcohol percentage': fetched.alcohol,
              'capacity': fetched.capacity,
              'inhoud': fetched.capacity,
              'closing': fetched.closing,
              'closure': fetched.closing,
              'sluiting': fetched.closing,
            };

            rows.forEach(row => {
              const label = (row.querySelector('span')?.textContent || '')
                .replace(/\s*:\s*$/, '')
                .trim()
                .toLowerCase();
              const valueEl = row.querySelector('strong');
              if (!valueEl) return;
              const current = (valueEl.textContent || '').trim();
              const nextVal = map[label] ? map[label].replace(/\s+/g, ' ').trim() : '';
              if (nextVal && nextVal !== current) {
                valueEl.textContent = nextVal;
              }
            });

            normalizeFactsSpacing();
          })
          .catch(() => {});
      }
    };

    if (!cardsEl.dataset.bundleModalDelegated) {
      cardsEl.dataset.bundleModalDelegated = '1';
      cardsEl.addEventListener('click', (e) => {
        if (e.target.closest('a, button')) return;
        const item = e.target.closest('.wr-bundle-item');
        if (item && cardsEl.contains(item)) openProductModal(item);
      });
      cardsEl.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        const item = e.target.closest('.wr-bundle-item');
        if (!item || !cardsEl.contains(item)) return;
        e.preventDefault();
        openProductModal(item);
      });
    }

    // Bundle: add the Starter Pack with line item properties, then open the cart drawer.
    //
    // We deliberately do NOT jump to checkout. This has now been tried twice and reverted
    // twice. The 2026-07-30 version sent the customer straight to the payment page to cut
    // a step; on 2026-08-01 the owner saw three clicks on this CTA and zero orders, and
    // read it as customers being pushed onto a payment form before they were ready. This
    // is cold paid traffic: the drawer lets them keep the page, and the reassurance on it,
    // while they decide. Three clicks is far too small a sample to prove anything either
    // way, so if the direct-to-checkout idea comes back, settle it with an A/B test rather
    // than another blind flip.
    const STARTER_PACK_VARIANT = 57742550860163;
    // Locale-aware root ('/', '/en/', …) so a customer reading the EN page is not
    // dropped into a Dutch checkout. Always ends with a slash.
    const shopRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const addBtn = cardsEl.querySelector('.wr-bundle__add');
    if (addBtn) {
      addBtn.onclick = async () => {
        addBtn.disabled = true;
        addBtn.querySelector('.push-btn__surface').textContent = isEN ? '⏳ Adding to your cart…' : '⏳ Toevoegen aan je winkelwagen…';
        try {
          // Build line item properties with wine names
          const properties = {};
          this._packWines.forEach((wine, i) => {
            const p = catalog[String(wine.wine_id)];
            if (!p) return;
            if (wine._isBonus) {
              return;
            } else if (wine._isSimilar) {
              properties[isEN ? 'Similar Wine' : 'Vergelijkbare Wijn'] = p.title;
            } else {
              properties[`${isEN ? 'Quiz Match' : 'Quiz Match'} #${i + 1}`] = p.title;
            }
          });

          // The gifted bottle is a PROPERTY, exactly like the paid ones, not a cart line of its
          // own. The customer buys a single €29,90 product and the properties tell the warehouse
          // what to pack, so nothing is ever charged for a wine and the page can never disagree
          // with the receipt. It replaces the old dedicated 0€ variant.
          //
          // WAREHOUSE: this line used to arrive as a separate order line. Bulpe Fulfilment must
          // read it from the properties now, or they will pack one bottle short.
          // KIT SNAPSHOT — see `KIT_RECAP_REFERRAL.md`.
          // The order itself only ever stores wine TITLES, and the buyer is usually logged
          // out here (paid Meta traffic), so `/api/quizz/save` has nothing either. Without
          // this call the printed recap sheet and the guest rating page cannot be rebuilt
          // from the order at all. It also freezes the fingerprint axes, which are min-max
          // normalised against THIS quiz run's candidate set and are not reproducible later.
          //
          // Deliberately non-blocking: a snapshot failure must never cost a sale, so the
          // token is simply absent and the order ships without a recap sheet. A copy is
          // kept in localStorage so a failed snapshot can still be replayed afterwards.
          const kitToken = await this.persistKitSnapshot(properties).catch(() => null);
          if (kitToken) {
            // Visible on purpose: fulfilment is manual and the merchant reads this order
            // line in admin anyway, so the code to print sits next to the wine names.
            // Never prefix with `_` — that hides it from admin (see QUIZ_BUNDLE_PAGE.md).
            properties[isEN ? 'Kit ref' : 'Kit ref'] = kitToken;
          }

          // Which question set built this kit, so ratings can be compared per path in admin.
          // Same English value in every locale to keep the orders filterable.
          if (this.pathChooser && this.quizPath) {
            properties['Quiz path'] = this.quizPath === 'expert' ? 'Wine lover' : 'Everyday';
          }

          Object.assign(properties, this.buildKitTraceProperties());

          const cartItems = [{
            id: STARTER_PACK_VARIANT,
            quantity: 1,
            properties: properties
          }];

          // Add calibration bonus bottle as a dedicated 0€ variant in cart.
          const calibrationBonusWine = (this._packWines || []).find(w => w && w._isBonus);
          if (calibrationBonusWine && calibrationBonusWine.wine_id) {
            const BONUS_VARIANT_ID = this.getFreeBonusVariantIdByProductId(calibrationBonusWine.wine_id);
            if (BONUS_VARIANT_ID) {
              cartItems.push({
                id: BONUS_VARIANT_ID,
                quantity: 1,
                properties: {
                  [isEN ? 'Calibration Bonus' : 'Calibration Bonus']: isEN ? 'Yes' : 'Ja'
                }
              });
            }
          }

          // Extra bottles: added at full price. The 20% comes off in the cart through the
          // "Buy 1 Test Kit, get N at 20% off" automatic discount, which is the only way
          // the reduction can never apply to a bottle bought without the kit.
          this.getAddedUpsellEntries().forEach((entry, index) => {
            cartItems.push({
              id: entry.variantId,
              quantity: 1,
              properties: {
                [`${isEN ? 'Extra bottle (-20%)' : 'Extra fles (-20%)'} #${index + 1}`]: entry.product.title
              }
            });
          });

          // Ask for the same sections the theme's own cart requests: its cart
          // count, drawer contents and live region all refresh from them.
          const resp = await fetch(`${shopRoot}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: cartItems,
              sections: 'cart-items,cart-footer,cart-item-count,cart-live-region',
              sections_url: window.location.pathname
            })
          });
          if (!resp.ok) throw new Error('Cart API error');
          const cartData = await resp.json();

          // Hand over to the theme's cart, then open the drawer ourselves: the
          // shop's "open drawer on add to cart" setting is off, so without this
          // the click would look like nothing happened.
          try {
            document.body.dispatchEvent(new CustomEvent('shapes:cart:afteradditem', {
              bubbles: true,
              detail: { response: cartData }
            }));
            window.Alpine?.store('modals')?.open('cart');
          } catch (drawerError) {
            console.warn('Cart drawer refresh failed, sending to cart page:', drawerError);
            window.location.href = `${shopRoot}cart`;
            return;
          }

          addBtn.querySelector('.push-btn__surface').textContent = isEN ? '✓ Added to your cart' : '✓ Toegevoegd aan je winkelwagen';
          addBtn.disabled = false;
        } catch (e) {
          console.error('Cart add failed:', e);
          addBtn.querySelector('.push-btn__surface').textContent = isEN ? '❌ Error. Try again' : '❌ Fout. Probeer opnieuw';
          addBtn.disabled = false;
        }
      };
    }

    // No email form is rendered on this page any more, so there is no submit handler to
    // bind. `vinpop_saved_gift_tag` is only read now, and only ever written by a Shopify
    // customer tag on the account itself.

    // Skip / browse all wines
    const skipBtn = cardsEl.querySelector('.wr-bundle__skip');
    if (skipBtn) {
      skipBtn.onclick = () => {
        this.closePopup();
        window.location.href = window.location.pathname + '?taste_profile=true';
      };
    }

    // Account creation (save selection)
    const emailBtn = cardsEl.querySelector('.wr-bundle__email-btn');
    if (emailBtn) {
      emailBtn.onclick = () => {
        const returnUrl = isEN ? '/en/account/register' : '/nl/account/register';
        window.location.href = returnUrl;
      };
    }

    // Retake quiz
    const retakeBtn = cardsEl.querySelector('.wr-bundle__retake-link');
    if (retakeBtn) {
      retakeBtn.onclick = () => {
        this.setForceRetake(true);
        localStorage.removeItem('vinpop-quiz-completed');
        sessionStorage.removeItem('vinpop-quiz-completed');
        sessionStorage.removeItem('vinpop_last_results');
        sessionStorage.removeItem('vinpop_taste_profile');
        sessionStorage.removeItem('tasteProfileResults');
        sessionStorage.removeItem('vinpop_quiz_state');
        this.quizSavePromise = null;
        this.lastQuizSaveSignature = '';
        this._reloadToQuizTop();
      };
    }

    // Calibration Kit: jump directly to bundle and hide step navigation.
    const skipWineSteps = /\/collections\/(?:test-kit|tasting-kit)(?:\/|$)/.test(window.location.pathname);
    if (skipWineSteps) {
      goToPage('bundle');
      const dotsEl = cardsEl.querySelector('.wr-dots');
      if (dotsEl) dotsEl.style.display = 'none';

      const bundlePrevBtn = cardsEl.querySelector('.wr-page--bundle .wr-page__prev');
      if (bundlePrevBtn) bundlePrevBtn.remove();

      const bundleNav = cardsEl.querySelector('.wr-page--bundle .wr-page__actions-nav');
      if (bundleNav) bundleNav.style.justifyContent = 'center';
    } else {
      // Animate first page in for non-calibration flows
      goToPage('0');
      const firstPage = cardsEl.querySelector('[data-page="0"]');
      if (firstPage) firstPage.classList.add('wr-page--enter');
    }
    if (preserveScroll) {
      requestAnimationFrame(() => {
        if (preservedPopupContent && preservedPopupY !== null) {
          preservedPopupContent.scrollTop = preservedPopupY;
        }
        if (preservedWindowY !== null) {
          window.scrollTo({ top: preservedWindowY, behavior: 'auto' });
        }
      });
    }
  }

  async fetchShopifyProducts(wines, container) {
    try {
      // Build collection URL with product IDs as handles
      const productHandles = wines.map(w => w.wine_id).join(',');
      
      // Get current collection handle from URL
      const collectionMatch = window.location.pathname.match(/\/collections\/([^\/]+)/);
      const collectionHandle = collectionMatch ? collectionMatch[1] : 'quiz';
      
      // Use Shopify Section Rendering API to get pre-rendered product HTML
      const sectionUrl = `/collections/${collectionHandle}?section_id=main-collection-product-grid`;
      
      const response = await fetch(sectionUrl);
      
      if (!response.ok) {
        throw new Error('Failed to fetch section from Shopify');
      }
      
      const html = await response.text();
      
      // Parse the HTML to extract product tiles
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const productItems = doc.querySelectorAll('li[data-product-id]');
      
      // Clear loading
      container.innerHTML = '';
      
      if (productItems.length === 0) {
        container.innerHTML = '<p class="no-results">No products found. Please try again.</p>';
        return;
      }
      
      // Create a map of wine scores by product ID
      const scoreMap = {};
      wines.forEach(wine => {
        scoreMap[wine.wine_id] = wine.score;
      });
      
      // Collect products with scores and sort
      const productsArray = [];
      productItems.forEach(item => {
        const productId = item.getAttribute('data-product-id');
        const score = scoreMap[productId];
        
        // Only include products with 70% or higher compatibility
        if (score !== undefined && score >= 70) {
          productsArray.push({
            element: item,
            id: productId,
            score: score
          });
        }
      });
      
      // Sort by score (highest first)
      productsArray.sort((a, b) => b.score - a.score);
      
      console.log(`🍷 Found ${productsArray.length} products with 70%+ compatibility`);
      
      // Build HTML string instead of cloning nodes
      let htmlString = '';
      productsArray.forEach((productData, index) => {
        // Get the outer HTML and add taste score badge
        let productHtml = productData.element.outerHTML;
        
        // Insert taste score badge into the product HTML
        // Find the product-tile__featured-media or product-media-object div
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = productHtml;
        const mediaContainer = tempDiv.querySelector('.product-tile__featured-media .relative, .product-media-object');
        
        if (mediaContainer) {
          const badge = document.createElement('div');
          badge.className = 'taste-score-badge';
          badge.innerHTML = `${productData.score}%`;
          badge.style.cssText = 'position: absolute; top: 10px; right: 10px; background: linear-gradient(135deg, #8B4513, #D2691E); color: white; padding: 8px 12px; border-radius: 20px; font-weight: 700; font-size: 14px; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2); z-index: 10;';
          mediaContainer.style.position = 'relative';
          mediaContainer.appendChild(badge);
          
          productHtml = tempDiv.innerHTML;
        }
        
        htmlString += productHtml;
      });
      
      // Insert all HTML at once
      container.innerHTML = htmlString;
      
      // Reinitialize Alpine.js components (quick-buy buttons, quantity selectors, etc.)
      if (window.Alpine) {
        console.log('🔄 Reinitializing Alpine.js components...');
        // Wait a bit for DOM to settle, then init all Alpine components in the container
        setTimeout(() => {
          // Find all elements with x-data in the newly added products
          const alpineElements = container.querySelectorAll('[x-data]');
          alpineElements.forEach(el => {
            // Check if Alpine is not already initialized on this element
            if (!el._x_dataStack) {
              try {
                window.Alpine.initTree(el);
              } catch (e) {
                console.warn('Could not init Alpine on element:', el, e);
              }
            }
          });
          
          // Also reinit any data-island elements
          const islands = container.querySelectorAll('data-island');
          islands.forEach(island => {
            if (!island._x_dataStack && island.hasAttribute('x-data')) {
              try {
                window.Alpine.initTree(island);
              } catch (e) {
                console.warn('Could not init Alpine on island:', island, e);
              }
            }
          });
        }, 100);
      }
      
      // Dispatch events for other scripts that might need to initialize
      document.dispatchEvent(new CustomEvent('vinpop:products-reordered'));
      document.dispatchEvent(new CustomEvent('theme:product-tile:loaded'));
      
    } catch (error) {
      console.error('Error fetching Shopify products:', error);
      container.innerHTML = '<p class="error">Error loading products. Please refresh the page.</p>';
    }
  }

  showError() {
    this.loadingEl.style.display = 'none';
    const errorDiv = document.createElement('div');
    errorDiv.className = 'taste-profile-error';
    errorDiv.innerHTML = `
      <p>Sorry, we couldn't load your recommendations. Please try again later.</p>
      <button class="btn" onclick="location.reload()">Retry</button>
    `;
    this.popup.querySelector('.taste-profile-questions').appendChild(errorDiv);
  }

  // 3-axis "taste fingerprint": a triangle of the wine's lab axes (x/y/z) overlaid
  // with the customer's quiz taste, so closeness is visible at a glance.
  // The chart itself now lives in `assets/wine-fiche.js` because the printed kit recap
  // sheet draws the same triangle; this wrapper is kept so existing call sites and any
  // console debugging against the quiz instance keep working.
  buildProductModalFingerprint(wx, wy, wz, ux, uy, uz, isEN) {
    if (!window.VinPopWineFiche) return '';
    return window.VinPopWineFiche.fingerprint(wx, wy, wz, ux, uy, uz, isEN);
  }

  buildProductModalTasteAxes(item, isEN) {
    const toPct = (value) => {
      const n = Number(value);
      if (!Number.isFinite(n)) return null;
      return Math.max(0, Math.min(100, Math.round(n)));
    };
    const x = toPct(item && item.dataset ? item.dataset.axisX : null);
    const y = toPct(item && item.dataset ? item.dataset.axisY : null);
    const z = toPct(item && item.dataset ? item.dataset.axisZ : null);
    if (x === null || y === null || z === null) return '';

    const band = (pct, low, mid, high) => {
      if (pct < 36) return low;
      if (pct > 64) return high;
      return mid;
    };
    const escHTML = (s) => String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

    const copy = isEN
      ? {
          title: 'What it will taste like',
          fresh: 'Fresh',
          round: 'Round',
          soft: 'Soft',
          structured: 'Structured',
          light: 'Light',
          intense: 'Intense',
          xTitle: band(x, 'Fresh and crisp', 'Balanced freshness', 'Round and smooth'),
          xText: band(
            x,
            'Like choosing lemon vinaigrette: expect a brighter, fresher sip.',
            'Neither sharp nor heavy: it should feel easy to drink.',
            'Like choosing creamy dressing or milk chocolate: softer edges, more roundness.'
          ),
          yTitle: band(y, 'Soft and gentle', 'Balanced structure', 'Bold structure'),
          yText: band(
            y,
            'Like cappuccino or Brie: low bitterness, low grip.',
            'Enough structure to feel like wine, without taking over.',
            'Like black coffee or aged cheese: more grip, depth and character.'
          ),
          zTitle: band(z, 'Light impact', 'Medium intensity', 'High intensity'),
          zText: band(
            z,
            'Best when you want something fresh and easy.',
            'Enough flavour to notice, still comfortable for a first glass.',
            'Closer to your Bold & Powerful answer: louder flavour and longer finish.'
          )
        }
      : {
          title: 'Hoe deze wijn smaakt',
          fresh: 'Fris',
          round: 'Rond',
          soft: 'Zacht',
          structured: 'Gestructureerd',
          light: 'Licht',
          intense: 'Intens',
          xTitle: band(x, 'Fris en strak', 'Gebalanceerde frisheid', 'Rond en zacht'),
          xText: band(
            x,
            'Zoals citroendressing: verwacht een frissere, levendigere slok.',
            'Niet scherp en niet zwaar: makkelijk om te drinken.',
            'Zoals romige dressing of melkchocolade: zachtere randen en meer rondheid.'
          ),
          yTitle: band(y, 'Zacht en vriendelijk', 'Gebalanceerde structuur', 'Krachtige structuur'),
          yText: band(
            y,
            'Zoals cappuccino of Brie: weinig bitterheid en weinig grip.',
            'Genoeg structuur om wijn te voelen, zonder te overheersen.',
            'Zoals zwarte koffie of oude kaas: meer grip, diepte en karakter.'
          ),
          zTitle: band(z, 'Lichte indruk', 'Medium intensiteit', 'Hoge intensiteit'),
          zText: band(
            z,
            'Goed als je iets fris en makkelijks wilt.',
            'Genoeg smaak om op te vallen, nog steeds comfortabel voor een eerste glas.',
            'Dichter bij jouw Krachtig & Vol antwoord: meer smaak en een langere afdronk.'
          )
        };

    const row = (modifier, labelLeft, labelRight, value, title, text) => `
      <div class="wr-product-modal__axis wr-product-modal__axis--${modifier}">
        <div class="wr-product-modal__axis-head">
          <span>${escHTML(title)}</span>
          <strong>${value}%</strong>
        </div>
        <div class="wr-product-modal__axis-track" aria-hidden="true">
          <span class="wr-product-modal__axis-fill" style="width:${value}%"></span>
          <span class="wr-product-modal__axis-dot" style="left:${value}%"></span>
        </div>
        <div class="wr-product-modal__axis-scale">
          <span>${escHTML(labelLeft)}</span>
          <span>${escHTML(labelRight)}</span>
        </div>
        <p>${escHTML(text)}</p>
      </div>
    `;

    return `
      <div class="wr-product-modal__taste-axes">
        <div class="wr-product-modal__taste-axes-head">
          <span>${escHTML(copy.title)}</span>
        </div>
        ${row('freshness', copy.fresh, copy.round, x, copy.xTitle, copy.xText)}
        ${row('structure', copy.soft, copy.structured, y, copy.yTitle, copy.yText)}
        ${row('intensity', copy.light, copy.intense, z, copy.zTitle, copy.zText)}
      </div>
    `;
  }

  buildDynamicWineMatchText(wine, fallbackText, isEN) {
    if (!wine) return fallbackText || '';

    const clampPct = (value) => {
      const number = Number(value);
      if (!Number.isFinite(number)) return null;
      return Math.max(0, Math.min(100, Math.round(number)));
    };
    const targetToPct = (value) => Math.round(((Math.max(-1.2, Math.min(1.2, Number(value) || 0)) + 1.2) / 2.4) * 100);
    const user = {
      x: clampPct(wine._barUserX) ?? targetToPct(this.targetX),
      y: clampPct(wine._barUserY) ?? targetToPct(this.targetY),
      z: clampPct(wine._barUserZ) ?? targetToPct(this.targetZ),
    };
    const bottle = {
      x: clampPct(wine._barWineX),
      y: clampPct(wine._barWineY),
      z: clampPct(wine._barWineZ),
    };

    const axisCopy = {
      x: isEN
        ? { name: 'freshness', left: 'fresh', mid: 'balanced', right: 'round' }
        : { name: 'frisheid', left: 'fris', mid: 'gebalanceerd', right: 'rond' },
      y: isEN
        ? { name: 'structure', left: 'soft', mid: 'balanced', right: 'structured' }
        : { name: 'structuur', left: 'zacht', mid: 'gebalanceerd', right: 'gestructureerd' },
      z: isEN
        ? { name: 'intensity', left: 'light', mid: 'balanced', right: 'intense' }
        : { name: 'intensiteit', left: 'licht', mid: 'gebalanceerd', right: 'intens' },
    };
    const zoneFor = (pct, copy) => {
      if (pct < 42) return copy.left;
      if (pct > 58) return copy.right;
      return copy.mid;
    };
    const axes = ['x', 'y', 'z']
      .filter(axis => bottle[axis] !== null)
      .map(axis => ({
        axis,
        copy: axisCopy[axis],
        userPct: user[axis],
        bottlePct: bottle[axis],
        similarity: 100 - Math.abs(user[axis] - bottle[axis])
      }))
      .sort((a, b) => b.similarity - a.similarity);

    if (!axes.length) return fallbackText || '';

    const best = axes[0];
    const second = axes[1];
    const score = Number.isFinite(Number(wine.score)) ? Math.round(Number(wine.score)) : null;
    const source = this.axisTweakActive
      ? (isEN ? 'Your updated sliders' : 'Je aangepaste assen')
      : (isEN ? 'Your quiz answers' : 'Je quizantwoorden');
    const userZone = zoneFor(best.userPct, best.copy);
    const bottleZone = zoneFor(best.bottlePct, best.copy);
    const secondText = second
      ? (isEN
        ? ` It also stays close on ${second.copy.name}.`
        : ` Hij blijft ook dicht bij je ${second.copy.name}.`)
      : '';
    const scoreText = score !== null
      ? (isEN ? ` ${score}% match.` : ` ${score}% match.`)
      : '';

    if (userZone === bottleZone) {
      return isEN
        ? `${source} point to ${userZone} ${best.copy.name}. This bottle sits in the same taste zone.${scoreText}${secondText}`
        : `${source} wijzen naar ${userZone} ${best.copy.name}. Deze fles zit in dezelfde smaakzone.${scoreText}${secondText}`;
    }

    return isEN
      ? `${source} are closest to this bottle on ${best.copy.name}: you are ${userZone}, it is ${bottleZone}.${scoreText}${secondText}`
      : `${source} komen het dichtst bij deze fles op ${best.copy.name}: jij bent ${userZone}, de fles is ${bottleZone}.${scoreText}${secondText}`;
  }

  // The gift bottle is not part of the scored selection, so its lab axes have to be looked
  // up in the pool every wine was scored against. Without them the product modal cannot
  // draw the taste fingerprint.
  getScoredWineByProductId(productId) {
    const id = String(productId || '');
    if (!id) return null;
    const pools = [this.calibrationSearchPool, this.recommendedWines, this._packWines];
    for (const pool of pools) {
      if (!Array.isArray(pool)) continue;
      const found = pool.find(wine => wine && String(wine.wine_id) === id);
      if (found) return found;
    }
    return null;
  }

  // Same lab axes the kit cards publish, so the modal draws the identical fingerprint.
  buildAxisAttrs(wine) {
    return [
      Number.isFinite(Number(wine?._barWineX)) ? `data-axis-x="${Math.round(Number(wine._barWineX))}"` : '',
      Number.isFinite(Number(wine?._barWineY)) ? `data-axis-y="${Math.round(Number(wine._barWineY))}"` : '',
      Number.isFinite(Number(wine?._barWineZ)) ? `data-axis-z="${Math.round(Number(wine._barWineZ))}"` : '',
    ].filter(Boolean).join(' ');
  }

  // Free gift bottle card, rendered next to the kit bottles. It is never scored and never
  // shows a match %: it is a gift, and pretending it was chosen for this customer's taste
  // would undermine the very thing the quiz is proving. The fingerprint is still shown,
  // because measuring the bottle is not the same claim as matching it to the customer.
  buildRebelBonusCardHTML(productId, isEN, catalog = window.shopCatalog || {}) {
    const rebelProd = catalog[String(productId || '')];
    if (!rebelProd) return '';
    const axisAttrs = this.buildAxisAttrs(this.getScoredWineByProductId(productId));
    const escAttr = (s) => String(s || '').replace(/"/g, '&quot;');
    const escHTML = (s) => String(s || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
    const rebelImgUrl = rebelProd.image && rebelProd.image.url ? rebelProd.image.url : '';
    const rebelUrl = rebelProd.url || '';
    const rebelPrice = this.getPaidDisplayPrice(rebelProd);
    const rebelTypeLabel = isEN ? '🎁 Free gift' : '🎁 Gratis cadeau';
    const rebelMolecularTitle = isEN ? '🎁 YOUR FREE BOTTLE' : '🎁 JOUW GRATIS FLES';
    const rebelMolecularText = isEN
      ? 'A thank-you gift, not a taste match: this bottle is on us for trying VinPop.'
      : 'Een bedankje, geen smaakmatch: deze fles is van ons omdat je VinPop probeert.';
    const openLabel = isEN ? 'Open details' : 'Details openen';

    return (
      `<div class="wr-bundle-item wr-bundle-item--rebel-bonus" role="button" tabindex="0" data-wine-id="${escAttr(productId)}" data-product-url="${escAttr(rebelUrl)}" data-display-price="${rebelPrice}" data-type-label="${escAttr(rebelTypeLabel)}" data-molecular-title="${escAttr(rebelMolecularTitle)}" data-molecular-text="${escAttr(rebelMolecularText)}" ${axisAttrs}>` +
      `<div class="wr-bundle-item__img-frame">` +
      `<img src="${escAttr(rebelImgUrl)}" alt="${escAttr(rebelProd.title)}" class="wr-bundle-item__img">` +
      `</div>` +
      `<div class="wr-bundle-item__info">` +
      `<div class="wr-bundle-item__type">${escHTML(rebelTypeLabel)}</div>` +
      `<div class="wr-bundle-item__title">${escHTML(rebelProd.title)}</div>` +
      this.buildProvenanceHTML(rebelProd, isEN, escHTML) +
      `<div class="wr-bundle-item__why">` +
      `<span>${isEN ? 'Why this wine' : 'Waarom deze wijn'}</span>` +
      `<p>${escHTML(rebelMolecularText)}</p>` +
      `</div>` +
      `<div class="wr-bundle-item__score-row">` +
      `<span class="wr-bundle-item__open-label">${escHTML(openLabel)}</span>` +
      `</div>` +
      `</div>` +
      `</div>`
    );
  }

  // ------------------------------------------------------------------
  // +1 bottle upsell: one extra bottle at 20% off, picked from the same
  // scored candidate list as the kit, swappable through a Replace button.
  // ------------------------------------------------------------------

  getPaidVariantId(product) {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const availablePaid = variants.find(v => v && v.available && Number(v.price) > 0);
    return availablePaid ? availablePaid.id : null;
  }

  formatEurosFromCents(cents) {
    return (Math.round(Number(cents) || 0) / 100).toFixed(2).replace('.', ',');
  }

  // `product.vendor` IS the winemaker on this store (vendor "Aotearoa" has a matching
  // "Aotearoa" collection, the convention `snippets/product-block-vendor.liquid` already
  // relies on). It is still dropped when it repeats the shop name, since printing "VinPop"
  // under every bottle proves nothing.
  // Returns '' whenever there is no usable name, and the caller then omits the line.
  growerName(product) {
    const vendor = String(product?.vendor || '').trim();
    if (!vendor) return '';
    const flatten = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const shop = flatten(String(window.Shopify?.shop || '').replace(/\.myshopify\.com$/i, ''));
    const flatVendor = flatten(vendor);
    if (!flatVendor || flatVendor === 'vinpop' || (shop && flatVendor === shop)) return '';
    return vendor;
  }

  // Provenance strip under the bottle title: who made it, where it comes from, and the one
  // claim that separates it from a supermarket shelf. This is the premium argument the
  // Dutch market actually reads, since a €9 retail price is no premium signal against a €7
  // supermarket bottle. Grower, region and country are all optional in the catalog, so the
  // strip degrades to the supermarket line alone rather than printing empty separators.
  buildProvenanceHTML(product, isEN, escHTML) {
    const grower = this.growerName(product);
    const place = [product?.region, product?.country]
      .map(value => String(value || '').trim())
      .filter(Boolean)
      .join(', ');
    const origin = [grower, place].filter(Boolean);
    return (
      `<div class="wr-bundle-item__provenance">` +
      (origin.length
        ? `<span class="wr-bundle-item__origin">${origin.map(part => escHTML(part)).join(' · ')}</span>`
        : '') +
      `<span class="wr-bundle-item__exclusive">${isEN ? 'Not in supermarkets' : 'Niet in de supermarkt'}</span>` +
      `</div>`
    );
  }

  // Rounded down, so the badge never claims a bigger discount than the two numbers
  // printed next to it actually add up to. Returns 0 when there is nothing to save,
  // which is the signal to hide the badge rather than print "-0%".
  discountPercent(retailCents, paidCents) {
    const retail = Math.round(Number(retailCents) || 0);
    const paid = Math.round(Number(paidCents) || 0);
    if (retail <= 0 || paid >= retail) return 0;
    return Math.floor(((retail - paid) / retail) * 100);
  }

  // Products the Buy X Get Y automatic discount actually covers, published by
  // `snippets/wine-catalog-data.liquid` from the discount's "Customer gets" collection.
  getUpsellEligibleIds() {
    const raw = window.vinpopUpsellEligibleIds;
    if (!Array.isArray(raw) || !raw.length) return null;
    return new Set(raw.map(String));
  }

  // `candidates` arrives already scored against the customer's quiz point and sorted by
  // taste distance, exactly like the kit bottles. Wines inside the customer's taste zone
  // are promoted ahead of the rest, so an added bottle is never a generic cross-sell.
  buildUpsellCandidates(candidates, packWines, catalog = window.shopCatalog || {}) {
    // KILL SWITCH. The whole upsell renders from this pool, so returning [] here hides the
    // teaser and every extra-bottle card on both the first render and the silent re-render.
    // Off depuis le 2026-07-28 : la remise -20% sur bouteilles supplémentaires est retirée
    // du parcours le temps de tester une autre approche (et elle ne s'appliquait pas au
    // checkout, donc afficher un prix barré non honoré trompait le client).
    if (this.upsellEnabled === false) {
      console.warn('🍷 Upsell hidden: disabled until the -20% discount is confirmed at checkout.');
      return [];
    }

    // The catalog is wider than the discount's collection: it also loads the blind tasting
    // SKUs and the wishing-list wines. Offering one of those would print -20% on a bottle
    // Shopify charges in full, so no eligibility list means no upsell at all.
    const eligible = this.getUpsellEligibleIds();
    if (!eligible) {
      console.error('🍷 Upsell disabled: window.vinpopUpsellEligibleIds is missing or empty. Check that snippets/wine-catalog-data.liquid renders on this page.');
      return [];
    }

    const packIds = new Set((packWines || []).map(wine => String(wine.wine_id)));
    const matches = [];
    const seen = new Set();
    const skippedIneligible = [];
    const cuts = { inKit: 0, duplicate: 0, notInCatalog: 0, outOfStock: 0, notEligible: 0, noPaidVariant: 0 };

    (candidates || []).forEach(wine => {
      const productId = String(wine?.wine_id || '');
      if (!productId) return;
      if (packIds.has(productId)) { cuts.inKit += 1; return; }
      if (seen.has(productId)) { cuts.duplicate += 1; return; }
      const product = catalog[productId];
      if (!product) { cuts.notInCatalog += 1; return; }
      if (product.available === false) { cuts.outOfStock += 1; return; }
      if (!eligible.has(productId)) {
        seen.add(productId);
        cuts.notEligible += 1;
        skippedIneligible.push(`${productId} ${product.title || ''}`);
        return;
      }
      const variantId = this.getPaidVariantId(product);
      const price = this.getPaidDisplayPrice(product);
      if (!variantId || !price) { cuts.noPaidVariant += 1; return; }
      seen.add(productId);
      matches.push({ productId, variantId, product, price, wine });
    });

    // Stable partition: inside the taste zone first, each group keeping the incoming
    // taste-distance order. `_insideTasteZone` is the same test the kit selection uses.
    const inZone = matches.filter(entry => entry.wine && entry.wine._insideTasteZone);
    const outZone = matches.filter(entry => !entry.wine || !entry.wine._insideTasteZone);
    const picked = inZone.concat(outZone).slice(0, this.upsellPoolSize);

    // Surfaced so an over-restrictive discount collection is visible instead of silently
    // shrinking the choice the customer gets.
    if (skippedIneligible.length) {
      console.warn('🍷 Upsell skipped (not in the discount collection):', skippedIneligible);
    }
    // The pool size is the hard ceiling on how many bottles the customer can add, so when
    // it comes out below `upsellMaxBottles` this breakdown says which filter ate the wines.
    console.log(
      `🍷 Upsell pool: ${picked.length} wines (${inZone.length} inside the taste zone) ` +
      `from ${(candidates || []).length} candidates, colour preference "${this.colorPreference}". ` +
      `Cut: ${cuts.inKit} in the kit, ${cuts.duplicate} duplicates, ${cuts.notInCatalog} not in the catalog, ` +
      `${cuts.outOfStock} out of stock, ${cuts.notEligible} outside the discount collection, ` +
      `${cuts.noPaidVariant} without a paid variant.`
    );
    if (picked.length < this.upsellMaxBottles) {
      console.warn(
        `🍷 Upsell capped at ${picked.length} bottles instead of ${this.upsellMaxBottles}: ` +
        'not enough eligible wines match this customer. Widen the discount collection, ' +
        'restock, or relax the colour preference to go deeper.'
      );
    }

    return picked;
  }

  // Rendered as a real `.wr-bundle-item`, not a bespoke card: it inherits the kit card
  // layout, and on mobile it inherits `order: 10` and the 2-column grid cell, so it lands
  // in the flow right after the kit bottles instead of jumping above them.
  buildUpsellCardHTML(card, position, isEN) {
    const entry = this._upsell.pool[card.options[card.optionIndex]];
    if (!entry) return '';

    const escAttr = (s) => String(s == null ? '' : s).replace(/"/g, '&quot;');
    const escHTML = (s) => String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

    const product = entry.product;
    const discountLabel = `${Math.round(this.upsellDiscountRate * 100)}%`;
    const oldPrice = entry.price;
    const newPrice = Math.round(oldPrice * (1 - this.upsellDiscountRate));
    const imgUrl = product.image && product.image.url ? product.image.url : '';
    const productUrl = product.url || (product.handle ? `/products/${product.handle}` : '');
    const typeLabel = isEN ? `🍷 Extra bottle -${discountLabel}` : `🍷 Extra fles -${discountLabel}`;
    const whyText = entry.wine && entry.wine._insideTasteZone
      ? (isEN
        ? 'Inside your taste zone: this bottle sits close to the taste point your answers drew.'
        : 'Binnen je smaakzone: deze fles ligt dicht bij het smaakpunt uit je antwoorden.')
      : (isEN
        ? 'Selected based on your quiz answers, right after the three bottles of your kit.'
        : 'Gekozen op basis van je quizantwoorden, direct na de drie flessen van je kit.');
    const openLabel = isEN ? 'Open details' : 'Details openen';
    const reachableOptions = this.getUpsellCardOptions(position);

    // Same lab axes the kit cards publish. Without these the product modal cannot draw the
    // taste fingerprint, which is the whole proof that this bottle fits the customer.
    const wine = entry.wine || {};
    const axisAttrs = [
      Number.isFinite(Number(wine._barWineX)) ? `data-axis-x="${Math.round(Number(wine._barWineX))}"` : '',
      Number.isFinite(Number(wine._barWineY)) ? `data-axis-y="${Math.round(Number(wine._barWineY))}"` : '',
      Number.isFinite(Number(wine._barWineZ)) ? `data-axis-z="${Math.round(Number(wine._barWineZ))}"` : '',
    ].filter(Boolean).join(' ');

    return (
      `<div class="wr-bundle-item wr-bundle-item--upsell" role="button" tabindex="0" aria-label="${escAttr(openLabel)}" data-upsell-position="${position}" data-wine-id="${escAttr(entry.productId)}" data-product-url="${escAttr(productUrl)}" data-display-price="${newPrice}" data-compare-price="${oldPrice}" data-type-label="${escAttr(typeLabel)}" data-molecular-title="${escAttr(isEN ? 'YOUR EXTRA BOTTLE' : 'JOUW EXTRA FLES')}" data-molecular-text="${escAttr(whyText)}" ${axisAttrs}>` +
      `<div class="wr-bundle-item__img-frame">` +
      `<img src="${escAttr(imgUrl)}" alt="${escAttr(product.title)}" class="wr-bundle-item__img">` +
      `</div>` +
      `<div class="wr-bundle-item__info">` +
      `<div class="wr-bundle-item__type">${escHTML(typeLabel)}</div>` +
      `<div class="wr-bundle-item__title">${escHTML(product.title)}</div>` +
      `<div class="wr-bundle-item__why">` +
      `<span>${isEN ? 'Why this wine' : 'Waarom deze wijn'}</span>` +
      `<p>${escHTML(whyText)}</p>` +
      `</div>` +
      `<div class="wr-bundle-item__score-row wr-upsell-item__price-row">` +
      `<s class="wr-upsell-item__price-old">€${this.formatEurosFromCents(oldPrice)}</s>` +
      `<span class="wr-upsell-item__price-new">€${this.formatEurosFromCents(newPrice)}</span>` +
      `</div>` +
      `<div class="wr-upsell-item__actions" style="display:flex;align-items:center;justify-content:space-between;gap:10px;margin-top:10px;">` +
      (reachableOptions.length > 1
        // Replace = a real secondary button: bordered pill, terracotta, swap icon + counter.
        ? `<button type="button" class="wr-upsell-item__swap" style="display:inline-flex;align-items:center;gap:5px;padding:7px 12px;border:1.5px solid #E07A5F;border-radius:999px;background:#fff;color:#C4573A;font-size:12px;font-weight:800;line-height:1;cursor:pointer;">🔄 ${isEN ? 'Swap wine' : 'Wissel wijn'} <span class="wr-upsell-item__swap-count" style="font-weight:700;opacity:0.7;">${reachableOptions.indexOf(card.options[card.optionIndex]) + 1}/${reachableOptions.length}</span></button>`
        : `<span></span>`) +
      // Remove = destructive, and it must not look like the swap button: no border, muted
      // red, a ✕ glyph, pushed to the opposite edge so the two are never mistaken.
      `<button type="button" class="wr-upsell-item__remove" style="display:inline-flex;align-items:center;gap:4px;padding:7px 4px;border:0;background:none;color:#B0453A;font-size:12px;font-weight:600;line-height:1;cursor:pointer;">✕ ${isEN ? 'Remove' : 'Verwijder'}</button>` +
      `</div>` +
      `</div>` +
      `</div>`
    );
  }

  // First pool index no card is showing: the wine the teaser offers next.
  getTeaserPoolIndex() {
    const state = this._upsell;
    if (!state) return null;
    const shown = this.getDisplayedUpsellIndices();
    for (let index = 0; index < state.pool.length; index += 1) {
      if (!shown.has(index)) return index;
    }
    return null;
  }

  // The plain "Add a bottle" button got looked at but not clicked (Clarity): text alone
  // does not read as an offer. The teaser is instead a real product card showing the next
  // recommended bottle, its struck price and a filled Add button, so the discount is a
  // thing the customer sees, not a line they skim. Being a `.wr-bundle-item` it inherits
  // `order: 10` and lands after the kit bottles on both layouts, no `order` juggling.
  buildUpsellTeaserHTML(isEN) {
    const poolIndex = this.getTeaserPoolIndex();
    if (poolIndex === null) return '';
    const entry = this._upsell.pool[poolIndex];
    if (!entry) return '';

    const escAttr = (s) => String(s == null ? '' : s).replace(/"/g, '&quot;');
    const escHTML = (s) => String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

    const product = entry.product;
    const wine = entry.wine || {};
    const discountLabel = `${Math.round(this.upsellDiscountRate * 100)}%`;
    const oldPrice = entry.price;
    const newPrice = Math.round(oldPrice * (1 - this.upsellDiscountRate));
    const imgUrl = product.image && product.image.url ? product.image.url : '';
    const productUrl = product.url || (product.handle ? `/products/${product.handle}` : '');
    const bottleNo = (Array.isArray(this._packWines) ? this._packWines.length : 3) + this._upsell.added.length + 1;
    const typeLabel = isEN ? `➕ Add bottle ${bottleNo} · -${discountLabel}` : `➕ Fles ${bottleNo} toevoegen · -${discountLabel}`;
    const whyText = wine._insideTasteZone
      ? (isEN
        ? 'Inside your taste zone: this bottle sits close to the taste point your answers drew.'
        : 'Binnen je smaakzone: deze fles ligt dicht bij het smaakpunt uit je antwoorden.')
      : (isEN
        ? 'Selected based on your quiz answers, right after the three bottles of your kit.'
        : 'Gekozen op basis van je quizantwoorden, direct na de drie flessen van je kit.');
    const openLabel = isEN ? 'Open details' : 'Details openen';
    const addLabel = isEN ? `+ Add to my box · €${this.formatEurosFromCents(newPrice)}` : `+ Toevoegen · €${this.formatEurosFromCents(newPrice)}`;

    const axisAttrs = [
      Number.isFinite(Number(wine._barWineX)) ? `data-axis-x="${Math.round(Number(wine._barWineX))}"` : '',
      Number.isFinite(Number(wine._barWineY)) ? `data-axis-y="${Math.round(Number(wine._barWineY))}"` : '',
      Number.isFinite(Number(wine._barWineZ)) ? `data-axis-z="${Math.round(Number(wine._barWineZ))}"` : '',
    ].filter(Boolean).join(' ');

    // Filled terracotta, inline: the whole reason we are here is that a subtle affordance
    // was invisible. Inline also means no stylesheet can strip it, the lesson from the CTA.
    const addStyle = [
      'display:flex', 'align-items:center', 'justify-content:center', 'gap:6px',
      'width:100%', 'min-height:46px', 'margin-top:10px', 'padding:12px 14px',
      'border:0', 'border-radius:12px', 'background:#E07A5F', 'color:#fff',
      'font-size:14px', 'font-weight:800', 'line-height:1.2', 'text-align:center',
      'cursor:pointer', 'box-sizing:border-box'
    ].join(';');

    return (
      `<div class="wr-bundle-item wr-bundle-item--upsell wr-bundle-item--upsell-teaser" role="button" tabindex="0" aria-label="${escAttr(openLabel)}" data-upsell-teaser="1" data-wine-id="${escAttr(entry.productId)}" data-product-url="${escAttr(productUrl)}" data-display-price="${newPrice}" data-compare-price="${oldPrice}" data-type-label="${escAttr(typeLabel)}" data-molecular-title="${escAttr(isEN ? 'YOUR EXTRA BOTTLE' : 'JOUW EXTRA FLES')}" data-molecular-text="${escAttr(whyText)}" ${axisAttrs}>` +
      `<div class="wr-bundle-item__img-frame">` +
      `<img src="${escAttr(imgUrl)}" alt="${escAttr(product.title)}" class="wr-bundle-item__img">` +
      `</div>` +
      `<div class="wr-bundle-item__info">` +
      `<div class="wr-bundle-item__type">${escHTML(typeLabel)}</div>` +
      `<div class="wr-bundle-item__title">${escHTML(product.title)}</div>` +
      `<div class="wr-bundle-item__why">` +
      `<span>${isEN ? 'Why this wine' : 'Waarom deze wijn'}</span>` +
      `<p>${escHTML(whyText)}</p>` +
      `</div>` +
      `<div class="wr-bundle-item__score-row wr-upsell-item__price-row">` +
      `<s class="wr-upsell-item__price-old">€${this.formatEurosFromCents(oldPrice)}</s>` +
      `<span class="wr-upsell-item__price-new">€${this.formatEurosFromCents(newPrice)}</span>` +
      `</div>` +
      `<button type="button" class="wr-upsell-teaser__add" style="${addStyle}">${escHTML(addLabel)}</button>` +
      `</div>` +
      `</div>`
    );
  }

  // Only the bottle a card currently shows is exclusive. Alternatives are shared between
  // cards, so a 9-bottle box needs 9 distinct wines, not 27. Reserving whole blocks was
  // what stopped the customer at 3 bottles: the eligible pool ran out.
  getDisplayedUpsellIndices(exceptPosition = -1) {
    const shown = new Set();
    (this._upsell?.added || []).forEach((card, position) => {
      if (position === exceptPosition) return;
      shown.add(card.options[card.optionIndex]);
    });
    return shown;
  }

  // A card gets `upsellSwapOptions` alternatives to cycle through, taken from the best
  // wines no other card is showing right now.
  takeUpsellOptionBlock() {
    const state = this._upsell;
    if (!state) return null;
    const shown = this.getDisplayedUpsellIndices();
    const block = [];
    for (let index = 0; index < state.pool.length && block.length < this.upsellSwapOptions; index += 1) {
      if (!shown.has(index)) block.push(index);
    }
    return block.length ? block : null;
  }

  // Pool indices this card can actually show: the one it displays, plus the alternatives
  // no other card has taken. The Replace counter is built from this, never from the raw
  // block, so it can never promise an option the customer cannot reach.
  getUpsellCardOptions(position) {
    const card = this._upsell?.added?.[position];
    if (!card) return [];
    const current = card.options[card.optionIndex];
    const shown = this.getDisplayedUpsellIndices(position);
    return card.options.filter(index => index === current || !shown.has(index));
  }

  // Next reachable option, as an index into `card.options`. null when Replace would be a
  // no-op, which is when the button is not rendered at all.
  getNextUpsellOptionIndex(position) {
    const card = this._upsell?.added?.[position];
    if (!card) return null;
    const reachable = this.getUpsellCardOptions(position);
    if (reachable.length < 2) return null;
    const current = card.options[card.optionIndex];
    const nextPoolIndex = reachable[(reachable.indexOf(current) + 1) % reachable.length];
    return card.options.indexOf(nextPoolIndex);
  }

  canAddAnotherUpsellBottle() {
    const state = this._upsell;
    if (!state) return false;
    if (state.added.length >= this.upsellMaxBottles) return false;
    return this.getDisplayedUpsellIndices().size < state.pool.length;
  }

  // Every extra bottle currently in the box, in display order.
  getAddedUpsellEntries() {
    const state = this._upsell;
    if (!state) return [];
    return state.added
      .map(card => state.pool[card.options[card.optionIndex]])
      .filter(Boolean);
  }

  // Repaints every upsell card and the CTA inside `.wr-bundle-items`, then re-syncs the
  // prices the page announces. Single entry point: add, replace and remove all call this.
  renderUpsellState(cardsEl, isEN) {
    const listEl = cardsEl?.querySelector('.wr-bundle-items');
    if (!listEl || !this._upsell) return;

    listEl.querySelectorAll('.wr-bundle-item--upsell').forEach(el => el.remove());

    if (this._upsell.pool.length) {
      // Committed bottles, then the teaser for the next one. Both are `.wr-bundle-item`s,
      // so they inherit `order: 10` and flow right after the kit bottles on both layouts,
      // which is what finally killed the `order` positioning bug the plain button had.
      const html =
        this._upsell.added
          .map((card, position) => this.buildUpsellCardHTML(card, position, isEN))
          .join('') +
        (this.canAddAnotherUpsellBottle() ? this.buildUpsellTeaserHTML(isEN) : '');

      const kitCards = listEl.querySelectorAll('.wr-bundle-item:not(.wr-bundle-item--upsell)');
      const lastKitCard = kitCards[kitCards.length - 1];
      if (lastKitCard) {
        lastKitCard.insertAdjacentHTML('afterend', html);
      } else {
        listEl.insertAdjacentHTML('beforeend', html);
      }
    }

    this.syncUpsellPricing(cardsEl, isEN);
  }

  syncUpsellPricing(cardsEl, isEN) {
    const added = this.getAddedUpsellEntries();
    const kitCents = 2990;
    const extraCents = added.reduce(
      (sum, entry) => sum + Math.round(entry.price * (1 - this.upsellDiscountRate)),
      0
    );
    const totalEuros = this.formatEurosFromCents(kitCents + extraCents);

    const btnSurface = cardsEl.querySelector('.wr-bundle__add .push-btn__surface');
    if (btnSurface && !cardsEl.querySelector('.wr-bundle__add')?.disabled) {
      btnSurface.textContent = isEN
        ? `Get my Taste Kit - €${totalEuros.replace(',', '.')}`
        : `Ontvang mijn Taste Kit - €${totalEuros}`;
    }

    const stickyPrice = cardsEl.querySelector('.wr-sticky-buy__price');
    if (stickyPrice) stickyPrice.textContent = `€${totalEuros}`;

    const mathPrice = cardsEl.querySelector('.wr-bundle__math-total-price');
    if (mathPrice) mathPrice.textContent = `€${totalEuros}`;

    const mathUpsell = cardsEl.querySelector('.wr-bundle__math-upsell');
    if (mathUpsell) {
      mathUpsell.style.display = added.length ? 'flex' : 'none';
      const countEl = mathUpsell.querySelector('.wr-bundle__math-upsell-count');
      if (countEl) countEl.textContent = String(added.length);
      const nounEl = mathUpsell.querySelector('.wr-bundle__math-upsell-noun');
      if (nounEl) {
        nounEl.textContent = added.length > 1
          ? (isEN ? 'extra bottles you picked, at 20% off' : 'extra flessen die je koos, met 20% korting')
          : (isEN ? 'extra bottle you picked, at 20% off' : 'extra fles die je koos, met 20% korting');
      }
    }

    const math = this._bundleMath;
    if (!math) return;

    const bottleCount = math.baseCount + added.length;
    const wineCents = math.wineCents + added.reduce((sum, entry) => sum + entry.price, 0);
    const paidCents = kitCents + extraCents;

    const setText = (selector, value) => {
      const el = cardsEl.querySelector(selector);
      if (el) el.textContent = value;
    };

    setText('.wr-bundle__math-total-count', String(bottleCount));
    setText('.wr-bundle__math-normal-bottle', `€${this.formatEurosFromCents(Math.round(wineCents / Math.max(1, bottleCount)))}`);
    setText('.wr-bundle__math-paid-bottle', `€${this.formatEurosFromCents(Math.round(paidCents / Math.max(1, bottleCount)))}`);

    const mathPercent = cardsEl.querySelector('.wr-bundle__math-percent');
    if (mathPercent) {
      const percent = this.discountPercent(wineCents, paidCents);
      mathPercent.textContent = `-${percent}%`;
      mathPercent.style.display = percent > 0 ? 'inline-block' : 'none';
    }

    // The saving line reads the shipping tier at the NEW bottle count, so a fifth bottle moves
    // the delivery term from €15,00 to €17,00 the same way it moves the wine term. Both are
    // printed, so a customer who checks the addition finds it correct at every count.
    const savingEl = cardsEl.querySelector('.wr-bundle__math-saving');
    if (savingEl) {
      const shippingCents = typeof math.getShippingCents === 'function'
        ? math.getShippingCents(bottleCount)
        : 0;
      const wineSavingCents = Math.max(0, wineCents - paidCents);
      const totalSavingCents = wineSavingCents + shippingCents;
      savingEl.style.display = totalSavingCents > 0 ? 'block' : 'none';
      setText('.wr-bundle__math-saving-total', `€${this.formatEurosFromCents(totalSavingCents)}`);
      setText('.wr-bundle__math-saving-wine', `€${this.formatEurosFromCents(wineSavingCents)}`);
      setText('.wr-bundle__math-saving-shipping', `€${this.formatEurosFromCents(shippingCents)}`);
    }
  }

  // Re-rendering removes the clicked button from the DOM. That drops focus to <body> and,
  // because the cards briefly disappear, the document shrinks and the browser clamps the
  // scroll position. On desktop, where the cards stack in one column, that reads as the
  // page jumping upwards. Restore both the scroll offsets and the focus.
  renderUpsellPreservingViewport(cardsEl, isEN, focusSelector) {
    const popupContent = this.popup ? this.popup.querySelector('.taste-profile-popup-content') : null;
    const windowY = window.scrollY;
    const popupY = popupContent ? popupContent.scrollTop : null;

    this.renderUpsellState(cardsEl, isEN);

    if (popupContent && popupY !== null) popupContent.scrollTop = popupY;
    if (window.scrollY !== windowY) window.scrollTo({ top: windowY, behavior: 'instant' });

    if (focusSelector) {
      const next = cardsEl.querySelector(focusSelector);
      if (next && typeof next.focus === 'function') next.focus({ preventScroll: true });
    }
  }

  setupUpsellBlock(cardsEl, isEN) {
    if (!cardsEl || !this._upsell) return;

    // The upsell cards are `.wr-bundle-item`s, so the delegated handler that opens the
    // product modal also matches them. That is wanted for the card body, but a click on
    // Replace or Remove must not open a modal. Both listeners sit on `cardsEl`, so only
    // stopImmediatePropagation can keep the modal one from firing.
    const handleUpsellClick = (event) => {
      const state = this._upsell;
      if (!state) return;

      const swapBtn = event.target.closest('.wr-upsell-item__swap');
      const removeBtn = event.target.closest('.wr-upsell-item__remove');
      const addBtn = event.target.closest('.wr-upsell-teaser__add');
      if (!swapBtn && !removeBtn && !addBtn) return;
      if (!cardsEl.contains(event.target)) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      if (addBtn) {
        // The teaser shows the first free wine; committing it takes a fresh option block
        // whose [0] is that same wine, so the bottle added is exactly the one previewed.
        const options = this.takeUpsellOptionBlock();
        if (options) state.added.push({ options, optionIndex: 0 });
        // Land focus on the next teaser's Add so a customer building a case keeps clicking
        // the same spot instead of chasing a button that moved down the page.
        this.renderUpsellPreservingViewport(cardsEl, isEN, '.wr-upsell-teaser__add');
        return;
      }

      const cardEl = (swapBtn || removeBtn).closest('.wr-bundle-item--upsell');
      const position = parseInt(cardEl?.dataset.upsellPosition ?? '-1', 10);
      if (position < 0 || position >= state.added.length) return;

      if (swapBtn) {
        const next = this.getNextUpsellOptionIndex(position);
        if (next !== null) state.added[position].optionIndex = next;
        this.renderUpsellPreservingViewport(
          cardsEl, isEN,
          `.wr-bundle-item--upsell[data-upsell-position="${position}"] .wr-upsell-item__swap`
        );
      } else {
        state.added.splice(position, 1);
        this.renderUpsellPreservingViewport(cardsEl, isEN, '.wr-upsell-teaser__add');
      }
    };

    // Enter/Space on one of our buttons must press the button, not open the card modal.
    const handleUpsellKeydown = (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      if (!event.target.closest('.wr-upsell-item__swap, .wr-upsell-item__remove, .wr-upsell-teaser__add')) return;
      event.stopImmediatePropagation();
    };

    cardsEl.addEventListener('click', handleUpsellClick);
    cardsEl.addEventListener('keydown', handleUpsellKeydown);

    this.renderUpsellState(cardsEl, isEN);
  }

  isReplacementProductAvailable(product) {
    if (!product || product.available === false) return false;
    const variants = Array.isArray(product.variants) ? product.variants : [];
    if (!variants.length) return !!product.available;
    return variants.some(variant => {
      if (!variant || variant.available === false) return false;
      if (variant.inventoryManaged && Number(variant.inventoryQuantity) <= 0) return false;
      return true;
    });
  }

  getPackReplacementOptions(position, catalog = window.shopCatalog || {}) {
    const current = this._packWines?.[position];
    if (!current) return [];

    const shown = new Set(this._packWines
      .filter((wine, index) => index !== position && wine)
      .map(wine => String(wine.wine_id)));

    const currentId = String(current.wine_id);
    const otherTotal = this._packWines.reduce((sum, wine, index) => {
      if (!wine || index === position) return sum;
      return sum + this.getPaidDisplayPrice(catalog[String(wine.wine_id)]);
    }, 0);
    const band = this.getPackPriceBand(catalog, product => this.isReplacementProductAvailable(product));
    const replacementMax = 4000;
    const options = [current];
    const seen = new Set([currentId]);

    (this._packReplacementCandidates || []).forEach(candidate => {
      const productId = String(candidate?.wine_id || '');
      const product = catalog[productId];
      const nextTotal = otherTotal + this.getPaidDisplayPrice(product);
      if (!productId || seen.has(productId) || shown.has(productId)) return;
      if (!this.isReplacementProductAvailable(product)) return;
      if (nextTotal < band.min || nextTotal > replacementMax) return;
      seen.add(productId);
      options.push(candidate);
    });

    return options;
  }

  applyPackReplacement(position, replacement) {
    const current = this._packWines?.[position];
    if (!current || !replacement) return;
    const roleKeys = [
      '_calibrationRole', '_calibrationIndex', 'reason_title', 'reason_text',
      '_isSimilar', '_similarTo', '_isBonus'
    ];
    const next = { ...replacement };
    roleKeys.forEach(key => {
      if (Object.prototype.hasOwnProperty.call(current, key)) next[key] = current[key];
      else delete next[key];
    });
    this._packWines[position] = next;
  }

  renderPackReplacementState(cardsEl, isEN) {
    const listEl = cardsEl?.querySelector('.wr-bundle-items');
    if (!listEl) return;
    const catalog = window.shopCatalog || {};
    const html = this.buildBundleItemCardsHTML(this._packWines || [], isEN);
    const insertionPoint = listEl.querySelector('.wr-bundle__retake-panel--mobile, .wr-bundle__tweak-panel--mobile');
    listEl.querySelectorAll('.wr-bundle-item:not(.wr-bundle-item--upsell)').forEach(item => item.remove());
    if (insertionPoint) insertionPoint.insertAdjacentHTML('beforebegin', html);
    else listEl.insertAdjacentHTML('beforeend', html);

    const wineCents = (this._packWines || []).reduce(
      (sum, wine) => sum + this.getPaidDisplayPrice(catalog[String(wine.wine_id)]),
      0
    );
    if (this._bundleMath) {
      this._bundleMath.wineCents = wineCents;
      this._bundleMath.baseCount = (this._packWines || []).length;
    }
    this.renderUpsellState(cardsEl, isEN);
  }

  setupPackReplacement(cardsEl, isEN) {
    if (!cardsEl || cardsEl.dataset.packReplacementDelegated) return;
    cardsEl.dataset.packReplacementDelegated = '1';

    cardsEl.addEventListener('click', event => {
      const button = event.target.closest('[data-replace-pack]');
      if (!button || !cardsEl.contains(button)) return;
      event.preventDefault();
      event.stopImmediatePropagation();

      const catalog = window.shopCatalog || {};
      if (button.hasAttribute('data-replace-pack')) {
        const position = Number(button.getAttribute('data-replace-pack'));
        const options = this.getPackReplacementOptions(position, catalog);
        const currentId = String(this._packWines?.[position]?.wine_id || '');
        const currentIndex = options.findIndex(wine => String(wine.wine_id) === currentId);
        const replacement = options[(currentIndex + 1) % options.length];
        if (!replacement || String(replacement.wine_id) === currentId) return;
        this.applyPackReplacement(position, replacement);
      }

      const windowY = window.scrollY;
      this.renderPackReplacementState(cardsEl, isEN);
      if (window.scrollY !== windowY) window.scrollTo({ top: windowY, behavior: 'instant' });
      const nextButton = cardsEl.querySelector(`[data-replace-pack="${button.getAttribute('data-replace-pack')}"]`);
      if (nextButton) nextButton.focus({ preventScroll: true });
    });

    cardsEl.addEventListener('keydown', event => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      if (event.target.closest('[data-replace-pack]')) {
        event.stopImmediatePropagation();
      }
    });
  }

  buildBundleItemCardsHTML(packWines, isEN) {
    const catalog = window.shopCatalog || {};
    const savedProfile = (() => {
      try { return JSON.parse(sessionStorage.getItem('vinpop_taste_profile') || '{}'); } catch(e) { return {}; }
    })();
    const getMolecularText = (profile, wineIdx, isENLang) => {
      const tannin = profile.tanninTolerance || 'medium';
      const acidity = profile.acidityTolerance || 'medium';
      const sweetness = profile.sweetnessPreference || 'balanced';
      const complexity = profile.complexityPreference || 'medium';
      const mood = profile.moodPreference || 'smooth';
      const en = [
        tannin === 'high' ? 'You drink it black. Same bold intensity as your espresso.' : tannin === 'low' ? 'You prefer cappuccino or tea. No harsh tannins here.' : 'Your bitterness threshold guided this pick.',
        acidity === 'high' ? 'You chose lemon vinaigrette. This wine has that same crisp bite.' : acidity === 'low' ? 'You went for creamy dressing. This wine is round, no sharp edges.' : 'You picked olive oil & salt. Clean and balanced.',
        sweetness === 'dry' ? 'You chose dark chocolate 70%+. Same dry, structured finish.' : sweetness === 'sweet' ? 'You prefer milk chocolate. This wine is soft and fruity.' : 'You picked fruit jellies. Same bright, fruity character.',
        complexity === 'complex' ? 'You chose aged Gouda. This wine has the same layered depth.' : complexity === 'simple' ? 'You chose Brie. Light, creamy, no rough edges.' : 'Your cheese choice points to a balanced complexity.',
        mood === 'bold' ? 'You asked for Bold & Powerful. This wine delivers exactly that.' : mood === 'fresh' ? 'You wanted Fresh & Fruity. This is exactly it.' : 'You picked Smooth & Round. That\'s precisely what this is.',
      ];
      const nl = [
        tannin === 'high' ? 'Jij drinkt het puur zwart. Dezelfde krachtige intensiteit als jouw espresso.' : tannin === 'low' ? 'Jij prefereert cappuccino of thee. Geen zware tannines hier.' : 'Jouw bitterheidsdrempel stuurde deze keuze.',
        acidity === 'high' ? 'Jij koos citroendressing. Deze wijn heeft dezelfde frisse bite.' : acidity === 'low' ? 'Jij ging voor romige dressing. Ronde wijn, geen scherpe kanten.' : 'Jij koos olijfolie & zout. Clean en uitgebalanceerd.',
        sweetness === 'dry' ? 'Jij koos pure chocolade 70%+. Dezelfde droge, gestructureerde afdronk.' : sweetness === 'sweet' ? 'Jij houdt van melkchocolade. Deze wijn is zacht en fruitig.' : 'Jij koos fruitgummies. Hetzelfde levendige, fruitige karakter.',
        complexity === 'complex' ? 'Jij koos oude Gouda. Deze wijn heeft dezelfde gelaagde diepte.' : complexity === 'simple' ? 'Jij koos Brie. Licht, romig, geen ruwe randjes.' : 'Jouw kaaskeuze wijst op een uitgebalanceerde complexiteit.',
        mood === 'bold' ? 'Jij vroeg om Krachtig & Koel. Deze wijn levert dat precies.' : mood === 'fresh' ? 'Jij wilde Fris & Fruitig. Dit is precies dat.' : 'Jij koos Zacht & Rond. Dat is precies wat dit is.',
      ];
      const pool = isENLang ? en : nl;
      return pool[wineIdx % pool.length];
    };
    const escAttr = (s) => String(s || '').replace(/"/g, '&quot;');
    const escHTML = (s) => String(s || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
    const topMatches = packWines.filter(w => !w._isSimilar && !w._isBonus).slice(0, 2);
    const topMatchNumberById = {
      [String(topMatches[0]?.wine_id || '')]: 1,
      [String(topMatches[1]?.wine_id || '')]: 2
    };

    return packWines.map((wine, i) => {
      const p = catalog[String(wine.wine_id)];
      if (!p) return '';
      const imgUrl = p.image && p.image.url ? p.image.url : '';
      const productUrl = p.url || '';
      const displayPrice = this.getPaidDisplayPrice(p);
      let typeLabel, typeClass, openLabel, molecularText;
      const molecularTitle = isEN ? '🔬 MOLECULAR PLAYLIST MATCH' : '🔬 MOLECULAIRE PLAYLIST MATCH';
      if (wine._calibrationRole) {
        typeClass = `wr-bundle-item--calibration wr-bundle-item--calibration-${wine._calibrationIndex}`;
        typeLabel = isEN ? `🍷 Bottle ${wine._calibrationIndex}: ${wine.reason_title}` : `🍷 Fles ${wine._calibrationIndex}: ${wine.reason_title}`;
        openLabel = isEN ? 'Open details' : 'Details openen';
        molecularText = wine.reason_text || getMolecularText(savedProfile, i, isEN);
      } else if (wine._isSimilar) {
        typeClass = 'wr-bundle-item--similar';
        const pct = wine.score || 90;
        const linkedMatchNo = topMatchNumberById[String(wine._similarTo)] || 1;
        const linkedMatchText = getMolecularText(savedProfile, linkedMatchNo - 1, isEN);
        typeLabel = isEN ? `🎯 Surprise Match: ${pct}% taste similarity with #match ${linkedMatchNo}.` : `🎯 Surprise Match: ${pct}% smaakovereenkomst met #match ${linkedMatchNo}.`;
        openLabel = isEN ? 'Linked to your top match.' : 'Gekoppeld aan jouw topmatch.';
        molecularText = isEN ? `${linkedMatchText} This extra bottle was added because it tastes very close to that match.` : `${linkedMatchText} Deze extra fles is toegevoegd omdat hij qua smaak heel dicht bij die match ligt.`;
        molecularText = this.buildDynamicWineMatchText(wine, molecularText, isEN);
      } else if (wine._isBonus) {
        typeClass = 'wr-bundle-item--bonus';
        typeLabel = isEN ? '🎁 Bonus: Included to sharpen your taste profile.' : '🎁 Bonus: Inbegrepen om je smaakprofiel scherper te maken.';
        openLabel = isEN ? 'Open details' : 'Details openen';
        molecularText = getMolecularText(savedProfile, i, isEN);
        molecularText = this.buildDynamicWineMatchText(wine, molecularText, isEN);
      } else {
        typeClass = '';
        typeLabel = isEN ? `🍷 Quiz Match #${i + 1}` : `🍷 Quizmatch #${i + 1}`;
        openLabel = isEN ? 'Open details' : 'Details openen';
        molecularText = getMolecularText(savedProfile, i, isEN);
        molecularText = this.buildDynamicWineMatchText(wine, molecularText, isEN);
      }
      const axisAttrs = [
        Number.isFinite(Number(wine._barWineX)) ? `data-axis-x="${Math.round(Number(wine._barWineX))}"` : '',
        Number.isFinite(Number(wine._barWineY)) ? `data-axis-y="${Math.round(Number(wine._barWineY))}"` : '',
        Number.isFinite(Number(wine._barWineZ)) ? `data-axis-z="${Math.round(Number(wine._barWineZ))}"` : '',
      ].filter(Boolean).join(' ');
      const canReplace = this.getPackReplacementOptions(i, catalog).length > 1;
      return `
        <div class="wr-bundle-item ${typeClass}" role="button" tabindex="0" aria-label="${escAttr(openLabel)}" data-wine-id="${wine.wine_id}" data-product-url="${escAttr(productUrl)}" data-display-price="${displayPrice}" data-type-label="${escAttr(typeLabel)}" data-molecular-title="${escAttr(molecularTitle)}" data-molecular-text="${escAttr(molecularText)}" ${axisAttrs}>
          <div class="wr-bundle-item__img-frame">
            <img src="${imgUrl}" alt="${escAttr(p.title)}" class="wr-bundle-item__img">
          </div>
          <div class="wr-bundle-item__info">
            <div class="wr-bundle-item__type">${escHTML(typeLabel)}</div>
            <div class="wr-bundle-item__title">${escHTML(p.title)}</div>
            ${this.buildProvenanceHTML(p, isEN, escHTML)}
            <div class="wr-bundle-item__why">
              <span>${isEN ? 'Why this wine' : 'Waarom deze wijn'}</span>
              <p>${escHTML(molecularText)}</p>
            </div>
            <div class="wr-bundle-item__score-row">
              <span class="wr-bundle-item__open-label">${escHTML(openLabel)}</span>
              ${canReplace ? `<button type="button" class="wr-bundle-item__replace" data-replace-pack="${i}">${isEN ? '↻ Replace wine' : '↻ Wijn vervangen'}</button>` : ''}
            </div>
          </div>
        </div>
      `;
    }).join('');
  }

  async updateBundleItemsFromRecommendations(wines, options = {}) {
    if (options.requestId && options.requestId !== this.axisUpdateRequestId) return;

    const cardsEl = this.popup?.querySelector('.wine-reveal-cards') || document.querySelector('.wine-reveal-cards');
    const listEl = cardsEl?.querySelector('.wr-bundle-items') || document.querySelector('.wr-bundle-items');
    if (!listEl) return;

    const isEN = (document.documentElement.lang || '').toLowerCase().slice(0, 2) !== 'nl';
    const catalog = window.shopCatalog || {};
    const isCatalogProductInStock = (product) => {
      if (!product || product.available === false) return false;
      const variants = Array.isArray(product.variants) ? product.variants : [];
      if (!variants.length) return !!product.available;
      // `available` stays true for a tracked variant that is set to "continue
      // selling when out of stock" (backorder) even at 0 units. Treat a tracked
      // variant with 0 or fewer units on hand as out of stock so it is never
      // recommended, matching the merchant's real inventory. Untracked variants
      // (inventoryManaged === false) keep relying on `available`.
      return variants.some(v => {
        if (!v || v.available === false) return false;
        if (v.inventoryManaged && Number(v.inventoryQuantity) <= 0) return false;
        return true;
      });
    };
    // Same band as the first render, from the same helper, so a silent axis update can never
    // re-price the kit against different rules.
    const { min: MIN_PACK_PRICE, max: MAX_PACK_PRICE } = this.getPackPriceBand(catalog, isCatalogProductInStock);
    const calibrationSelection = await this.buildCalibrationBottleSelection(wines, {
      isEN,
      catalog,
      minPackPrice: MIN_PACK_PRICE,
      maxPackPrice: MAX_PACK_PRICE,
      maxPrice: MAX_PACK_PRICE,
      isCatalogProductInStock,
      getPaidDisplayPrice: (product) => this.getPaidDisplayPrice(product)
    });
    if (options.requestId && options.requestId !== this.axisUpdateRequestId) return;
    const packWines = [...calibrationSelection.selectedWines];
    if (packWines.length < 2) return;

    this._fastBonusProductId = '';

    this._packWines = packWines;
    this._packReplacementCandidates = calibrationSelection.candidates || [];
    sessionStorage.setItem('tasteProfileResults', JSON.stringify(wines));
    sessionStorage.setItem('vinpop_taste_profile', JSON.stringify(this.buildTasteProfile()));
    sessionStorage.setItem('vinpop_last_results', JSON.stringify(wines));

    const html = this.buildBundleItemCardsHTML(packWines, isEN);
    const mobilePanel = listEl.querySelector('.wr-bundle__retake-panel--mobile, .wr-bundle__tweak-panel--mobile');
    const insertionPoint = mobilePanel;
    listEl.querySelectorAll('.wr-bundle-item').forEach(item => item.remove());
    if (insertionPoint) {
      insertionPoint.insertAdjacentHTML('beforebegin', html);
    } else {
      listEl.insertAdjacentHTML('beforeend', html);
    }

    // The kit changed, so the next best matches changed with it. Rebuild the pool, then
    // re-add the same products the customer had chosen when they are still on offer, each
    // with a fresh block of alternatives.
    const previouslyAdded = this.getAddedUpsellEntries().map(entry => entry.productId);
    const pool = this.buildUpsellCandidates(calibrationSelection.candidates, packWines, catalog);
    this._upsell = { pool, added: [] };
    previouslyAdded.slice(0, this.upsellMaxBottles).forEach(productId => {
      const poolIndex = pool.findIndex(entry => entry.productId === productId);
      if (poolIndex < 0 || this.getDisplayedUpsellIndices().has(poolIndex)) return;
      const block = this.takeUpsellOptionBlock();
      if (!block) return;
      // Keep the wine the customer picked at the front of its own option block.
      const options = [poolIndex].concat(block.filter(index => index !== poolIndex));
      this._upsell.added.push({ options: options.slice(0, this.upsellSwapOptions), optionIndex: 0 });
    });
    // The listeners are delegated on cardsEl and were bound on first render, so this only
    // has to repaint. Binding again here would double every click.
    this.renderUpsellState(cardsEl || document, isEN);
  }

  // ------------------------------------------------------------------
  // Tweak panel: shown on the bundle page, lets user revise answers.
  // Open by default. On change, re-runs the recommendation flow.
  // ------------------------------------------------------------------
  renderTweakPanel(mountEl, isEN) {
    if (!mountEl) return;

    const isMobilePanel = mountEl.classList.contains('wr-bundle__tweak-panel-mount--mobile');
    const isMobileViewport = !!(window.matchMedia && window.matchMedia('(max-width: 719px)').matches);

    // Keep a single retake panel in the DOM: mobile mount on mobile, desktop mount on desktop.
    if ((isMobileViewport && !isMobilePanel) || (!isMobileViewport && isMobilePanel)) {
      mountEl.remove();
      return;
    }

    const title = isEN ? 'Want another colour?' : 'Liever een andere kleur?';
    const cta = isEN ? 'Retake the quiz' : 'Quiz opnieuw doen';
    const normalizeMixForBoth = (value) => {
      const red = parseInt(value, 10);
      return red >= 2 ? 2 : 1;
    };

    const activeMode = (this.colorPreference === 'red' || this.colorPreference === 'white')
      ? this.colorPreference
      : 'both';
    const initialMix = this.colorPreference === 'mix'
      ? this.normalizeColorMixPreference(normalizeMixForBoth(this.colorMixPreference && this.colorMixPreference.red))
      : this.normalizeColorMixPreference(this.colorPreference === 'red' ? 2 : this.colorPreference === 'white' ? 1 : 2);

    const colors = {
      red: isEN ? 'Only red' : 'Alleen rood',
      white: isEN ? 'Only white' : 'Alleen wit',
      both: isEN ? 'Mix red + white' : 'Mix rood + wit'
    };
    const mixLabel = isEN ? 'How many of each?' : 'Hoeveel van elk?';
    const mixHint = '';
    const mixUnit = isEN ? 'red' : 'rood';
    const mixUnitWhite = isEN ? 'white' : 'wit';
    const applyLabel = isEN ? 'Updating your box...' : 'Je box wordt bijgewerkt...';
    const alreadySelectedLabel = isEN
      ? 'Already selected. This is your current box.'
      : 'Al geselecteerd. Dit is je huidige box.';

    const panel = document.createElement('div');
    panel.className = 'wr-bundle__retake-panel';
    if (isMobilePanel) {
      panel.classList.add('wr-bundle__retake-panel--mobile');
      if (isMobileViewport) {
        panel.classList.add('wr-bundle__retake-panel--desktop');
      }
    } else {
      panel.classList.add('wr-bundle__retake-panel--desktop');
    }
    panel.innerHTML =
      `<div class="wr-tweak-panel__header">` +
      `<p class="wr-tweak-panel__label">${title}</p>` +
      `<button class="push-btn wr-tweak-panel__retake-btn" type="button"><span class="push-btn__surface wr-tweak-panel__retake-surface">${cta}</span></button>` +
      `</div>` +
      `<div class="wr-bundle-color-control" data-bundle-color-control>` +
      `<div class="wr-bundle-color-control__choices" role="tablist" aria-label="${isEN ? 'Wine color preference' : 'Wijnkleur voorkeur'}">` +
      `<button class="wr-bundle-color-control__choice${activeMode === 'red' ? ' is-active' : ''}" type="button" data-color-mode="red">${colors.red}</button>` +
      `<button class="wr-bundle-color-control__choice${activeMode === 'white' ? ' is-active' : ''}" type="button" data-color-mode="white">${colors.white}</button>` +
      `<button class="wr-bundle-color-control__choice${activeMode === 'both' ? ' is-active' : ''}" type="button" data-color-mode="both">${colors.both}</button>` +
      `</div>` +
      `<div class="wr-bundle-color-control__mix${activeMode === 'both' ? ' is-visible' : ''}" data-bundle-mix-wrap>` +
      `<p class="wr-bundle-color-control__mix-label">${mixLabel}</p>` +
      `<div class="wr-bundle-color-control__presets">` +
      `<button class="wr-bundle-color-control__preset${initialMix.red === 2 ? ' is-active' : ''}" type="button" data-mix-red="2">2 ${mixUnit} + 1 ${mixUnitWhite}</button>` +
      `<button class="wr-bundle-color-control__preset${initialMix.red === 1 ? ' is-active' : ''}" type="button" data-mix-red="1">1 ${mixUnit} + 2 ${mixUnitWhite}</button>` +
      `</div>` +
      `<p class="wr-bundle-color-control__mix-value" data-bundle-mix-value>${initialMix.red} ${mixUnit} + ${initialMix.white} ${mixUnitWhite}</p>` +
      (mixHint ? `<p class="wr-bundle-color-control__hint">${mixHint}</p>` : '') +
      `</div>` +
      `<p class="wr-bundle-color-control__feedback" data-bundle-feedback role="status" aria-live="polite" hidden></p>` +
      `</div>`;
    mountEl.replaceWith(panel);

    const colorButtons = Array.from(panel.querySelectorAll('[data-color-mode]'));
    const mixWrap = panel.querySelector('[data-bundle-mix-wrap]');
    const mixPresets = Array.from(panel.querySelectorAll('[data-mix-red]'));
    const mixValue = panel.querySelector('[data-bundle-mix-value]');
    const feedback = panel.querySelector('[data-bundle-feedback]');
    let currentMode = activeMode;
    let pendingMix = initialMix;
    let colorUpdateBusy = false;
    let feedbackTimer = null;

    const hideFeedback = () => {
      if (feedbackTimer) clearTimeout(feedbackTimer);
      if (feedback) feedback.hidden = true;
    };

    const showAlreadySelected = (button) => {
      if (!feedback) return;
      feedback.textContent = alreadySelectedLabel;
      feedback.hidden = false;
      button.classList.remove('is-nudged');
      void button.offsetWidth;
      button.classList.add('is-nudged');
      if (feedbackTimer) clearTimeout(feedbackTimer);
      feedbackTimer = setTimeout(() => {
        feedback.hidden = true;
        button.classList.remove('is-nudged');
      }, 2600);
    };

    const updateMixLabel = () => {
      if (!mixValue) return;
      mixValue.textContent = `${pendingMix.red} ${mixUnit} + ${pendingMix.white} ${mixUnitWhite}`;
    };

    const updateChoiceUI = () => {
      colorButtons.forEach(button => {
        const isActive = button.dataset.colorMode === currentMode;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
      mixPresets.forEach(button => {
        const red = parseInt(button.dataset.mixRed, 10);
        const isActive = pendingMix.red === red;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
      if (mixWrap) {
        mixWrap.classList.toggle('is-visible', currentMode === 'both');
      }
      updateMixLabel();
    };

    const refreshRecommendations = async () => {
      if (colorUpdateBusy) return;
      colorUpdateBusy = true;
      panel.classList.add('is-updating');
      const labelEl = panel.querySelector('.wr-tweak-panel__label');
      const previousLabel = labelEl ? labelEl.textContent : '';
      if (labelEl) labelEl.textContent = applyLabel;
      try {
        await this.fetchResults({ silentUpdate: true });
      } finally {
        colorUpdateBusy = false;
        panel.classList.remove('is-updating');
        if (labelEl) labelEl.textContent = previousLabel || title;
      }
    };

    const applyMode = async (nextMode, shouldRefresh = true) => {
      currentMode = nextMode;
      if (nextMode === 'red' || nextMode === 'white') {
        this.colorPreference = nextMode;
        this.colorMixPreference = null;
      } else {
        this.colorPreference = 'mix';
        pendingMix = this.normalizeColorMixPreference(normalizeMixForBoth(pendingMix.red));
        this.colorMixPreference = { ...pendingMix };
      }
      updateChoiceUI();
      if (shouldRefresh) {
        await refreshRecommendations();
      }
    };

    colorButtons.forEach(button => {
      button.addEventListener('click', () => {
        const nextMode = button.dataset.colorMode;
        if (!nextMode) return;
        if (nextMode === currentMode) {
          showAlreadySelected(button);
          return;
        }
        hideFeedback();
        applyMode(nextMode, true);
      });
    });

    mixPresets.forEach(button => {
      button.addEventListener('click', () => {
        const redCount = normalizeMixForBoth(button.dataset.mixRed);
        if (redCount === pendingMix.red) {
          showAlreadySelected(button);
          return;
        }
        hideFeedback();
        pendingMix = this.normalizeColorMixPreference(redCount);
        if (currentMode === 'both') {
          this.colorPreference = 'mix';
          this.colorMixPreference = { ...pendingMix };
          updateChoiceUI();
          refreshRecommendations();
        } else {
          updateChoiceUI();
        }
      });
    });

    updateChoiceUI();

    panel.querySelector('.wr-tweak-panel__retake-btn')?.addEventListener('click', () => {
      this.setForceRetake(true);
      localStorage.removeItem('vinpop-quiz-completed');
      sessionStorage.removeItem('vinpop-quiz-completed');
      sessionStorage.removeItem('vinpop_last_results');
      sessionStorage.removeItem('vinpop_taste_profile');
      sessionStorage.removeItem('tasteProfileResults');
      sessionStorage.removeItem('vinpop_quiz_state');
      this.quizSavePromise = null;
      this.lastQuizSaveSignature = '';
      this._reloadToQuizTop();
    });
  }

  async debugCalibrationRecommendation(wines = this.recommendedWines || []) {
    const catalog = window.shopCatalog || {};
    const isCatalogProductInStock = (product) => {
      if (!product || product.available === false) return false;
      const variants = Array.isArray(product.variants) ? product.variants : [];
      if (!variants.length) return !!product.available;
      // `available` stays true for a tracked variant that is set to "continue
      // selling when out of stock" (backorder) even at 0 units. Treat a tracked
      // variant with 0 or fewer units on hand as out of stock so it is never
      // recommended, matching the merchant's real inventory. Untracked variants
      // (inventoryManaged === false) keep relying on `available`.
      return variants.some(v => {
        if (!v || v.available === false) return false;
        if (v.inventoryManaged && Number(v.inventoryQuantity) <= 0) return false;
        return true;
      });
    };
    const selection = await this.buildCalibrationBottleSelection(wines, {
      catalog,
      maxPrice: 1400,
      isCatalogProductInStock,
      getPaidDisplayPrice: (product) => this.getPaidDisplayPrice(product)
    });
    const selected = selection.selectedWines;
    const ids = selected.map(wine => String(wine.wine_id));
    const unique = ids.length === new Set(ids).size;
    const bottleB = selected[1];
    const bottleC = selected[2];
    const knownDistanceMap = await this.getPerceptiveDistanceMap('15513163760003');
    const knownDistance = knownDistanceMap.get('15988234125699');
    const report = {
      unique_product_ids: unique,
      selected_ids: ids,
      distance_1_2: bottleB?.distance_to_bottle_1,
      distance_1_3: bottleC?.distance_to_bottle_1,
      distance_2_3: bottleC?.distance_to_bottle_2,
      pairwise_target_12_18:
        Number(bottleB?.distance_to_bottle_1) >= 12 && Number(bottleB?.distance_to_bottle_1) <= 18 &&
        Number(bottleC?.distance_to_bottle_1) >= 12 && Number(bottleC?.distance_to_bottle_1) <= 18 &&
        Number(bottleC?.distance_to_bottle_2) >= 12 && Number(bottleC?.distance_to_bottle_2) <= 18,
      free_bottle_is_separate: !ids.includes(this.colorPreference === 'white' ? '15513213370755' : '15510612148611'),
      known_distance_15513163760003_to_15988234125699: knownDistance,
      known_distance_matches_expected_8: knownDistance === 8
    };
    console.table(report);
    return { report, selection };
  }
}


// Create instance
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function() {
    window.tasteProfileQuiz = new TasteProfileQuiz();
    applyTasteProfileFilter();
  });
} else {
  window.tasteProfileQuiz = new TasteProfileQuiz();
  applyTasteProfileFilter();
}

// Filter products based on taste profile
function applyTasteProfileFilter() {
  // Si le nouveau système (wine-recommendation-manager.js) est actif, il gère le rendu
  // shopCatalog est défini par wine-catalog-data.liquid (script inline, chargé avant les scripts defer)
  //
  // On teste le descripteur et non la valeur : depuis que le catalogue est servi en JSON
  // inerte, lire window.shopCatalog déclenche le JSON.parse des 82 Ko. Or ici on ne veut
  // que savoir si le global existe, on ne lit jamais la donnée avant de sortir. Passer par
  // getOwnPropertyDescriptor répond à la même question sans réveiller l'accesseur, que le
  // global soit posé en accesseur paresseux ou en propriété simple.
  if (Object.getOwnPropertyDescriptor(window, 'shopCatalog')) {
    console.log('🍷 applyTasteProfileFilter: Skipping - new recommendation manager handles rendering');
    return;
  }
  
  var urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('taste_profile') !== 'true') return;
  
  var resultsJson = sessionStorage.getItem('tasteProfileResults');
  if (!resultsJson) return;
  
  var wines = JSON.parse(resultsJson);
  var wineMap = {};
  for (var i = 0; i < wines.length; i++) {
    wineMap[wines[i].wine_id] = wines[i].score;
  }
  
  var productGrid = document.querySelector('#facets-results ul.grid, #facets-results ul');
  if (!productGrid) return;
  
  var allProducts = productGrid.querySelectorAll('li[data-product-id]');
  
  for (var j = 0; j < allProducts.length; j++) {
    var productLi = allProducts[j];
    var productId = productLi.getAttribute('data-product-id');
    var score = wineMap[productId];
    
    if (!score || score < 80) {
      productLi.remove();
      continue;
    }
    
    var compatText = score >= 90 ? 
      (window.themeStrings && window.themeStrings.taste_profile ? window.themeStrings.taste_profile.love_it : 'You will love it') :
      (window.themeStrings && window.themeStrings.taste_profile ? window.themeStrings.taste_profile.like_it : 'You will like it');
    
    var existingBadge = productLi.querySelector('.taste-score-badge');
    if (existingBadge) existingBadge.remove();
    
    var productTile = productLi.querySelector('.product-tile');
    if (productTile) {
      var badgeId = 'taste-score-' + productId;
      var rotate = Math.random() > 0.5 ? -5 : 5;
      
      var badge = document.createElement('div');
      badge.className = 'sticker-' + badgeId + ' pointer-events-none absolute z-20 origin-top-left will-change-transform';
      badge.style.cssText = 'top:15%;right:10%;transform:rotate(' + rotate + 'deg) translate(50%,-50%);';
      
      badge.innerHTML = '<div class="sticker__container rotate-anim relative">' +
        '<div class="relative max-w-none">' +
        '<div class="absolute inset-0 bg-scheme-accent-1 text-scheme-accent-1-contrast" style="clip-path:url(#clip-sold-out)">' +
        '<div class="sticker__text sticker-text-sold-out absolute inset-0 flex items-center justify-center text-center font-heading" style="padding:2rem;">' +
        '<div>' + score + '%</div>' +
        '</div></div>' +
        '<svg class="sticker__width-container relative max-w-none text-scheme-accent-1-contrast" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" style="width:75px;">' +
        '<use class="sticker__svg fill-transparent" href="#outline-sold-out" />' +
        '</svg></div></div>';
      
      productTile.style.position = 'relative';
      productTile.insertBefore(badge, productTile.firstChild);
    }
    
    // 🔧 SUPPRIMÉ: Ne plus afficher le texte "You will love it"
    // Les utilisateurs voient déjà le badge avec le score %
    var existingText = productLi.querySelector('.taste-compat-text');
    if (existingText) existingText.remove();
    
    productLi.setAttribute('data-taste-score', score);
  }
  
  var remaining = productGrid.querySelectorAll('li[data-product-id]');
  var productsArray = Array.from(remaining);
  productsArray.sort(function(a, b) {
    var scoreA = parseInt(a.getAttribute('data-taste-score')) || 0;
    var scoreB = parseInt(b.getAttribute('data-taste-score')) || 0;
    return scoreB - scoreA;
  });
  for (var k = 0; k < productsArray.length; k++) {
    productGrid.appendChild(productsArray[k]);
  }
}

document.addEventListener('shopify:section:load', function() {
  var urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('taste_profile') === 'true') {
    setTimeout(applyTasteProfileFilter, 300);
  }
});
